// @ts-ignore
import * as THREE from 'three';
// @ts-ignore
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import { MODULES, getModule, loadModuleAsync, paramsFromSchema } from './modules/registry';
import { renderHome } from './modules/home';
import {
  renderSidebar,
  createModelHealthLeds,
  createViewSwitcher, ViewMode,
  injectTheme,
  createZoomAllButton,
  createViewPresets, ViewPreset,
  createDebugLog,
  getExportFormat,
  getAppQuality,
  onAppQualityChange,
  setAppQualityTriangleCount,
  type AppQuality,
  getGlobalSliderMode,
  setGlobalSliderMode,
  onSliderModeChange,
} from './modules/ui-framework';
import { ApiError, cancelPreview, fetchPreview } from './modules/api-client';
import { createThinGrid, applyTheme as applySceneTheme } from './modules/theme';
import { fitCameraToObject } from './modules/camera-fit';
import {
  ConsolidatedRightPanel,
  CollapsibleSidebar,
  StatusLogViewer,
} from './modules/ui-panels';
import { SceneTreeViewer } from './modules/scene-tree';
import { exportMeshesToBinarySTL } from './modules/stl-exporter';
import { exportMeshesTo3MF } from './modules/3mf-exporter';
import { estimatePrint } from './modules/print-estimator';
import { DimensionOverlay } from './modules/dimension-overlay';
import { constrainPointToAxis, MeasurementOverlay, projectRayToAxis, snapToClosestVertexOrEdge } from './modules/measurement-tool';
import { CameraController } from './modules/camera-controller';
import { createViewCube, type ViewCubeTargetData } from './modules/view-cube';
import { PreviewController } from './modules/preview-controller';
import { PreviewRenderer, type PreviewMeshData as MeshData } from './modules/preview-renderer';
import { buildSvgPreviewMeshFromText, fetchSvgPreviewMesh, isSvgPreviewModule } from './modules/svg-preview-loader';
import { applyViewportSelection, AxisDragController, beginSectionHandleDrag, hitAlphaMaskedSprite, pickSelectableIntersections, sectionHandleDragPercent } from './modules/model-interaction';
import { buildSectionCutOutline, cameraSideOnSectionAxis, computeSectionIntersectionSegments, sectionAxisIndex, sectionPlaneCoordinate } from './modules/section-view';
import { BottomProgressBar } from './modules/ui-components';
import { UI_ICONS } from './modules/icons';
import { createCropAnnotationController } from './modules/crop-annotation';
import { AppLifecycle } from './modules/app-lifecycle';
import { disposeSceneResources } from './modules/scene-resource-disposal';
import { renderDrawing2D } from './modules/drawing-2d';
import { renderFlexiBuilder } from './modules/flexi-builder';
import { ProfileInspector } from './modules/profile-inspector';
import { ProfileDrawingTool } from './modules/profile-drawing-tool';

// Initialize theme immediately on page load
injectTheme();

function showHome(scope: 'all' | 'core' = 'all'): void {
  // TASK 25: leaving the editor must release its WebGL context too, not just clear the DOM.
  // Otherwise every visit to the home screen leaks one context and the app stops rendering
  // after a handful of navigations.
  // Invalidate any startApp() still awaiting its module load, so it cannot build a scene on
  // top of the home screen.
  appLifecycle.invalidate();
  const root = document.getElementById('app')!;
  root.innerHTML = '';
  root.style.cssText = 'display: block; width: 100vw; min-height: 100vh; overflow-y: auto;';
  renderHome(root, (moduleId) => {
    root.style.cssText = 'display: flex; height: 100vh; background: #ffffff;';
    // TASK 25 (2026-09-30): set the hash and NOTHING else. This used to also call
    // startApp(moduleId) directly, which meant opening one module ran startApp TWICE:
    //   line 1: window.location.hash = ...  -> fires hashchange -> route() -> startApp
    //   line 2: startApp(moduleId)                                            <- the second one
    // Two full apps started: two WebGL renderers, two requestAnimationFrame loops, two
    // Three.js scenes. The first canvas was detached from the DOM but never disposed, so its
    // WebGL context leaked - and browsers cap contexts at roughly 8-16, after which rendering
    // stops working entirely.
    // Letting the hashchange listener be the single entry point fixes the duplicate and keeps
    // one code path into startApp instead of two.
    window.location.hash = `#/m/${moduleId}`;
  }, scope);
}

let baseMesh: THREE.Mesh | null = null;
let baseWire: THREE.LineSegments | null = null;
let switchMesh: THREE.Mesh | null = null;
let switchWire: THREE.LineSegments | null = null;
let keycapMesh: THREE.Mesh | null = null;
let keycapWire: THREE.LineSegments | null = null;
let letterMesh: THREE.Mesh | null = null;
let letterWire: THREE.LineSegments | null = null;
let assemblyGroup: THREE.Group | null = null;

const appLifecycle = new AppLifecycle();

// Color states per part (1 part = 1 color, directly mapped to 3D object):
const partColors: Record<string, string> = {};

let sceneTree: SceneTreeViewer | null = null;

// Track selected object cluster: 'all' | 'base' | 'keycap' | 'switch' | 'letter'
let selectedGroup: 'all' | 'base' | 'keycap' | 'switch' | 'letter' = 'all';

// Z-explode state is per-app (see the explode button handler in startApp), so it resets
// whenever a module is opened instead of leaking across modules.

// R13 (2026-09-22): Overlays for analysis — toggleable by UI buttons
let isOverhangActive = false;
let baseSupport: THREE.Mesh | null = null;
let switchSupport: THREE.Mesh | null = null;
let keycapSupport: THREE.Mesh | null = null;
let letterSupport: THREE.Mesh | null = null;
let supportOverlay: THREE.Mesh | null = null;
let watertightEdges: THREE.Group | null = null;

async function startApp(moduleId: string): Promise<void> {
  // TASK 25 (2026-09-30) - TEARDOWN THE PREVIOUS APP BEFORE STARTING A NEW ONE.
  //
  // showHome() used to call startApp() as well as setting the hash, so every module opened
  // started two full apps. The duplicate call is gone, but a guard and a teardown are still
  // needed because ANY second entry into startApp leaks a WebGL context: the old canvas is
  // removed from the DOM while its renderer keeps holding a GPU context, and browsers allow
  // only ~8-16 live contexts before rendering stops working.
  //
  // `dispose()` frees the context's resources; `forceContextLoss()` is what actually releases
  // the context slot (dispose alone does not, which is the part that is easy to miss). The
  // animation loop is stopped first, otherwise it keeps scheduling frames against a disposed
  // renderer.
  // If a previous startApp() is still awaiting loadModuleAsync, its continuation must not
  // build a scene on top of the one we are about to create. Bumping the token invalidates it.
  const myToken = appLifecycle.begin();

  const loadedModule = await loadModuleAsync(moduleId);
  if (!appLifecycle.isCurrent(myToken)) {
    // A newer startApp() began while we were loading; this run is obsolete.
    console.log(`[app] Superseded while loading ${moduleId}; abandoning this run`);
    return;
  }
  if (!loadedModule) {
    console.error(`Unknown module: ${moduleId}`);
    return showHome();
  }
  const module = loadedModule;

  // Create debug log first
  const debugLog = createDebugLog();
  
  console.log(`Starting app for module: ${moduleId}`);
  const root = document.getElementById('app')!;
  root.style.cssText = 'display: flex; height: 100vh; background: #ffffff; overflow: hidden;';
  root.innerHTML = `
    <div id="sidebar"></div>
    <div id="viewer">
      <button id="mobile-toggle-btn" class="mobile-nav-btn">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M2.5 4h11M2.5 8h11M2.5 12h11"/></svg>
        <span>Parameters</span>
      </button>
      <div id="mobile-backdrop" class="mobile-backdrop"></div>
      <div id="canvas-container"></div>
    </div>
  `;

  const mobileToggleBtn = document.getElementById('mobile-toggle-btn');
  const mobileBackdrop = document.getElementById('mobile-backdrop');
  const sidebarEl = document.getElementById('sidebar');

  if (mobileToggleBtn && mobileBackdrop && sidebarEl) {
    const toggleSidebar = (open?: boolean) => {
      const shouldOpen = open !== undefined ? open : !sidebarEl.classList.contains('open');
      sidebarEl.classList.toggle('open', shouldOpen);
      mobileBackdrop.classList.toggle('open', shouldOpen);
    };

    mobileToggleBtn.addEventListener('click', () => toggleSidebar());
    mobileBackdrop.addEventListener('click', () => toggleSidebar(false));
  }

  // TASK 19 (2026-09-30): a "← Home" button used to be created right here and given a click
  // handler, but it was NEVER appended to the DOM - so it never appeared. Dead code that
  // looked like a feature. Removed rather than attached, because the sidebar already has its
  // own working "← Home" link (ui-framework.ts) and two identical controls in the same panel
  // would just be clutter.
  //
  // Its handler also called showHome() directly after setting the hash, which would now run
  // showHome() twice: once directly and once via the hashchange listener firing route().

  // TASK 19 (2026-09-30) - DON'T LOSE THE USER'S WORK WHEN THEY LEAVE THE EDITOR.
  //
  // `currentParams` used to start from the schema defaults on every entry, and the only way
  // out of the editor was the sidebar's `href="/"` link - a full page navigation, which
  // reloaded the app and silently reset every slider the user had moved. Adjusting a model
  // and coming back to compare cost the user all their work.
  //
  // Params are now persisted per module and restored on re-entry. localStorage (not a module
  // variable) because the whole point is surviving the reload that `href="/"` causes.
  // Wrapped in try/catch throughout: localStorage throws in private mode and when the quota
  // is exceeded, and losing the memory of params is far better than breaking the editor.
  const PARAMS_STORAGE_KEY = `boxed3d_params_${module.id}`;

  function loadSavedParams(): Record<string, any> | null {
    try {
      const raw = localStorage.getItem(PARAMS_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  function persistParams(): void {
    try {
      localStorage.setItem(PARAMS_STORAGE_KEY, JSON.stringify(currentParams));
    } catch {
      // Private mode or quota full - params simply will not survive a reload.
    }
  }

  const currentParams: Record<string, any> = paramsFromSchema(module);
  if (module.id === 'text_bead_50') {
    currentParams.text = '50';
  }
  // Migrate this lamp's previously saved default dimensions to its new scaled defaults.
  // Preserve any values the user had customized away from the old defaults.
  const previousDefaults: Record<string, number> = module.id === 'teacher_20_11_round_medal'
    ? { textOffsetX: 1, textOffsetY: -1, textWidthXY: 29.5, bookOffsetX: 1, bookOffsetY: -3, bookScaleXY: 0.95, flowersOffsetX: 0, flowersOffsetY: -0.5, flowersScaleXY: 1.4 }
    : module.id === 'youtube_short_spiral_lamp'
    ? {
        totalHeight: 190,
        shadeMaxDia: 132,
        shadeTopDia: 58,
        shadeBotDia: 88,
        waistDia: 68,
        ribWidth: 14,
        ribThickness: 12,
        baseDia: 130,
      }
      : module.id === 'text_bead'
        ? { overallDepth: 30, outlineOffset: 2 }
      : module.id === 'text_bead_50'
        ? { overallLength: 50 }
      : (module.id === 'aa_aaa_battery_tray' || module.id === 'core_grid_hole_tray')
      ? { edgeMargin: 8 }
      : module.id === 'essential_oil_tray'
        ? { columns10: 6, rows10: 2, columns30: 6, rows30: 2 }
      : {};
  let migratedDefaults = 0;
  // Overlay saved values on top of the defaults, keeping only keys the module still declares.
  // Without that filter a renamed or removed slider would linger in the object and be sent to
  // the worker as a stale parameter.
  {
    const saved = loadSavedParams();
    if (saved) {
      let restored = 0;
      for (const key of Object.keys(currentParams)) {
        if (module.id === 'text_bead_50' && key === 'text') continue;
        // Keep the selected font on text_bead: a font change is a real model parameter
        // there, and silently reverting it made the picker appear disconnected from Update.
        // Preserve the historical reset behavior for the other text modules.
        if (key === 'fontStyle' && module.id !== 'text_bead') continue;
        if (saved[key] !== undefined) {
        if (previousDefaults[key] !== undefined &&
              saved[key] === previousDefaults[key] &&
              currentParams[key] !== previousDefaults[key]) {
            migratedDefaults++;
            continue;
          }
          currentParams[key] = saved[key];
          restored++;
        }
      }
      if (migratedDefaults > 0) {
        try {
          localStorage.setItem(PARAMS_STORAGE_KEY, JSON.stringify(currentParams));
        } catch {
          // The new defaults still apply for this session if storage is unavailable.
        }
        console.log(`[params] Migrated ${migratedDefaults} outdated default(s)`);
      }
      if (restored > 0) {
        console.log(`[params] Restored ${restored} saved value(s) for ${module.id}`);
      }
    }
  }

  // Older medal registry builds persisted a mojibake version of the Vietnamese
  // default. Repair only those known default strings; preserve custom wording.
  if (module.id === 'teacher_20_11_round_medal' || module.id === 'teacher_20_11_medal') {
    const savedDefault = String(currentParams.text ?? '');
    const brokenDefaults = new Set(['M?NG 20.11', 'M\u00c3\u00bbNG 20.11', 'M\u00e1\u00bb\u00aaNG 20.11', 'M\u00c3\u00a1\u00c2\u00bb\u00c2\u00aaNG 20.11', 'M\u00c3\u0192\u00c2\u00a1\u00c2\u00bb\u00c2\u00aaNG 20.11']);
    if (brokenDefaults.has(savedDefault)) {
      currentParams.text = module.textParams?.find(t => t.key === 'text')?.default ?? 'MỪNG 20.11';
      persistParams();
    }
  }

  // Migrate the broken text-ribbon state from the old single-line control.
  // The previous UI rendered this textarea as an <input>, so localStorage may
  // contain the three default rows concatenated without newline separators.
  // Normalize before the first preview so the model cannot come back as one
  // 158 mm-wide row while the editor visibly shows three rows.
  if (module.id === 'text_ribbon_3line' && typeof currentParams.text === 'string' && !currentParams.text.includes('\n')) {
    const second = 'KIẾN TẠO';
    const third = 'VỮNG BƯỚC';
    const secondAt = currentParams.text.indexOf(second);
    const thirdAt = currentParams.text.indexOf(third);
    if (secondAt > 0 && thirdAt > secondAt) {
      currentParams.text = `${currentParams.text.slice(0, secondAt)}\n${currentParams.text.slice(secondAt, thirdAt)}\n${currentParams.text.slice(thirdAt)}`;
      persistParams();
    }
  }

  let previewDebounceTimer: any = null;

  // TASK 03 (2026-09-29) - DEBOUNCE FIX.
  //
  // This used to be 120 ms, which is far too short next to the actual cost of a preview:
  // measured at 3.1 s for a light module (e27_led_base) and up to 70 s for a heavy one
  // (lamp_020). At 120 ms a one-second drag still queued several worker jobs, and a
  // ten-second drag queued dozens - all for the same model, with only the last one
  // ever being seen.
  //
  // Worse, the debounce did not actually work at all: a SECOND slider hook further down
  // this function called updatePreview() directly on every input event (see the note
  // where it used to be), so every event fired an immediate request and this timer never
  // got the chance to coalesce anything.
  //
  // 450 ms is chosen to sit inside the 400-600 ms target: long enough that a continuous
  // drag issues no request until the user pauses, short enough to still feel responsive.
  const PREVIEW_DEBOUNCE_MS = 450;
  const updatePreviewDebounced = (delay = PREVIEW_DEBOUNCE_MS) => {
    if (previewDebounceTimer) clearTimeout(previewDebounceTimer);
    previewDebounceTimer = setTimeout(() => {
      previewDebounceTimer = null;
      updatePreview();
    }, delay);
  };

  // TASK 28 (2026-09-30) - MANUAL MODE THAT ACTUALLY REDUCES LOAD.
  //
  // The old Manual mode locked the sliders and gave every slider its own Update button, so
  // adjusting five values took five clicks and produced five previews - exactly the server
  // load the mode existed to prevent. It also could not be dragged, which is the one thing a
  // slider is for.
  //
  // Now: dragging always works, the change is recorded and the model is left alone until the
  // user presses one Update button. `pendingKeys` tracks WHICH sliders are waiting so the
  // button can show a count, and so an "unapplied changes" warning can be shown - without
  // that warning the viewport would silently display a stale model and the user would think
  // their edit had taken effect.
  const pendingKeys = new Set<string>();

  // TASK 28: `globalSliderMode` is module-level state, so the mode used to persist into the
  // next module you opened - you would switch modules, drag a slider, and nothing would happen
  // with no explanation. Reset on entry so each module starts from the same known state.
  // Value is `false` = MANUAL, matching the default in ui-framework.ts (Nam's decision).
  setGlobalSliderMode(false);

  function markDirty(key: string): void {
    if (key === 'showAssembled') return;  // pure client-side view toggle, no server call
    pendingKeys.add(key);
    refreshUpdateButton();
  }

  function clearDirty(): void {
    pendingKeys.clear();
    refreshUpdateButton();
  }

  const sidebar = renderSidebar(module, document.getElementById('sidebar')!, (key: string, value: any) => {
    if (module.id === 'text_bead_50' && key === 'text') {
      currentParams.text = '50';
      persistParams();
      return;
    }
    currentParams[key] = value;
    persistParams();  // TASK 19: covers selects and number inputs too, not just sliders
    if (key === 'showAssembled') return;
    // A text control's button is explicitly its Update action. Do not leave the typed
    // letters/font sitting in a pending state behind the separate viewer-level manual button.
    //
    // FIX (2026-10-14): this used to be gated on `module.id === 'text_bead'`, so every OTHER
    // module with a text box (text_keychain, name_clicker, chess_medals, ...) fell through to
    // the Manual-mode branch below and only called markDirty(). Pressing the button labelled
    // "Update 3D Text" then changed nothing on screen - the model kept the old text until the
    // user found the separate floating "Update model" button. Measured in headless Chrome on
    // text_keychain: typing "50" + clicking "Update 3D Text" sent no preview request at all.
    // Gate on the SCHEMA instead of a hardcoded id: any param the module declares as a text
    // control applies immediately when its own button fires.
    //
    // No debounce here: this callback is invoked once per button click, not per keystroke,
    // so a plain updatePreview() cannot storm the worker.
    const isTextParam = module.textParams?.some((t) => t.key === key) ?? false;
    if (isTextParam) {
      clearDirty();
      updatePreview();
      return;
    }
    // Real-time mode previews immediately; Manual mode waits for the Update button.
    if (getGlobalSliderMode()) {
      updatePreviewDebounced();
    } else {
      markDirty(key);
    }
  },
  // TASK 19 fix: hand over the restored params so the controls show them instead of defaults.
  currentParams);

  onAppQualityChange(() => { updatePreviewDebounced(120); });

  // Hook sliders with undo tracking.
  //
  // FOUND IN THE BROWSER, 2026-09-30: this block used to ALSO handle the input event and do
  // exactly what the renderSidebar callback above already does (set currentParams, persist,
  // preview-or-markDirty). Both ran for a single drag, so every change was processed TWICE.
  // Real-time hid it (the debounce collapsed the two preview calls into one), but Manual mode
  // exposed it: `markDirty` ran twice and pressing Update fired TWO requests instead of one -
  // measured in Chrome as two POSTs 4 ms apart.
  //
  // This is the same class of bug as the duplicate hook removed in TASK 03; that fix removed a
  // third copy and missed this one. The undo push on focus is the only thing this block needs
  // to do, so the input handler is gone entirely - the callback in renderSidebar owns it.
  sidebar.root.querySelectorAll<HTMLInputElement>('input[type="range"]').forEach((input) => {
    const wrap = input.closest('.control-group') as HTMLElement;
    const key = wrap?.dataset.sliderKey;
    if (!key) return;

    input.addEventListener('focus', () => {
      pushState();
    });
  });

  // ---- Get viewer element first ----
  const viewerEl = document.getElementById('viewer')!;
  const modelHealthLeds = createModelHealthLeds();

  // Container for right overlays: RIGHT PANEL & QUICK TOOLBAR (docked vertically, never overlapping with bottom widgets or extensions)
  const rightOverlayCol = document.createElement('div');
  rightOverlayCol.id = 'right-overlay-col';
  // No align-items:flex-end: the default `stretch` makes the quick toolbar below match the width
  // of the View & Display panel, so the right column reads as one stack rather than two
  // differently-sized floating cards.
  rightOverlayCol.style.cssText = [
    'position:absolute',
    'top:12px',
    'right:12px',
    'display:flex',
    'flex-direction:column',
    'gap:8px',
    'z-index:20',
    'pointer-events:none',
    'max-height:calc(100vh - 24px)',
    'box-sizing:border-box',
  ].join(';');

  // ---- R23 Consolidated Right Panel ----
  const rightPanel = new ConsolidatedRightPanel();
  const profileInspector = new ProfileInspector();
  const profileDrawingTool = new ProfileDrawingTool();
  rightPanel.setModuleSchema(module);
  rightPanel.el.style.position = 'relative';
  rightPanel.el.style.top = '0';
  rightPanel.el.style.right = '0';
  rightPanel.el.style.pointerEvents = 'auto';
  rightPanel.el.style.flex = '1 1 auto';
  rightPanel.el.style.minHeight = '0';
  rightPanel.el.style.overflowY = 'auto';
  rightPanel.el.style.overflowX = 'hidden';
  rightPanel.el.style.paddingRight = '4px';
  rightOverlayCol.appendChild(rightPanel.el);
  viewerEl.appendChild(rightOverlayCol);

  rightPanel.onPrintEstimateOptionsChange(() => updatePrintEstimate());
  rightPanel.onProfileDrawingOpen(() => profileDrawingTool.open());
  rightPanel.onSvgTo3DOpen(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.svg,image/svg+xml';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        statusLog.setStatus(`Loading SVG to 3D: ${file.name}`, 'info');
        const text = await file.text();
        const svgMesh = buildSvgPreviewMeshFromText(
          text,
          Number(currentParams.targetHeight ?? 80),
          Number(currentParams.extrusion ?? 4),
        );
        previewRenderer.clearGroups(new Set([0]));
        // SVGLoader creates a fresh client-side mesh. Remove red edge markers
        // left by a previous C++ preview so they cannot falsely describe it.
        if (watertightEdges) {
          watertightEdges.traverse((child) => {
            if (!(child instanceof THREE.LineSegments)) return;
            child.geometry.dispose();
            (child.material as THREE.Material).dispose();
          });
          watertightEdges.removeFromParent();
          watertightEdges = null;
        }
        watertightEdgesRequested = false;
        const mesh = previewRenderer.createPartMesh(svgMesh, 0, {
          colorKey: 'svg_to_3d',
          color: partColors.svg_to_3d || '#4a90e2',
          name: file.name,
        }, isInternalLightOn);
        latestMeshData = svgMesh;
        if (svgMesh.analysis) {
          modelHealthLeds.setWatertight(svgMesh.analysis.isWatertight, svgMesh.analysis.nonManifoldEdgeCount);
        }
        if (mesh && assemblyGroup) {
          assemblyGroup.updateMatrixWorld(true);
          fitCameraToObject(camera, controls, assemblyGroup, 1.75);
        }
        statusLog.setStatus(`✓ SVG to 3D ready • ${svgMesh.triangleCount}△ • ${file.name}`, 'ok');
      } catch (err) {
        statusLog.setStatus(`SVG to 3D error: ${err instanceof Error ? err.message : String(err)}`, 'error');
      }
    };
    input.click();
  });

  // Explode toggle — ONE mechanism, not two.
  //
  // FIX (2026-10-03): this used to be two writers fighting over child.position.z. The button
  // called updateExplodePositions(), which set position.z directly from module.objects; but
  // animate() ALSO ran applyUniversalCascadingExplode() every frame, deriving its factor from
  // currentParams['showAssembled'] — a slider no module defines any more (removed by
  // scripts/remove-showassembled-slider.mjs). With the param undefined the factor was pinned at
  // 1.0 (assembled), so every frame reset position.z to 0 and erased the button's work.
  // Measured in headless Chrome on bambu_led_base: clicking the button changed its label and the
  // status line, but no pixel in the 3D viewport moved.
  //
  // Now the button only sets the target, and the animate() lerp (through the schema-driven
  // applyUniversalCascadingExplode) is the only thing that writes Z. 1.0 = assembled, 0.0 = exploded.
  //
  // These live inside startApp, not at module scope: module-scope state leaked across modules, so
  // a module you had exploded left `isExploded = true` behind and the next one opened mislabelled.
  let isExploded = false;
  let targetExplodeFactor = 1.0;
  let currentExplodeFactor = 1.0;

  rightPanel.onExplodeToggle((exploded: boolean) => {
    isExploded = exploded;
    targetExplodeFactor = exploded ? 0.0 : 1.0;
    statusLog.setStatus(isExploded ? '🧩 Exploded Assembly View ON' : '🧩 Assembled View ON', 'ok');
  });

  // Status bar widget (docked in bottom of left overlay column)
  const statusLog = new StatusLogViewer();
  statusLog.el.style.cssText = [
    'background:rgba(255,255,255,0.92)',
    'padding:8px 12px',
    'border-radius:6px',
    'border:1px solid #e5e5e5',
    'font-family:var(--mono-font), monospace',
    'font-size:11px',
    'color:#1a1a1a',
    'line-height:1.6',
    'backdrop-filter:blur(6px)',
    '-webkit-backdrop-filter:blur(6px)',
    'box-shadow:0 1px 3px rgba(0,0,0,0.04)',
    // No fixed width: stretch to match the widest panel in left-overlay-col (see that column).
    'align-self:stretch',
    'box-sizing:border-box',
    'overflow-wrap:anywhere',
    'pointer-events:auto',
  ].join(';');

  // ============================================================================
  // KEYBOARD SHORTCUTS & CONTROLS POPUP MODAL
  // ============================================================================
  let shortcutsModalEl: HTMLElement | null = null;

  function toggleShortcutsModal(force?: boolean): void {
    if (shortcutsModalEl) {
      if (force === true) return;
      const el = shortcutsModalEl;
      shortcutsModalEl = null;
      el.style.opacity = '0';
      const m = el.firstElementChild as HTMLElement;
      if (m) m.style.transform = 'scale(0.96)';
      setTimeout(() => el.remove(), 180);
      return;
    }
    if (force === false) return;

    const overlay = document.createElement('div');
    overlay.id = 'shortcuts-modal-overlay';
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.55);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      z-index: 99999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      opacity: 0;
      transition: opacity 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    `;

    const modal = document.createElement('div');
    modal.style.cssText = `
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.08);
      width: 580px;
      max-width: 100%;
      max-height: calc(100vh - 48px);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transform: scale(0.96);
      transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    `;

    const kbd = (key: string) => `<kbd style="display:inline-flex;align-items:center;justify-content:center;min-width:24px;height:24px;padding:0 7px;font-family:var(--mono-font, monospace);font-size:11px;font-weight:700;color:#0f172a;background:#f8fafc;border:1px solid #cbd5e1;border-bottom:2px solid #94a3b8;border-radius:5px;box-shadow:0 1px 2px rgba(0,0,0,0.06);white-space:nowrap;">${key}</kbd>`;

    const row = (keysHtml: string, desc: string, detail?: string) => `
      <div style="display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid #f1f5f9;gap:12px;">
        <div style="display:flex;flex-direction:column;gap:1px;">
          <span style="font-size:12.5px;font-weight:600;color:#1e293b;">${desc}</span>
          ${detail ? `<span style="font-size:11px;color:#64748b;">${detail}</span>` : ''}
        </div>
        <div style="display:flex;align-items:center;gap:4px;flex-shrink:0;">${keysHtml}</div>
      </div>
    `;

    modal.innerHTML = `
      <div style="padding:14px 18px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between;background:#f8fafc;">
        <div style="display:flex;align-items:center;gap:8px;">
          <div style="width:28px;height:28px;border-radius:6px;background:#e0f2fe;color:#0284c7;display:flex;align-items:center;justify-content:center;font-size:15px;">⌨️</div>
          <div>
            <h3 style="margin:0;font-size:14px;font-weight:700;color:#0f172a;letter-spacing:-0.01em;">Keyboard Shortcuts & Controls</h3>
            <p style="margin:2px 0 0 0;font-size:11px;color:#64748b;">Quick 3D navigation and viewport controls guide</p>
          </div>
        </div>
        <button id="close-shortcuts-modal" type="button" style="width:28px;height:28px;border-radius:6px;border:1px solid #e2e8f0;background:#ffffff;color:#64748b;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:14px;line-height:1;transition:all 0.15s;">✕</button>
      </div>

      <div style="padding:14px 18px;overflow-y:auto;display:flex;flex-direction:column;gap:16px;">
        <!-- Group 1: Mouse & 3D Navigation -->
        <div>
          <div style="font-size:10.5px;font-weight:700;color:#0369a1;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px;display:flex;align-items:center;gap:6px;">
            <span>🖱️ Mouse & 3D Navigation</span>
          </div>
          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:2px 12px;">
            ${row(`${kbd('Left Drag')}`, 'Orbit 3D View', 'Rotate camera around model')}
            ${row(`${kbd('Scroll')}`, 'Zoom In / Out', 'Dolly camera closer or farther')}
            ${row(`${kbd('Right Drag')}`, 'Pan View', 'Slide viewport horizontally & vertically')}
            ${row(`${kbd('Double Click')}`, 'Focus & Zoom to Point', 'Set orbit pivot directly on clicked surface')}
            ${row(`${kbd('Shift')} + ${kbd('Click')}`, 'Multi-select Parts', 'Select multiple objects in tree')}
          </div>
        </div>

        <!-- Group 2: Tool & Action Shortcuts -->
        <div>
          <div style="font-size:10.5px;font-weight:700;color:#0369a1;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px;display:flex;align-items:center;gap:6px;">
            <span>⚡ Tool & Action Shortcuts</span>
          </div>
          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:2px 12px;">
            ${row(`${kbd('F')}`, 'Fit View (Home)', 'Fit all objects neatly in viewport')}
            ${row(`${kbd('Z')}`, 'Toggle Zoom to Point', 'Click any surface point to zoom in close')}
            ${row(`${kbd('W')}`, 'Wireframe / Solid', 'Toggle wireframe overlay mode')}
            ${row(`${kbd('G')}`, 'Toggle Ground Grid', 'Show or hide ground reference grid')}
            ${row(`${kbd('Esc')}`, 'Exit Measure / Section', 'Turn off the active tool and clear measurement; dismiss dialogs first')}
          </div>
        </div>

        <!-- Group 3: CAD / Blender Orthographic Views -->
        <div>
          <div style="font-size:10.5px;font-weight:700;color:#0369a1;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px;display:flex;align-items:center;gap:6px;">
            <span>📐 CAD / Blender Orthographic Views</span>
          </div>
          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:2px 12px;">
            ${row(`${kbd('1')}`, 'Front View', 'Orthographic front projection')}
            ${row(`${kbd('3')}`, 'Right View', 'Orthographic right projection')}
            ${row(`${kbd('7')}`, 'Top View', 'Orthographic top projection')}
            ${row(`${kbd('9')}`, 'Bottom View', 'Orthographic bottom projection')}
          </div>
        </div>
      </div>

      <div style="padding:10px 18px;background:#f8fafc;border-top:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between;font-size:11.5px;color:#64748b;">
        <span>Tip: Press <kbd style="padding:1px 5px;background:#fff;border:1px solid #cbd5e1;border-radius:3px;font-size:10.5px;font-weight:600;">?</kbd> anytime to open this guide.</span>
        <button id="close-shortcuts-modal-btn" type="button" style="padding:5px 14px;background:#0f172a;color:#ffffff;border:none;border-radius:6px;font-size:11.5px;font-weight:600;cursor:pointer;">Got it</button>
      </div>
    `;

    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    shortcutsModalEl = overlay;

    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
      modal.style.transform = 'scale(1)';
    });

    const closeModal = () => {
      overlay.style.opacity = '0';
      modal.style.transform = 'scale(0.96)';
      setTimeout(() => {
        overlay.remove();
        if (shortcutsModalEl === overlay) shortcutsModalEl = null;
      }, 180);
    };

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeModal();
    });

    modal.querySelector('#close-shortcuts-modal')?.addEventListener('click', closeModal);
    modal.querySelector('#close-shortcuts-modal-btn')?.addEventListener('click', closeModal);
  }

  // ---- UNDO / REDO / SHORTCUTS HELP BAR AT TOP-CENTER OF VIEWPORT (Sleek Apple/Figma Pill Toolbar) ----
  const topCenterUndoBar = document.createElement('div');
  topCenterUndoBar.id = 'top-center-undo-bar';
  topCenterUndoBar.style.cssText = [
    'position:absolute',
    'top:12px',
    'left:50%',
    'transform:translateX(-50%)',
    'display:inline-flex',
    'align-items:center',
    'justify-content:center',
    'height:30px',
    'gap:2px',
    'background:rgba(255, 255, 255, 0.95)',
    'padding:0 3px',
    'border-radius:15px',
    'border:1px solid #cbd5e1',
    'box-shadow:0 3px 10px rgba(0,0,0,0.06)',
    'z-index:20',
    'backdrop-filter:blur(10px)',
    '-webkit-backdrop-filter:blur(10px)',
    'pointer-events:auto',
    'box-sizing:border-box',
  ].join(';');

  const topUndoBtn = document.createElement('button');
  topUndoBtn.id = 'undo-btn';
  topUndoBtn.title = 'Undo';
  topUndoBtn.innerHTML = '<span style="display:inline-flex;align-items:center;gap:4px;transform:translateY(-0.5px);"><span>↶</span><span>Undo</span></span>';
  topUndoBtn.style.cssText = `
    height: 24px;
    padding: 0 10px;
    font-size: 11px;
    font-weight: 600;
    border: none;
    border-radius: 12px;
    background: transparent;
    color: #0f172a;
    cursor: pointer;
    font-family: var(--mono-font);
    transition: all 0.15s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    box-sizing: border-box;
    margin: 0;
  `;
  topUndoBtn.addEventListener('mouseenter', () => { topUndoBtn.style.background = '#f1f5f9'; });
  topUndoBtn.addEventListener('mouseleave', () => { topUndoBtn.style.background = 'transparent'; });

  const topDivider1 = document.createElement('div');
  topDivider1.style.cssText = 'width:1px;height:12px;background:#cbd5e1;flex-shrink:0;';

  const topHelpBtn = document.createElement('button');
  topHelpBtn.id = 'help-shortcuts-btn';
  topHelpBtn.type = 'button';
  topHelpBtn.title = 'Keyboard Shortcuts & Controls (?)';
  topHelpBtn.innerHTML = `
    <span style="display:inline-flex;align-items:center;gap:4px;transform:translateY(-0.5px);">
      <span style="display:inline-flex;align-items:center;width:13px;height:13px;color:#0f172a;">${UI_ICONS.keyboard}</span>
      <span>Shortcuts</span>
      <span style="font-size:9.5px;font-weight:700;background:#e2e8f0;color:#0369a1;border-radius:3px;padding:0 3.5px;line-height:12px;">?</span>
    </span>
  `;
  topHelpBtn.style.cssText = `
    height: 24px;
    padding: 0 9px;
    font-size: 11px;
    font-weight: 600;
    border: none;
    border-radius: 12px;
    background: transparent;
    color: #0f172a;
    cursor: pointer;
    font-family: var(--mono-font);
    transition: all 0.15s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    box-sizing: border-box;
    margin: 0;
  `;
  topHelpBtn.addEventListener('mouseenter', () => { topHelpBtn.style.background = '#f1f5f9'; });
  topHelpBtn.addEventListener('mouseleave', () => { topHelpBtn.style.background = 'transparent'; });
  topHelpBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleShortcutsModal();
  });

  const topDivider2 = document.createElement('div');
  topDivider2.style.cssText = 'width:1px;height:12px;background:#cbd5e1;flex-shrink:0;';

  const topRedoBtn = document.createElement('button');
  topRedoBtn.id = 'redo-btn';
  topRedoBtn.title = 'Redo';
  topRedoBtn.innerHTML = '<span style="display:inline-flex;align-items:center;gap:4px;transform:translateY(-0.5px);"><span>↷</span><span>Redo</span></span>';
  topRedoBtn.style.cssText = `
    height: 24px;
    padding: 0 10px;
    font-size: 11px;
    font-weight: 600;
    border: none;
    border-radius: 12px;
    background: transparent;
    color: #0f172a;
    cursor: pointer;
    font-family: var(--mono-font);
    transition: all 0.15s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    box-sizing: border-box;
    margin: 0;
  `;
  topRedoBtn.addEventListener('mouseenter', () => { topRedoBtn.style.background = '#f1f5f9'; });
  topRedoBtn.addEventListener('mouseleave', () => { topRedoBtn.style.background = 'transparent'; });

  topCenterUndoBar.appendChild(topUndoBtn);
  topCenterUndoBar.appendChild(topDivider1);
  topCenterUndoBar.appendChild(topHelpBtn);
  topCenterUndoBar.appendChild(topDivider2);
  topCenterUndoBar.appendChild(topRedoBtn);
  viewerEl.appendChild(topCenterUndoBar);

  // ============================================================================
  // CROP SNAPSHOT — drag a region, then copy it or save it
  // ============================================================================
  //
  // The 📸 button grabs the whole viewport and saves it straight to disk. This is for the other
  // case, requested 2026-10-03: you want PART of the view (one detail, one dimension line), and
  // you may want it on the CLIPBOARD to paste into a message rather than as a file.
  //
  // Flow: click the button, drag the region, then Copy or Save or Cancel.


  // Container for left overlays: DIMENSIONS HUD & SCENE TREE (auto 12px apart, never overlapping)
  //
  // align-items:stretch (the default, so it is not set) makes every child the SAME width as the
  // widest one. The children therefore must not carry a fixed `width` - a set width opts a flex
  // item out of stretching - so each uses min-width for its floor instead. The column's own width
  // resolves to the widest child's max-content, and max-width stops a long status message from
  // dragging the whole column across the screen.
  const leftOverlayCol = document.createElement('div');
  leftOverlayCol.id = 'left-overlay-col';
  leftOverlayCol.style.cssText = [
    'position:absolute',
    'top:12px',
    'left:12px',
    'display:flex',
    'flex-direction:column',
    'gap:12px',
    'z-index:15',
    'pointer-events:none',
    'max-height:calc(100% - 110px)',
    'max-width:330px',
  ].join(';');

  // ---- Dimensions HUD (top-left) ----
  const dimsHud = document.createElement('div');
  dimsHud.id = 'dims-hud';
  dimsHud.style.cssText = [
    'background:rgba(255,255,255,0.94)',
    'padding:8px 12px',
    'border-radius:8px',
    'border:1px solid #e2e8f0',
    'font-family:var(--mono-font), monospace',
    'font-size:11px',
    'color:#1e293b',
    'line-height:1.6',
    'backdrop-filter:blur(8px)',
    '-webkit-backdrop-filter:blur(8px)',
    'box-shadow:0 2px 8px rgba(0,0,0,0.06)',
    'box-sizing:border-box',
    'min-width:140px',
    'pointer-events:auto',
  ].join(';');
  dimsHud.innerHTML = '<div style="font-weight:600;font-size:10px;color:#64748b;letter-spacing:0.06em;margin-bottom:2px;">DIMENSIONS</div><div>W × D × H<br><span id="dims-val" style="font-weight:600;">—</span></div>';
  leftOverlayCol.appendChild(dimsHud);

  // Initialize default colors from schema
  if (module.colorConfigs) {
    for (const cfg of module.colorConfigs) {
      if (!partColors[cfg.key]) {
        partColors[cfg.key] = cfg.default;
      }
    }
  }

  // ---- Scene Tree Viewer (Object -> Part hierarchy, visibility eye, colors, Zoom & Isolate) ----
  sceneTree = new SceneTreeViewer(module, {
    onVisibilityChange: () => {
      applyVisibility();
      updateSectionCutOutline();
    },
    onSelect: (type, id, groupId) => {
      // The planar badge handles are opt-in: no badge part is selected means
      // no Move/Scale/Rotate gizmo should sit on top of the model.
      selectedBadgeItemIndex = -1;
      if (type === 'object') {
        selectedGroup = id as any;
      } else if (type === 'part') {
        selectedGroup = (id === 'legend_text' ? 'letter' : id) as any;
      } else {
        selectedGroup = 'all';
      }
      applySelectionHighlighting();
      rightPanel.setSelectedGroup(selectedGroup);
      updatePrintEstimate();
      updateDimensionsHud();
      updateArchitecturalDimensions();

      if (module.id === 'swing_badge_keychain' && groupId !== undefined) {
        let idx = -1;
        if (groupId === 1) idx = 0;       // Số 50
        else if (groupId === 10) idx = 1; // 1976-2026
        else if (groupId === 8) idx = 2;  // Dải Banner
        else if (groupId === 9) idx = 3;  // Hoa Lá 1
        else if (groupId === 11) idx = 4; // Hoa Lá 2
        else if (groupId === 12) idx = 5; // Hoa Lá 3
        if (idx >= 0 && BADGE_ITEMS[idx]) {
          selectedBadgeItemIndex = idx;
          syncBadge2DGizmo();
          statusLog.setStatus(`🎯 Chọn 2D Đế Tròn: ${BADGE_ITEMS[idx]!.label}`, 'info');
        }
      }
    },
    onColorChange: (colorKey, hex) => {
      partColors[colorKey] = hex;
      applyPartColors();
    },
    onZoomSelected: () => {
      zoomToSelected();
    },
    onIsolateToggle: (isIsolated) => {
      updateSectionCutOutline();
      statusLog.setStatus(isIsolated ? '👁️ Isolating selected object' : 'Exited isolate mode (Showing all objects)', 'ok');
    },
  });
  leftOverlayCol.appendChild(sceneTree.el);
  leftOverlayCol.appendChild(statusLog.el);
  // TASK 20 (2026-09-30): the model-health widget was created, updated on every preview and
  // given click handlers - but its element was never appended to anything, so it never
  // appeared. Nothing in the UI reported whether a model was watertight or needed supports.
  // This also mattered for TASK 13: the whole point of adding `analysis.skipped` was to stop
  // the widget claiming "watertight" for a mesh that was never analysed, and that work could
  // not reach anyone while the widget was invisible.
  // Placed with the other left-column overlays, above the scene tree.
  leftOverlayCol.appendChild(modelHealthLeds.el);
  viewerEl.appendChild(leftOverlayCol);

  // Two toggles: panel 2 (left overlay) and panel 3 (right overlay).
  // Panel 1 (the main application sidebar) remains fixed.
  const columnToggles: Array<{
    host: HTMLElement;
    side: 'left' | 'right';
    button: HTMLButtonElement;
    collapsed: boolean;
    lastRect: { left: number; right: number; top: number; height: number };
    sync: () => void;
  }> = [];
  const addColumnToggle = (host: HTMLElement, side: 'left' | 'right') => {
    const button = document.createElement('button');
    const state = {
      host,
      side,
      button,
      collapsed: false,
      lastRect: { left: 0, right: 0, top: 12, height: window.innerHeight - 24 },
      sync: () => {},
    };
    button.type = 'button';
    button.style.cssText = 'position:fixed;z-index:100;width:22px;height:42px;padding:0;margin:0;border:1px solid #cbd5e1;border-radius:6px;background:rgba(255,255,255,0.94);color:#475569;font-size:14px;line-height:1;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,0.12);pointer-events:auto;transition:left 180ms ease,right 180ms ease,top 180ms ease;';
    const sync = () => {
      const measured = host.getBoundingClientRect();
      // visibility:hidden produces a zero rect. Keep the last real rect so the toggle
      // does not jump to the middle of the viewport while the panel is collapsed.
      if (measured.width > 0 && measured.height > 0) {
        state.lastRect = {
          left: measured.left,
          right: measured.right,
          top: measured.top,
          height: measured.height,
        };
      }
      const rect = state.lastRect;
      button.textContent = state.collapsed ? (side === 'left' ? '›' : '‹') : (side === 'left' ? '‹' : '›');
      button.title = state.collapsed ? `Open ${side} panel` : `Collapse ${side} panel`;
      button.style.top = `${Math.max(12, Math.round(rect.top + rect.height / 2 - 21))}px`;
      if (side === 'left') {
        // When collapsed, move the restore control beside the fixed app sidebar; when open,
        // keep it attached to the overlay panel's outer edge.
        const sidebarRight = document.getElementById('sidebar')?.getBoundingClientRect().right;
        const left = state.collapsed && sidebarRight !== undefined ? sidebarRight - 11 : rect.right - 11;
        button.style.left = `${Math.max(2, Math.round(left))}px`;
        button.style.right = 'auto';
      } else {
        // A collapsed right column has no layout width: pin its restore button to the viewport edge.
        button.style.right = `${Math.max(2, Math.round(state.collapsed ? 2 : window.innerWidth - rect.left - 11))}px`;
        button.style.left = 'auto';
      }
    };
    state.sync = sync;
    button.addEventListener('click', () => {
      state.collapsed = !state.collapsed;
      host.style.visibility = state.collapsed ? 'hidden' : 'visible';
      host.style.pointerEvents = state.collapsed ? 'none' : '';
      if (host.id === 'sidebar') {
        host.style.width = state.collapsed ? '0px' : '';
        host.style.padding = state.collapsed ? '0px' : '';
      }
      sync();
    });
    document.body.appendChild(button);
    columnToggles.push(state);
    sync();
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => state.sync());
      observer.observe(host);
    }
  };
  // Panel 1 (main application sidebar) is fixed and intentionally has no collapse button.
  addColumnToggle(leftOverlayCol, 'left');
  addColumnToggle(rightOverlayCol, 'right');
  window.addEventListener('resize', () => columnToggles.forEach((state) => state.sync()));

  // ---- Bottom Progress Bar (long loading bar at the bottom of the screen) ----
  const progressBar = new BottomProgressBar();
  viewerEl.appendChild(progressBar.el);

  // TASK 17 (2026-09-30): wire the Cancel button to the abort controller that already existed.
  // cancelPreview() aborts the in-flight fetch, which makes fetchPreview resolve with null
  // (handled in updatePreview). The server side needs no new code: TASK 02 already makes the
  // API kill its C++ worker as soon as the client disconnects, so the pool slot is released
  // in ~300 ms instead of the worker running to completion.
  progressBar.setOnCancel(() => {
    previewController.invalidate();
    cancelPreview();
    statusLog.setStatus('Cancelling...', 'info');
  });

  let autoRotate = false;
  let is2DView = false;

  // ---- Three.js scene ----
  const container = document.getElementById('canvas-container')!;

  // ---------------------------------------------------------------------------
  // TOOLTIP TRA MA MANH (2026-09-30)
  //
  // Hovering a part shows that part's code right at the pointer, with a copy button.
  // Purpose: the user can read a part code and say exactly "fix part X" instead of having to
  // mo ta "cai mat o tren ay".
  let hasPartCodes = false;

  const scene = new THREE.Scene();
  // TWO cameras. Perspective for the 3D views, orthographic for the locked 2D projection
  // views (Front/Top/Right). A perspective camera pointed straight at a face is only
  // "2D-ish": near edges still render larger than far ones, so parallel edges converge and
  // a measurement taken across the frame is wrong. Orthographic projects in parallel - that
  // is what makes Front/Top/Right true engineering views.
  //
  // Only one is active at a time. `camera` always points at the active one and every other
  // site in this file (raycasting, rendering, marker sizing) keeps using `camera` unchanged;
  // OrbitControls is re-pointed on each switch.
  const CAM_FOV = 35;
  const perspCamera = new THREE.PerspectiveCamera(CAM_FOV, container.clientWidth / container.clientHeight, 1, 3000);
  const orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 5000);
  let camera: THREE.PerspectiveCamera | THREE.OrthographicCamera = perspCamera;
  camera.up.set(0, 0, 1);
  // Pre-calculate initial camera position to match module bounds on load (ZERO camera jump/pop)
  const defaultMaxDim = Math.max(
    ...module.sliders.filter(s => s.key === 'width' || s.key === 'depth' || s.key === 'height' || s.key === 'outerDiameter').map(s => Number(s.default) || 0),
    50
  );
  const estDist = (defaultMaxDim / 2) / Math.tan((CAM_FOV * Math.PI / 180) / 2) * 1.75;
  // The keychain loop is an XY-plane ring. A diagonal iso camera makes it look like a
  // detached puck, so give this module a front/top-biased presentation where the hole and
  // attachment are immediately readable (also used as the thumbnail presentation).
  const initDir = module.id === 'core_keychain_loop'
    ? new THREE.Vector3(0, -1, 1.15).normalize()
    : new THREE.Vector3(1, -1, 0.75).normalize();
  camera.position.copy(initDir.multiplyScalar(estDist));
  camera.lookAt(0, 0, 0);

  /** Half-height of the orthographic frustum, derived from a perspective distance so that
   *  switching between the two cameras shows the same amount of the model (no jump). */
  function orthoHalfHeightForDistance(dist: number): number {
    return Math.max(dist * Math.tan((CAM_FOV * Math.PI / 180) / 2), 1);
  }

  /** Resize the orthographic frustum to the canvas aspect, keeping `halfHeight` fixed. */
  function setOrthoFrustum(halfHeight: number): void {
    const aspect = container.clientWidth / Math.max(container.clientHeight, 1);
    orthoCamera.top = halfHeight;
    orthoCamera.bottom = -halfHeight;
    orthoCamera.left = -halfHeight * aspect;
    orthoCamera.right = halfHeight * aspect;
    orthoCamera.updateProjectionMatrix();
  }

  /** Point the shared `camera` variable (and OrbitControls) at the given camera. */
  function activateCamera(next: THREE.PerspectiveCamera | THREE.OrthographicCamera): void {
    if (camera === next) return;
    next.position.copy(camera.position);
    next.up.copy(camera.up);
    next.lookAt(controls.target);
    if (next === orthoCamera) {
      // Match the perspective framing at the current distance.
      setOrthoFrustum(orthoHalfHeightForDistance(camera.position.distanceTo(controls.target)));
      orthoCamera.zoom = 1;
      orthoCamera.updateProjectionMatrix();
    }
    camera = next;
    controls.object = next;
    controls.update();
  }
  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
  renderer.setClearColor(0xffffff, 1.0);
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.autoClear = false;
  renderer.localClippingEnabled = true; // Enable 3D Section View / Clipping Plane
  
  // Real-world Light & Shadow Simulation setup (Strictly OFF in default daylight view)
  renderer.shadowMap.enabled = false;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  container.appendChild(renderer.domElement);
  const cropController = createCropAnnotationController({
    container,
    renderer,
    scene,
    getCamera: () => camera,
    moduleId: module.id,
    statusLog,
  });
  const { startCropMode, endCropMode } = cropController;

  const dimensionsOverlay = new DimensionOverlay(container);
  let architecturalDimensionsOn = true;
  dimensionsOverlay.setVisible(architecturalDimensionsOn);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.object.up.set(0, 0, 1);
  controls.target.set(0, 0, 0);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.autoRotateSpeed = 2.5;
  controls.update();

  // Default Daylight Setup (Zero shadows, standard clean 3D workspace)
  const hemiLight = new THREE.HemisphereLight(0xffffff, 0x8d99ae, 0.75); // Neutral silver ambient
  hemiLight.position.set(0, 0, 300);
  scene.add(hemiLight);

  // 1. Key Light (Main daylight from front-right)
  const mainLight = new THREE.DirectionalLight(0xffffff, 0.95);
  mainLight.position.set(150, -200, 250);
  mainLight.castShadow = false;
  mainLight.shadow.mapSize.width = 2048;
  mainLight.shadow.mapSize.height = 2048;
  mainLight.shadow.camera.near = 10;
  mainLight.shadow.camera.far = 1000;
  mainLight.shadow.camera.left = -250;
  mainLight.shadow.camera.right = 250;
  mainLight.shadow.camera.top = 250;
  mainLight.shadow.camera.bottom = -250;
  mainLight.shadow.bias = -0.0005;
  scene.add(mainLight);

  // 2. Fill Light (Soft daylight fill from front-left to prevent harsh dark side)
  const fillLight = new THREE.DirectionalLight(0xf8fafc, 0.45);
  fillLight.position.set(-150, 150, 100);
  fillLight.castShadow = false;
  fillLight.shadow.mapSize.width = 1024;
  fillLight.shadow.mapSize.height = 1024;
  fillLight.shadow.camera.near = 10;
  fillLight.shadow.camera.far = 1000;
  fillLight.shadow.camera.left = -250;
  fillLight.shadow.camera.right = 250;
  fillLight.shadow.camera.top = 250;
  fillLight.shadow.camera.bottom = -250;
  fillLight.shadow.bias = -0.0005;
  scene.add(fillLight);

  // 3. Rim / Back Light (Only active when Light Bulb Simulation is ON)
  const rimLight = new THREE.DirectionalLight(0x38bdf8, 0.85);
  rimLight.position.set(0, 250, 200);
  rimLight.castShadow = false;
  rimLight.shadow.mapSize.width = 1024;
  rimLight.shadow.mapSize.height = 1024;
  rimLight.shadow.camera.near = 10;
  rimLight.shadow.camera.far = 1000;
  rimLight.shadow.camera.left = -250;
  rimLight.shadow.camera.right = 250;
  rimLight.shadow.camera.top = 250;
  rimLight.shadow.camera.bottom = -250;
  rimLight.shadow.bias = -0.0005;
  rimLight.visible = false;
  scene.add(rimLight);

  // 4. Studio Floor Bounce Accent Light (Only active when Light Bulb Simulation is ON)
  const bounceLight = new THREE.DirectionalLight(0xfef3c7, 0.35);
  bounceLight.position.set(100, 100, -100);
  bounceLight.visible = false;
  scene.add(bounceLight);

  const gridHelper = createThinGrid(500, 100);
  gridHelper.rotation.x = Math.PI / 2;
  gridHelper.visible = true;
  scene.add(gridHelper);

  // Optional clean viewport background, selectable from the right panel.
  const solidGround = new THREE.Mesh(
    new THREE.PlaneGeometry(1200, 1200),
    new THREE.MeshBasicMaterial({ color: 0xf1f5f9, side: THREE.DoubleSide })
  );
  solidGround.position.z = -0.12;
  solidGround.visible = false;
  solidGround.renderOrder = -2;
  scene.add(solidGround);

  // Snapmaker build plate option: dark plate with a subtle branded mark.
  const snapmakerCanvas = document.createElement('canvas');
  snapmakerCanvas.width = 1000;
  snapmakerCanvas.height = 700;
  const snapmakerCtx = snapmakerCanvas.getContext('2d')!;
  snapmakerCtx.fillStyle = '#3f4143';
  snapmakerCtx.fillRect(0, 0, snapmakerCanvas.width, snapmakerCanvas.height);
  snapmakerCtx.fillStyle = '#f3f4f6';
  snapmakerCtx.font = '700 56px Arial, sans-serif';
  snapmakerCtx.textAlign = 'center';
  snapmakerCtx.textBaseline = 'middle';
  snapmakerCtx.fillText('snapmaker', 500, 350);
  const snapmakerBed = new THREE.Mesh(
    new THREE.PlaneGeometry(500, 350),
    new THREE.MeshBasicMaterial({
      map: new THREE.CanvasTexture(snapmakerCanvas),
      side: THREE.DoubleSide,
    }),
  );
  snapmakerBed.position.z = -0.14;
  snapmakerBed.visible = false;
  snapmakerBed.renderOrder = -2;
  scene.add(snapmakerBed);
  let groundStyle: 'grid' | 'snapmaker' | 'solid' | 'none' = 'grid';

  // Create procedural wood grain Canvas Texture for realistic Wooden Floor/Table
  const createWoodTexture = (): THREE.CanvasTexture => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;
    
    // Base warm oak wood background
    ctx.fillStyle = '#b47b48';
    ctx.fillRect(0, 0, 512, 512);

    // Draw wood grain lines & subtle noise
    ctx.fillStyle = '#8b5327';
    for (let i = 0; i < 120; i++) {
      const y = Math.random() * 512;
      const h = Math.random() * 3 + 1;
      ctx.globalAlpha = Math.random() * 0.25 + 0.05;
      ctx.fillRect(0, y, 512, h);
    }

    // Wood rings / knots effect
    ctx.globalAlpha = 0.08;
    ctx.strokeStyle = '#4a2810';
    for (let r = 20; r < 600; r += 15) {
      ctx.beginPath();
      ctx.arc(256, -100, r, 0, Math.PI * 2);
      ctx.stroke();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(4, 4);
    return texture;
  };

  const woodTexture = createWoodTexture();

  // Table surface (Wooden floor/table) — Only visible in Light Bulb mode
  const tablePlaneGeo = new THREE.PlaneGeometry(1200, 1200);
  const tablePlaneMat = new THREE.MeshStandardMaterial({
    map: woodTexture,
    roughness: 0.65,
    metalness: 0.05,
    side: THREE.DoubleSide
  });
  const tablePlane = new THREE.Mesh(tablePlaneGeo, tablePlaneMat);
  tablePlane.position.set(0, 0, -0.1); // Placed slightly below z=0
  tablePlane.receiveShadow = true;
  tablePlane.visible = false; // Hidden by default in normal mode
  scene.add(tablePlane);

  const { scene: gizmoScene, camera: gizmoCamera, interaction: viewCubeInteraction } = createViewCube(renderer, container);


  const cameraController = new CameraController();

  function startCameraTransition(
    targetPos: THREE.Vector3,
    targetUp: THREE.Vector3,
    targetLook: THREE.Vector3,
    duration = 320,
    startTarget = controls.target.clone()
  ): void {
    cameraController.start(camera, targetPos, targetUp, targetLook, duration, startTarget);
  }

  function onViewCubeTargetClicked(data: ViewCubeTargetData): void {
    const target = controls.target.clone();
    const pose = cameraController.poseForViewCube(camera.position, target, data.viewDir);

    if (camera === orthoCamera && data.type !== 'face') {
      activateCamera(perspCamera);
    }
    is2DView = false;
    controls.enableRotate = true;

    startCameraTransition(pose.position, pose.up, target, 320);
    statusLog.setStatus(`View: ${data.label}`, 'ok');
  }

  function getViewCubeHit(clientX: number, clientY: number): THREE.Intersection | null {
    return viewCubeInteraction.getHit(clientX, clientY);
  }

  function updateViewCubeHover(clientX: number, clientY: number): boolean {
    return viewCubeInteraction.updateHover(clientX, clientY);
  }

  controls.addEventListener('start', () => {
    cameraController.cancel();
  });

  // Group container for all assembly meshes
  assemblyGroup = new THREE.Group();
  scene.add(assemblyGroup);
  const previewRenderer = new PreviewRenderer(assemblyGroup);

  // ---- Internal Light Source Simulation (💡 PointLight inside 3D model) ----
  let isInternalLightOn = false;
  let lightLevel: 'low' | 'med' | 'high' = 'med';
  let lightMode: 'warm' | 'white' | 'mixed' = 'warm';
  // Studio / ambient light strength, independent of the lamp. Low keeps the room nearly dark so
  // the lamp's projected pattern dominates; High lifts the fill for a brighter, flatter read of
  // the whole model. Multiplies the base studio intensities set in the toggle handler.
  let envLevel: 'low' | 'med' | 'high' = 'med';
  const ENV_MULTIPLIER: Record<'low' | 'med' | 'high', number> = { low: 0.45, med: 1.0, high: 2.0 };

  // Intensity is in physical units with decay = 2, so the irradiance on a surface is
  // intensity / distance^2 - and distances here are in MILLIMETRES. The bulb sits ~110mm above
  // the table, so distance^2 is ~12000 and an intensity of 120 contributed about 1% next to the
  // studio lights (~2.95 combined), which is why the old fixed value showed no visible change
  // between levels. These values are chosen so the LAMP dominates the scene, which is the point
  // of the simulation; the studio lights are dimmed in tandem below.
  const LIGHT_INTENSITY: Record<'low' | 'med' | 'high', number> = { low: 4000, med: 12000, high: 30000 };
  const LIGHT_COLOR: Record<'warm' | 'white' | 'mixed', number> = {
    warm: 0xff9e00,   // warm bulb
    white: 0xffffff,  // daylight white
    mixed: 0xffd9a0,  // warm + white blend
  };

  // Physical PointLight: decay = 2 (inverse-square) so brightness falls off naturally.
  //
  // ⚠️ The shadow bias is in NORMALISED depth units - the depth range is divided by
  // (far - near) - so its world-space size is `bias * far`. The old values were
  // far = 1200 and bias = -0.002, i.e. 2.4mm of world depth, which is MORE than the 2mm
  // shade wall. Every wall therefore failed its own depth test, occluded nothing, and the
  // perforated pattern could never appear on the table - the shade acted transparent.
  // far is now set from the model bounds (see syncInternalLightPosition) and the bias is
  // kept ~25x below the wall thickness.
  const internalPointLight = new THREE.PointLight(LIGHT_COLOR.warm, 0, 0, 2);
  internalPointLight.castShadow = false;
  internalPointLight.shadow.mapSize.width = 2048;
  internalPointLight.shadow.mapSize.height = 2048;
  internalPointLight.shadow.camera.near = 0.5;
  internalPointLight.shadow.camera.far = 600;
  internalPointLight.shadow.bias = -0.0002;
  internalPointLight.shadow.radius = 1; // 1 = crisp; larger blurs the hole pattern away
  internalPointLight.position.set(0, 0, 50);
  internalPointLight.visible = false;
  scene.add(internalPointLight);

  // 64x48 segments, not 16x16. A sphere at 16 segments is visibly faceted - each flat quad
  // catches the studio highlight on its own edge and the silhouette kinks ("it looks bent /
  // polygonal", reported 2026-10-03). The bulb is small and is the one object the eye goes to, so
  // it is worth the ~200 extra triangles; this is not a background object.
  const bulbSphereGeo = new THREE.SphereGeometry(6, 64, 48);
  const bulbSphereMat = new THREE.MeshBasicMaterial({ color: 0xffea00 });
  const internalLightBulbMesh = new THREE.Mesh(bulbSphereGeo, bulbSphereMat);
  internalLightBulbMesh.visible = false;
  scene.add(internalLightBulbMesh);

  /** Push the current level + colour mode onto the light and its visible bulb. */
  const applyLightSettings = (): void => {
    internalPointLight.color.setHex(LIGHT_COLOR[lightMode]);
    internalPointLight.intensity = isInternalLightOn ? LIGHT_INTENSITY[lightLevel] : 0;
    // The bulb mesh shows the colour a bit brighter than the light itself (a filament is
    // near-white even in a warm bulb), so blend toward white.
    bulbSphereMat.color.setHex(LIGHT_COLOR[lightMode]).lerp(new THREE.Color(0xffffff), 0.25);
  };

  /** Studio (ambient) lighting. Two states, each with a 3-step strength:
   *  - lamp OFF: the default daylight workspace (grid, white background)
   *  - lamp ON:  dim fill so the lamp dominates, but the lights still cast SOFT shadows
   *              (shadow.intensity below 1) because removing them entirely looks flat and fake.
   *  envLevel scales the lamp-ON values only; it has no effect in daylight. */
  const applyStudioLighting = (): void => {
    if (!isInternalLightOn) {
      hemiLight.intensity = 0.75;
      mainLight.intensity = 0.95;
      fillLight.intensity = 0.45;
      rimLight.intensity = 0.85;
      bounceLight.intensity = 0.35;
      return;
    }
    const k = ENV_MULTIPLIER[envLevel];
    hemiLight.intensity = 0.18 * k;
    mainLight.intensity = 0.15 * k;
    fillLight.intensity = 0.12 * k;
    rimLight.intensity = 0.15 * k;
    bounceLight.intensity = 0.08 * k;
  };

  /** Re-fit the bulb's shadow frustum to wherever the bulb currently is.
   *  Depth precision is spread across [near, far], so an oversized far plane is exactly what let
   *  the old bias swallow the shade wall - and a far plane that is too SHORT silently cuts the
   *  projected hole pattern off partway down. Dragging the bulb away from its default spot
   *  therefore has to re-fit this, not just move the light. */
  function updateInternalLightShadowRange(): void {
    if (!assemblyGroup) return;
    const bbox = new THREE.Box3().setFromObject(assemblyGroup);
    if (bbox.isEmpty()) return;
    const size = new THREE.Vector3();
    bbox.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z, 1);
    // Farthest corner of the model from the bulb - that is what `far` must cover.
    const p = internalPointLight.position;
    const reach = Math.max(
      Math.abs(p.x - bbox.min.x), Math.abs(p.x - bbox.max.x),
      Math.abs(p.y - bbox.min.y), Math.abs(p.y - bbox.max.y),
      Math.abs(p.z - bbox.min.z), Math.abs(p.z - bbox.max.z),
    );
    internalPointLight.shadow.camera.near = 0.5;
    internalPointLight.shadow.camera.far = Math.max(maxDim * 2.5, reach * 1.6, 150);
    internalPointLight.shadow.camera.updateProjectionMatrix();
  }

  // Set once the user drags the bulb, so re-running syncInternalLightPosition() (on a module
  // load, or on the light toggle) does not teleport their bulb back to the default spot.
  let internalLightUserMoved = false;

  const syncInternalLightPosition = () => {
    if (assemblyGroup) {
      const bbox = new THREE.Box3().setFromObject(assemblyGroup);
      if (!bbox.isEmpty()) {
        const center = new THREE.Vector3();
        bbox.getCenter(center);
        // Position bulb inside upper chamber of lampshade (65% height of bounding box)
        const bulbZ = bbox.min.z + (bbox.max.z - bbox.min.z) * 0.65;
        center.z = bulbZ;
        if (!internalLightUserMoved) {
          internalPointLight.position.copy(center);
          internalLightBulbMesh.position.copy(center);
        }
        updateInternalLightShadowRange();
      }
    }
  };

  let lightSimulationOn = false;
  rightPanel.onLightBulbToggle((on: boolean) => {
    lightSimulationOn = on;
    isInternalLightOn = on;

    // Toggling the light off while its gizmo is on screen would leave a movable handle
    // floating over a light that is no longer there. (The bulb sphere's own visibility is
    // already synced a few lines below, next to internalPointLight.visible.)
    if (!on && moveTarget === 'light') hideMoveGizmo();

    // Toggle WebGL Shadow Map calculation (Strictly OFF when light toggle is OFF)
    renderer.shadowMap.enabled = on;

    // Studio lights DO cast shadows - removing them entirely makes the model look flat and
    // fake. They are set to act like soft studio fill: a low shadow.intensity (how much the
    // shadow darkens) plus a wide radius, so they only suggest form. The lamp keeps a full
    // strength, crisp shadow because its hole pattern is the thing being shown.
    mainLight.castShadow = on;
    fillLight.castShadow = on;
    rimLight.castShadow = on;
    // LightShadow.intensity exists at runtime (three r186: `this.intensity = 1`) but is missing
    // from the bundled type definitions, hence the casts.
    (mainLight.shadow as any).intensity = 0.35;
    (fillLight.shadow as any).intensity = 0.30;
    (rimLight.shadow as any).intensity = 0.30;
    internalPointLight.castShadow = on;
    (internalPointLight.shadow as any).intensity = 1.0;

    internalPointLight.visible = on;
    internalLightBulbMesh.visible = on;
    // Level + colour live in applyLightSettings so the two new control rows share one code path.
    applyLightSettings();

    // Secondary studio lights (Only active when light toggle is ON)
    rimLight.visible = on;
    bounceLight.visible = on;

    // 1. Grid & Table behavior. Keep the user's selected viewport background
    // when switching the lamp simulation on/off.
    gridHelper.visible = !on && (groundStyle === 'grid' || groundStyle === 'snapmaker');
    snapmakerBed.visible = !on && groundStyle === 'snapmaker';
    solidGround.visible = !on && groundStyle === 'solid';
    tablePlane.visible = on;

    // 2. Atmosphere & color balance
    scene.background = new THREE.Color(on ? 0x1e293b : 0xffffff);
    renderer.setClearColor(on ? 0x1e293b : 0xffffff, 1.0);

    hemiLight.color.setHex(on ? 0xf8fafc : 0xffffff);
    hemiLight.groundColor.setHex(on ? 0x475569 : 0x8d99ae);
    tablePlaneMat.color.setHex(0xffffff);

    // Studio lights are DIMMED hard while the lamp is on. With them at full strength the
    // interior point light was ~1% of the scene's light and changing its level changed nothing
    // visible. A lamp simulation should be lit by the lamp; the rest is only ambient fill so
    // the room does not go pitch black. envLevel scales that fill.
    applyStudioLighting();

    // Toggle mesh shadows & force material recompile so shaders NEVER retain old shadow map artifacts
    assemblyGroup?.traverse((obj) => {
      if ((obj as THREE.Mesh).isMesh) {
        obj.castShadow = on;
        obj.receiveShadow = on;
        const mat = (obj as THREE.Mesh).material;
        if (mat) {
          if (Array.isArray(mat)) mat.forEach(m => m.needsUpdate = true);
          else mat.needsUpdate = true;
        }
      }
    });

    syncInternalLightPosition();
    statusLog.setStatus(on ? `💡 Light ON • ${lightLevel.toUpperCase()} • ${lightMode}` : 'Default Daylight View • Grid Active', 'ok');
  });

  // Brightness level and colour mode. Both only matter while the light is on, so they
  // re-apply through the same helper rather than duplicating the light setup.
  rightPanel.onLightLevelChange((level) => {
    lightLevel = level;
    applyLightSettings();
    if (isInternalLightOn) statusLog.setStatus(`💡 Light level: ${level.toUpperCase()}`, 'ok');
  });

  rightPanel.onLightModeChange((mode) => {
    lightMode = mode;
    applyLightSettings();
    if (isInternalLightOn) statusLog.setStatus(`💡 Light colour: ${mode}`, 'ok');
  });

  rightPanel.onEnvLevelChange((level) => {
    envLevel = level;
    applyStudioLighting();
    if (isInternalLightOn) statusLog.setStatus(`💡 Studio / ambient light: ${level.toUpperCase()}`, 'ok');
  });

  // ============================================================================
  // QUICK VIEWPORT FLOATING GLASS TOOLBAR (UI/UX Enhancement: docked under right panel)
  // ============================================================================
  const quickToolbar = document.createElement('div');
  quickToolbar.className = 'quick-viewport-toolbar';
  // Styled to match the panel above it (same card surface, same corner radius) instead of the
  // old floating pill, so the right column reads as one family of controls.
  quickToolbar.style.cssText = `
    position: relative;
    pointer-events: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 4px 8px;
    height: 40px;
    box-sizing: border-box;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    z-index: 900;
    transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    flex-shrink: 0;
  `;

  // Zoom to Point tool state & functions
  let isZoomPointActive = false;

  function showZoomPointPulse(point: THREE.Vector3): void {
    const pulseGeo = new THREE.RingGeometry(0.5, 3.0, 32);
    const pulseMat = new THREE.MeshBasicMaterial({
      color: 0x3b82f6,
      transparent: true,
      opacity: 0.95,
      side: THREE.DoubleSide,
      depthTest: false
    });
    const pulseMesh = new THREE.Mesh(pulseGeo, pulseMat);
    pulseMesh.position.copy(point);
    pulseMesh.quaternion.copy(camera.quaternion);
    pulseMesh.renderOrder = 9999;
    scene.add(pulseMesh);

    const startTime = performance.now();
    const duration = 500;

    function animatePulse() {
      const elapsed = performance.now() - startTime;
      const t = Math.min(1, elapsed / duration);
      if (t >= 1) {
        scene.remove(pulseMesh);
        pulseGeo.dispose();
        pulseMat.dispose();
      } else {
        const scale = 1 + t * 2.2;
        pulseMesh.scale.set(scale, scale, scale);
        pulseMesh.quaternion.copy(camera.quaternion);
        pulseMat.opacity = (1 - t) * 0.95;
        requestAnimationFrame(animatePulse);
      }
    }
    requestAnimationFrame(animatePulse);
  }

  function zoomToPoint(hitPoint: THREE.Vector3): void {
    let modelDiagonal = 120;
    if (assemblyGroup) {
      const box = new THREE.Box3().setFromObject(assemblyGroup);
      const size = new THREE.Vector3();
      box.getSize(size);
      modelDiagonal = Math.max(size.length(), 20);
    }

    let dir = new THREE.Vector3().subVectors(camera.position, controls.target).normalize();
    if (dir.lengthSq() < 1e-6) {
      dir = new THREE.Vector3().subVectors(camera.position, hitPoint).normalize();
    }
    if (dir.lengthSq() < 1e-6) {
      dir = new THREE.Vector3(0, 0, 1);
    }

    const currentDist = new THREE.Vector3().subVectors(camera.position, hitPoint).length();
    const baseCloseDist = Math.max(22, Math.min(modelDiagonal * 0.22, 55));
    const closeDist = currentDist < baseCloseDist ? Math.max(12, currentDist * 0.5) : baseCloseDist;

    const targetPos = hitPoint.clone().addScaledVector(dir, closeDist);
    const targetLook = hitPoint.clone();
    const targetUp = camera.up.clone();

    showZoomPointPulse(hitPoint);
    startCameraTransition(targetPos, targetUp, targetLook, 380, controls.target.clone());

    statusLog.setStatus(
      `🔍 Zoomed to point (${hitPoint.x.toFixed(1)}, ${hitPoint.y.toFixed(1)}, ${hitPoint.z.toFixed(1)})mm`,
      'ok'
    );
  }

  function setZoomPointMode(active: boolean): void {
    isZoomPointActive = active;
    const btnEl = quickToolbar.querySelector('[data-tool-id="zoomPoint"]') as HTMLElement;
    if (btnEl) {
      btnEl.style.background = isZoomPointActive ? '#eff6ff' : 'transparent';
      btnEl.style.color = isZoomPointActive ? '#2563eb' : '#334155';
      btnEl.style.borderColor = isZoomPointActive ? '#bfdbfe' : 'transparent';
    }
    renderer.domElement.style.cursor = isZoomPointActive ? 'crosshair' : 'default';
    if (isZoomPointActive) {
      statusLog.setStatus('🔍 Zoom to Point active: Click any location on model to zoom in close (Esc to exit)', 'info');
    } else {
      statusLog.setStatus('Zoom to Point OFF', 'info');
    }
  }

  interface QuickToolBtn {
    id: string;
    icon: string;
    label: string;
    onClick: () => void;
  }

  const quickButtons: QuickToolBtn[] = [
    {
      id: 'resetCam',
      icon: UI_ICONS.target,
      label: 'Fit View (Home)',
      onClick: () => {
        zoomAllToMesh();
        statusLog.setStatus('Camera reset to fit model', 'ok');
      }
    },
    {
      id: 'zoomPoint',
      icon: UI_ICONS.zoomPoint,
      label: 'Zoom to Point (Z)',
      onClick: () => {
        setZoomPointMode(!isZoomPointActive);
      }
    },
    {
      id: 'dimensions',
      icon: UI_ICONS.dimensions,
      label: 'Toggle architectural dimensions (selection / full model)',
      onClick: () => {
        architecturalDimensionsOn = !architecturalDimensionsOn;
        dimensionsOverlay.setVisible(architecturalDimensionsOn);
        const btnEl = quickToolbar.querySelector('[data-tool-id="dimensions"]') as HTMLElement;
        if (btnEl) {
          btnEl.style.background = architecturalDimensionsOn ? '#eff6ff' : 'transparent';
          btnEl.style.color = architecturalDimensionsOn ? '#2563eb' : '#334155';
          btnEl.style.borderColor = architecturalDimensionsOn ? '#bfdbfe' : 'transparent';
        }
        if (architecturalDimensionsOn) updateArchitecturalDimensions();
      }
    },
    {
      id: 'turntable',
      icon: UI_ICONS.turntable,
      label: '360° Turntable',
      onClick: () => {
        autoRotate = !autoRotate;
        controls.autoRotate = autoRotate;
        const btnEl = quickToolbar.querySelector('[data-tool-id="turntable"]') as HTMLElement;
        if (btnEl) {
          btnEl.style.background = autoRotate ? '#eff6ff' : 'transparent';
          btnEl.style.color = autoRotate ? '#2563eb' : '#334155';
          btnEl.style.borderColor = autoRotate ? '#bfdbfe' : 'transparent';
        }
        statusLog.setStatus(autoRotate ? '360° Turntable ON' : '360° Turntable OFF', 'ok');
      }
    },
    {
      id: 'recordVideo',
      icon: UI_ICONS.videoCam,
      label: 'Record 360° Studio Video (60 FPS PBR)',
      onClick: () => {
        const canvas = renderer.domElement;
        if (!canvas) return;

        const prevAutoRotate = controls.autoRotate;
        const prevAutoRotateSpeed = controls.autoRotateSpeed;

        controls.autoRotate = true;
        controls.autoRotateSpeed = 2.5; // Exactly match smooth turntable speed

        const btnEl = quickToolbar.querySelector('[data-tool-id="recordVideo"]') as HTMLElement;
        if (btnEl) {
          btnEl.style.background = '#fef2f2';
          btnEl.style.color = '#dc2626';
          btnEl.style.borderColor = '#fca5a5';
        }

        const durationMs = 24000; // 24 seconds for 1 complete 360° rotation
        const stream = canvas.captureStream(60); // Pure WebGL 60 FPS Canvas capture

        let mimeType = 'video/webm;codecs=vp9';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm;codecs=vp8';
        }
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
        }

        let mediaRecorder: MediaRecorder;
        try {
          mediaRecorder = new MediaRecorder(stream, {
            mimeType,
            videoBitsPerSecond: 16000000 // Crisp 16 Mbps WebGL PBR video
          });
        } catch (e) {
          mediaRecorder = new MediaRecorder(stream);
        }

        const chunks: Blob[] = [];
        mediaRecorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) chunks.push(e.data);
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: mediaRecorder.mimeType || 'video/webm' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const ext = mediaRecorder.mimeType.includes('mp4') ? 'mp4' : 'webm';
          const lightState = isInternalLightOn ? '_LightON' : '_LightOFF';
          a.download = `${module.id}_360_pbr_studio${lightState}_${new Date().toISOString().slice(0, 10)}.${ext}`;
          a.click();
          URL.revokeObjectURL(url);

          controls.autoRotate = prevAutoRotate;
          controls.autoRotateSpeed = prevAutoRotateSpeed;

          if (btnEl) {
            btnEl.style.background = 'transparent';
            btnEl.style.color = '#334155';
            btnEl.style.borderColor = 'transparent';
          }

          statusLog.setStatus('✅ 360° Studio PBR Video exported to Downloads!', 'ok');
        };

        const startTime = performance.now();
        mediaRecorder.start();

        const updateProgress = () => {
          const elapsed = performance.now() - startTime;
          const pct = Math.min(100, Math.round((elapsed / durationMs) * 100));
          statusLog.setStatus(`🎥 Recording 360° PBR Video: ${pct}%`, 'info');

          if (elapsed < durationMs) {
            requestAnimationFrame(updateProgress);
          } else {
            if (mediaRecorder && mediaRecorder.state === 'recording') {
              mediaRecorder.stop();
            }
          }
        };

        requestAnimationFrame(updateProgress);
      }
    },
    {
      id: 'snapshot',
      icon: UI_ICONS.camera,
      label: 'Snapshot HD PNG',
      onClick: () => {
        try {
          renderer.render(scene, camera);
          const dataUrl = renderer.domElement.toDataURL('image/png');
          const a = document.createElement('a');
          a.href = dataUrl;
          a.download = `${module.id}_snapshot_${new Date().toISOString().slice(0, 10)}.png`;
          a.click();
          statusLog.setStatus('✓ HD Viewport Snapshot saved to Downloads!', 'ok');
        } catch (e: any) {
          statusLog.setStatus(`Snapshot failed: ${e.message}`, 'error');
        }
      }
    },
    {
      id: 'crop',
      icon: UI_ICONS.crop,
      label: 'Crop & Copy / Save',
      onClick: () => startCropMode()
    },
    {
      id: 'fullscreen',
      icon: UI_ICONS.fullscreen,
      label: 'Fullscreen View',
      onClick: () => {
        if (!document.fullscreenElement) {
          const docEl = document.documentElement;
          if (docEl.requestFullscreen) {
            docEl.requestFullscreen().catch((err) => {
              statusLog.setStatus(`Fullscreen error: ${err.message}`, 'error');
            });
          }
        } else {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          }
        }
      }
    }
  ];

  quickButtons.forEach((b) => {
    const btn = document.createElement('button');
    btn.dataset.toolId = b.id;
    btn.title = b.label;
    btn.innerHTML = b.icon;
    btn.style.cssText = `
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      line-height: 1;
      border-radius: 50%;
      border: 1px solid transparent;
      background: transparent;
      color: #334155;
      cursor: pointer;
      transition: all 0.15s ease;
      flex-shrink: 0;
    `;
    if (b.id === 'dimensions' && architecturalDimensionsOn) {
      btn.style.background = '#eff6ff';
      btn.style.color = '#2563eb';
      btn.style.borderColor = '#bfdbfe';
    }
    btn.addEventListener('mouseenter', () => {
      const isTurntableActive = b.id === 'turntable' && autoRotate;
      const isZoomActive = b.id === 'zoomPoint' && isZoomPointActive;
      const isDimensionsActive = b.id === 'dimensions' && architecturalDimensionsOn;
      if (!isTurntableActive && !isZoomActive && !isDimensionsActive) {
        btn.style.background = '#f1f5f9';
      }
      btn.style.transform = 'scale(1.08)';
    });
    btn.addEventListener('mouseleave', () => {
      const isTurntableActive = b.id === 'turntable' && autoRotate;
      const isZoomActive = b.id === 'zoomPoint' && isZoomPointActive;
      const isDimensionsActive = b.id === 'dimensions' && architecturalDimensionsOn;
      if (!isTurntableActive && !isZoomActive && !isDimensionsActive) {
        btn.style.background = 'transparent';
      }
      btn.style.transform = 'scale(1.0)';
    });
    btn.addEventListener('click', b.onClick);
    quickToolbar.appendChild(btn);
  });

  // Track fullscreen state to toggle icon/tooltip & update canvas resize
  document.addEventListener('fullscreenchange', () => {
    const fsBtn = quickToolbar.querySelector('[data-tool-id="fullscreen"]') as HTMLElement;
    if (fsBtn) {
      const isFs = !!document.fullscreenElement;
      fsBtn.innerHTML = isFs ? UI_ICONS.exitFullscreen : UI_ICONS.fullscreen;
      fsBtn.title = isFs ? 'Exit Fullscreen' : 'Fullscreen View';
    }
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  });

  // Place quickToolbar permanently at the TOP of rightOverlayCol so it is always
  // fully visible and clickable, never pushed off-screen when rightPanel cards expand (e.g. 3D Section View).
  rightOverlayCol.insertBefore(quickToolbar, rightOverlayCol.firstChild);

  // ---- TASK 28 (2026-09-30): the single floating Update button ----
  //
  // Replaces the per-slider Update buttons. Manual mode now means "adjust several sliders, then
  // apply once" - one request for all pending changes instead of one per slider.
  //
  // Three states, and the middle one is the important one:
  //   hidden     - nothing pending
  //   visible    - changes waiting, with a count badge so the user knows how many
  //   running    - the preview is in flight (Cancel lives on the progress bar, TASK 17)
  //
  // The "unapplied changes" warning is NOT optional polish: while values are pending the
  // viewport still shows the OLD model, so without a clear signal the user would believe their
  // edit had already taken effect. A badge alone is too easy to miss, so a text line is shown
  // ---- Floating Update Button & Notice Container (Task 28 & User UI Request) ----
  // Appears when in Manual mode and pending changes > 0.
  // Positioned at the bottom-left of the viewport, with its left edge aligned with
  // leftOverlayCol / modelHealthLeds (left: 12px), sitting at the bottom of the screen.
  const updateActionBox = document.createElement('div');
  updateActionBox.id = 'update-action-box';
  // No align-items:flex-start: the default `stretch` makes the notice strip and the Update
  // button the SAME width (the wider of the two wins), matching the left overlay column.
  // With flex-start each child shrank to its own text and the two looked mismatched.
  updateActionBox.style.cssText = [
    'display:none',
    'position:absolute',
    'left:12px',
    'bottom:16px',
    'flex-direction:column',
    'gap:6px',
    'z-index:60',
    'pointer-events:none',
  ].join(';');

  const dirtyNotice = document.createElement('div');
  dirtyNotice.id = 'dirty-notice';
  dirtyNotice.style.cssText = [
    'display:none',
    'box-sizing:border-box',
    'align-self:stretch',
    'text-align:center',
    'padding:6px 12px',
    'border-radius:6px',
    'background:#fef3c7',
    'border:1px solid #fcd34d',
    'color:#92400e',
    'font-size:11px',
    'font-weight:600',
    'pointer-events:none',
    'box-shadow:0 2px 8px rgba(0,0,0,0.12)',
    'white-space:nowrap',
    'line-height:1.4',
  ].join(';');
  dirtyNotice.textContent = '● Changes not applied — press Update';

  const updateNowBtn = document.createElement('button');
  updateNowBtn.type = 'button';
  updateNowBtn.id = 'update-now-btn';
  updateNowBtn.style.cssText = [
    'display:none',
    'box-sizing:border-box',
    'align-self:stretch',
    'justify-content:center',
    'align-items:center',
    'gap:8px',
    'padding:9px 18px',
    'border-radius:8px',
    'border:1px solid #1a1a1a',
    'background:#1a1a1a',
    'color:#ffffff',
    'font-size:13px',
    'font-weight:600',
    'font-family:inherit',
    'cursor:pointer',
    'pointer-events:auto',
    'box-shadow:0 4px 14px rgba(0,0,0,0.26)',
    'transition:background 0.15s ease, transform 0.1s ease',
  ].join(';');

  updateNowBtn.addEventListener('mouseenter', () => { updateNowBtn.style.background = '#2d2d2d'; });
  updateNowBtn.addEventListener('mouseleave', () => { updateNowBtn.style.background = '#1a1a1a'; });

  const updateBadge = document.createElement('span');
  updateBadge.style.cssText = [
    'display:none',
    'min-width:18px',
    'height:18px',
    'padding:0 5px',
    'border-radius:9px',
    'background:#dc2626',
    'color:#fff',
    'font-size:10px',
    'font-weight:700',
    'line-height:18px',
    'text-align:center',
  ].join(';');
  updateNowBtn.appendChild(document.createTextNode('✓ Update model'));
  updateNowBtn.appendChild(updateBadge);

  updateNowBtn.addEventListener('click', () => {
    if (pendingKeys.size === 0) return;
    // One request for every pending change. clearDirty() runs first so the button hides
    // immediately and a double-click cannot queue a second preview.
    clearDirty();
    updatePreview();
  });

  updateActionBox.appendChild(dirtyNotice);
  updateActionBox.appendChild(updateNowBtn);
  viewerEl.appendChild(updateActionBox);

  /** TASK 28: reflect `pendingKeys` in the button and the warning line. */
  function refreshUpdateButton(): void {
    const n = pendingKeys.size;
    const manual = !getGlobalSliderMode();
    if (n > 0 && manual) {
      updateActionBox.style.display = 'flex';
      updateNowBtn.style.display = 'inline-flex';
      updateBadge.style.display = 'inline-block';
      updateBadge.textContent = String(n);
      dirtyNotice.style.display = 'block';
    } else {
      updateActionBox.style.display = 'none';
      updateNowBtn.style.display = 'none';
      updateBadge.style.display = 'none';
      dirtyNotice.style.display = 'none';
    }
  }

  // Switching back to Real-time should apply whatever was pending, otherwise the user would
  // flip the toggle, see no preview, and be left with values that never reached the server.
  onSliderModeChange(() => {
    if (getGlobalSliderMode() && pendingKeys.size > 0) {
      clearDirty();
      updatePreview();
    } else {
      refreshUpdateButton();
    }
  });

  // ---- UNDO / REDO & CUSTOM PRESETS ENGINE ----
  interface StateSnapshot {
    params: Record<string, any>;
    colors: Record<string, string>;
  }

  const undoStack: StateSnapshot[] = [];
  const redoStack: StateSnapshot[] = [];

  function pushState() {
    undoStack.push({
      params: JSON.parse(JSON.stringify(currentParams)),
      colors: JSON.parse(JSON.stringify(partColors)),
    });
    if (undoStack.length > 50) undoStack.shift();
    redoStack.length = 0; // clear redo
  }

  function applySnapshot(snap: StateSnapshot) {
    Object.assign(currentParams, snap.params);
    Object.assign(partColors, snap.colors);
    persistParams();  // TASK 19: undo/redo/preset-load are param changes too
    
    // Sync slider inputs
    sidebar.root.querySelectorAll<HTMLInputElement>('input[type="range"], input[type="number"]').forEach((input) => {
      const wrap = input.closest('.control-group') as HTMLElement;
      const key = wrap?.dataset.sliderKey;
      if (key && currentParams[key] !== undefined) {
        input.value = String(currentParams[key]);
      }
    });

    updatePreview();
  }

  const undoBtn = (sidebar.root as any)._undoBtn || document.getElementById('undo-btn');
  const redoBtn = (sidebar.root as any)._redoBtn || document.getElementById('redo-btn');
  const presetSelect = (sidebar.root as any)._presetSelect || document.getElementById('preset-select');
  const loadPresetBtn = (sidebar.root as any)._loadPresetBtn || document.getElementById('load-preset-btn');
  const customNameInput = (sidebar.root as any)._customNameInput || document.getElementById('custom-name-input');
  const saveCustomBtn = (sidebar.root as any)._saveCustomBtn || document.getElementById('save-custom-btn');

  if (undoBtn) {
    undoBtn.addEventListener('click', () => {
      if (undoStack.length === 0) {
        statusLog.setStatus('Nothing to undo!', 'warning');
        return;
      }
      const currentSnap = {
        params: JSON.parse(JSON.stringify(currentParams)),
        colors: JSON.parse(JSON.stringify(partColors)),
      };
      redoStack.push(currentSnap);
      const prevSnap = undoStack.pop()!;
      applySnapshot(prevSnap);
      statusLog.setStatus('✓ Undo applied', 'info');
    });
  }

  if (redoBtn) {
    redoBtn.addEventListener('click', () => {
      if (redoStack.length === 0) {
        statusLog.setStatus('Nothing to redo!', 'warning');
        return;
      }
      const currentSnap = {
        params: JSON.parse(JSON.stringify(currentParams)),
        colors: JSON.parse(JSON.stringify(partColors)),
      };
      undoStack.push(currentSnap);
      const nextSnap = redoStack.pop()!;
      applySnapshot(nextSnap);
      statusLog.setStatus('✓ Redo applied', 'info');
    });
  }

  // LocalStorage Custom Presets Management
  const STORAGE_KEY = `boxed3d_custom_presets_${module.id}`;

  function getStoredPresetsMap(): Record<string, StateSnapshot> {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }

  function updateSaveBtnState() {
    if (!saveCustomBtn) return;
    const inputName = customNameInput?.value.trim() || '';
    const selectedPreset = presetSelect?.value || '';
    const presetsMap = getStoredPresetsMap();

    // Pick preset name to act on: prefer text in input, fall back to selected dropdown item
    const targetName = inputName || selectedPreset;

    if (targetName && presetsMap[targetName]) {
      saveCustomBtn.innerHTML = '🔄';
      saveCustomBtn.title = `Update preset "${targetName}"`;
      saveCustomBtn.style.background = '#2563eb'; // Blue for Update
      saveCustomBtn.style.borderColor = '#2563eb';
    } else {
      saveCustomBtn.innerHTML = '💾+';
      saveCustomBtn.title = targetName ? `Save new preset "${targetName}"` : 'Save new preset';
      saveCustomBtn.style.background = '#16a34a'; // Green for Save New
      saveCustomBtn.style.borderColor = '#16a34a';
    }
  }

  function loadSavedPresetsList() {
    if (!presetSelect) return;
    presetSelect.innerHTML = '';
    const defaultOpt = document.createElement('option');
    defaultOpt.value = '';
    defaultOpt.textContent = '-- Select saved preset --';
    presetSelect.appendChild(defaultOpt);

    const presets = getStoredPresetsMap();
    for (const name of Object.keys(presets)) {
      const opt = document.createElement('option');
      opt.value = name;
      opt.textContent = name;
      presetSelect.appendChild(opt);
    }
    updateSaveBtnState();
  }

  loadSavedPresetsList();

  if (presetSelect) {
    presetSelect.addEventListener('change', () => {
      // When user picks a preset from dropdown, auto-fill its name into the input
      if (customNameInput && presetSelect.value) {
        customNameInput.value = presetSelect.value;
      }
      updateSaveBtnState();
    });
  }

  if (customNameInput) {
    customNameInput.addEventListener('input', () => {
      updateSaveBtnState();
    });
  }

  if (saveCustomBtn) {
    saveCustomBtn.addEventListener('click', () => {
      const selectedPreset = presetSelect?.value || '';
      const inputName = customNameInput?.value.trim() || '';
      const name = inputName || selectedPreset || `Custom Preset ${new Date().toLocaleTimeString()}`;
      
      try {
        const presets = getStoredPresetsMap();
        const isUpdate = Boolean(presets[name]);

        presets[name] = {
          params: JSON.parse(JSON.stringify(currentParams)),
          colors: JSON.parse(JSON.stringify(partColors)),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
        loadSavedPresetsList();
        if (presetSelect) presetSelect.value = name;
        if (customNameInput) customNameInput.value = name;
        updateSaveBtnState();

        if (isUpdate) {
          statusLog.setStatus(`✓ Updated preset "${name}" in LocalStorage!`, 'ok');
        } else {
          statusLog.setStatus(`✓ Saved new preset "${name}" to LocalStorage!`, 'ok');
        }
      } catch (e: any) {
        statusLog.setStatus(`Error saving preset: ${e.message}`, 'error');
      }
    });
  }

  if (loadPresetBtn) {
    loadPresetBtn.addEventListener('click', () => {
      const selectedName = presetSelect?.value;
      if (!selectedName) {
        statusLog.setStatus('Please select a saved preset to load!', 'warning');
        return;
      }
      try {
        const presets = getStoredPresetsMap();
        const snap = presets[selectedName];
        if (snap) {
          pushState();
          applySnapshot(snap);
          if (customNameInput) customNameInput.value = selectedName;
          updateSaveBtnState();
          statusLog.setStatus(`✓ Loaded custom preset "${selectedName}"`, 'ok');
        }
      } catch (e: any) {
        statusLog.setStatus(`Error loading preset: ${e.message}`, 'error');
      }
    });
  }

  // TASK 03 (2026-09-29) - REMOVED A DUPLICATE SLIDER HOOK.
  //
  // There used to be a second `querySelectorAll('input[type="range"]')` block here that
  // did exactly what the hook earlier in this function already does - except it called
  // updatePreview() DIRECTLY, with no debounce at all:
  //
  //     input.addEventListener('input', () => {
  //       currentParams[key] = parseFloat(input.value);
  //       if (key !== 'showAssembled') {
  //         updatePreview();          // <-- one request per input event
  //       }
  //     });
  //
  // Both blocks matched the same elements (renderSidebar runs once and the sidebar is
  // never re-rendered), so every `input` event ran both handlers and the immediate call
  // always won - the debounce above could never coalesce anything. Dragging a slider for
  // one second therefore issued roughly one request per input event instead of one per
  // pause, which is what put N workers on the server for every single drag.
  //
  // Undo tracking was identical in both blocks, so removing this one loses nothing:
  // the surviving hook still does pushState() on focus and debounces on input.

  // 1. Export by View (WYSIWYG: what you see is what you export)
  const exportViewBtn = document.getElementById('exportViewBtn');
  if (exportViewBtn) {
    exportViewBtn.addEventListener('click', async () => {
      if (!assemblyGroup) return;
      await updatePreview('high');
      const visibleMeshes: THREE.Mesh[] = [];
      assemblyGroup.traverse((child) => {
        if (child instanceof THREE.Mesh && child.visible && child !== supportOverlay && !child.userData?.isSupport) {
          visibleMeshes.push(child);
        }
      });
      if (visibleMeshes.length === 0) {
        statusLog.setStatus('⚠ No visible parts to export!', 'error');
        return;
      }
      const fmt = getExportFormat();
      statusLog.setStatus(`Exporting ${fmt.toUpperCase()} (${visibleMeshes.length} visible parts)...`, 'info');
      try {
        if (fmt === '3mf') {
          // TASK 24: await so the try/catch actually covers it, and show progress - this can
          // take many seconds on a heavy model and the user deserves to see it is working.
          statusLog.setStatus(`Exporting 3MF...`, 'info');
          await exportMeshesTo3MF(visibleMeshes, `${module.id}_WYSIWYG_View.3mf`, {
            partColors,
            onProgress: (done, total) => statusLog.setStatus(`Exporting 3MF ${done}/${total} parts...`, 'info'),
          });
          statusLog.setStatus(`✓ Exported 3MF view selection (${visibleMeshes.length} parts with Bambu colors)`, 'ok');
        } else {
          exportMeshesToBinarySTL(visibleMeshes, `${module.id}_WYSIWYG_View.stl`);
          statusLog.setStatus(`✓ Exported STL view selection (${visibleMeshes.length} parts)`, 'ok');
        }
      } catch (err: any) {
        statusLog.setStatus(`Export error: ${err?.message || err}`, 'error');
      }
    });
  }

  // 2. Export Each Object Separately (3D Print Layout)
  const exportObjectsBtn = document.getElementById('exportObjectsBtn');
  if (exportObjectsBtn) {
    exportObjectsBtn.addEventListener('click', async () => {
      if (!assemblyGroup) return;
      await updatePreview('high');
      const fmt = getExportFormat();
      const objects = module.objects;
      if (!objects || objects.length === 0) {
        const allMeshes: THREE.Mesh[] = [];
        assemblyGroup.traverse((c) => { if (c instanceof THREE.Mesh && c !== supportOverlay && !c.userData?.isSupport) allMeshes.push(c); });
        try {
          if (fmt === '3mf') {
            statusLog.setStatus(`Exporting 3MF...`, 'info');
            await exportMeshesTo3MF(allMeshes, `${module.id}_Full_Assembly.3mf`, {
              partColors,
              onProgress: (done, total) => statusLog.setStatus(`Exporting 3MF ${done}/${total} parts...`, 'info'),
            });
            statusLog.setStatus(`✓ Exported 3MF Full Assembly file!`, 'ok');
          } else {
            exportMeshesToBinarySTL(allMeshes, `${module.id}_Full_Assembly.stl`);
            statusLog.setStatus(`✓ Exported STL Full Assembly file!`, 'ok');
          }
        } catch (err: any) {
          statusLog.setStatus(`Export error: ${err?.message || err}`, 'error');
        }
        return;
      }

      statusLog.setStatus(`Exporting ${objects.length} separate Objects (${fmt.toUpperCase()}) for 3D layout...`, 'info');
      let exportedCount = 0;
      // TASK 24 + TASK 26 (2026-09-30): await each file, report progress, and give the browser
      // a moment between downloads.
      //   TASK 24 - the 3MF path used to run synchronously with no try/catch, so a heavy model
      //     froze the tab and an error vanished silently.
      //   TASK 26 - firing many `a.click()` downloads back to back makes browsers show a
      //     "download multiple files?" prompt and the user cannot tell which file is in flight.
      //     A short pause plus "3/8" progress makes the sequence legible.
      try {
        for (let i = 0; i < objects.length; i++) {
          const obj = objects[i];
          if (!obj) continue;
          const gids = new Set(obj.parts.map(p => p.groupId));
          const objMeshes: THREE.Mesh[] = [];
          assemblyGroup.traverse((c) => {
            if (c instanceof THREE.Mesh && c.userData?.groupId !== undefined && gids.has(c.userData.groupId)) {
              objMeshes.push(c);
            }
          });
          if (objMeshes.length > 0) {
            const safeName = (obj.id ?? obj.name ?? `object_${i + 1}`).replace(/[^a-zA-Z0-9_-]/g, '_');
            statusLog.setStatus(`Exporting object ${i + 1}/${objects.length} (${fmt.toUpperCase()})...`, 'info');
            if (fmt === '3mf') {
              await exportMeshesTo3MF(objMeshes, `${module.id}_Object${i + 1}_${safeName}.3mf`, { partColors });
            } else {
              exportMeshesToBinarySTL(objMeshes, `${module.id}_Object${i + 1}_${safeName}.stl`);
            }
            exportedCount++;
            // Let the previous download start before triggering the next one.
            await new Promise(r => setTimeout(r, 120));
          }
        }
        statusLog.setStatus(`✓ Exported ${exportedCount} separate ${fmt.toUpperCase()} files!`, 'ok');
      } catch (err: any) {
        statusLog.setStatus(`Export error after ${exportedCount} file(s): ${err?.message || err}`, 'error');
      }
    });
  }

  // 3. Export ONLY the current selection. The other two export buttons cover "everything
  // visible" and "every object"; neither can produce one part on its own, which is what is
  // wanted when a single piece needs reprinting. Selection comes from the scene tree, which the
  // 3D view and the tree both drive (click a part, or Shift-click for several).
  const exportSelectedBtn = document.getElementById('exportSelectedBtn');
  if (exportSelectedBtn) {
    exportSelectedBtn.addEventListener('click', async () => {
      if (!assemblyGroup) return;
      await updatePreview('high');
      const selectedGids = sceneTree ? sceneTree.getSelectedGroupIds() : new Set<number>();
      const selMeshes: THREE.Mesh[] = [];
      assemblyGroup.traverse((c) => {
        if (c instanceof THREE.Mesh && !c.userData?.isSupport && c !== supportOverlay) {
          const gid = c.userData?.groupId;
          if (gid !== undefined && selectedGids.has(gid)) selMeshes.push(c);
        }
      });
      if (selMeshes.length === 0) {
        statusLog.setStatus('⚠ Nothing selected - click a part in the 3D view or scene tree first', 'error');
        return;
      }
      const fmt = getExportFormat();
      // Name the file after what is selected, so a folder of exports is readable.
      const names = [...new Set(selMeshes.map(m => m.userData?.partName || m.userData?.partCode || `group_${m.userData?.groupId}`))]
        .filter(Boolean) as string[];
      const suffix = names.length === 1 ? (names[0] ?? 'selected').replace(/[^a-zA-Z0-9_-]/g, '_') : `${names.length}_parts`;
      statusLog.setStatus(`Exporting ${fmt.toUpperCase()} of ${selMeshes.length} selected part(s)...`, 'info');
      try {
        if (fmt === '3mf') {
          await exportMeshesTo3MF(selMeshes, `${module.id}_${suffix}.3mf`, {
            partColors,
            onProgress: (done, total) => statusLog.setStatus(`Exporting 3MF ${done}/${total} parts...`, 'info'),
          });
          statusLog.setStatus(`✓ Exported 3MF: ${names.join(', ') || suffix}`, 'ok');
        } else {
          exportMeshesToBinarySTL(selMeshes, `${module.id}_${suffix}.stl`);
          statusLog.setStatus(`✓ Exported STL: ${names.join(', ') || suffix}`, 'ok');
        }
      } catch (err: any) {
        statusLog.setStatus(`Export error: ${err?.message || err}`, 'error');
      }
    });
  }

  // Part-Level Geometry Cache (Part-Level Incremental Mesh Generation & Caching System)
  const previewController = new PreviewController<MeshData['triangles'][number], MeshData>();
  let lastEvaluatedParams: Record<string, any> = {};
  let lastPreviewQuality: AppQuality | null = null;

  /**
   * Automatically determines which groupIds need re-evaluation based on changed parameters
   * and the targetGroupIds mappings defined in module schema (ui-config.json).
   */
  function determineTargetGroupsToReevaluate(): number[] | 'all' {
    if (Object.keys(lastEvaluatedParams).length === 0) {
      // Do not generate hidden comparison objects on the first preview. Some
      // core modules intentionally expose several heavy sample forms, while
      // only a subset is visible by default. Generate the visible groups first;
      // hidden groups can still be loaded later when explicitly requested.
      const initialVisibleGroups = new Set<number>();
      for (const obj of module.objects || []) {
        for (const part of obj.parts || []) {
          if (part.visible !== false) initialVisibleGroups.add(part.groupId);
        }
      }
      if (initialVisibleGroups.size > 0) return Array.from(initialVisibleGroups);
      return 'all';
    }

    const changedKeys = new Set<string>();
    for (const key of Object.keys(currentParams)) {
      if (currentParams[key] !== lastEvaluatedParams[key]) {
        changedKeys.add(key);
      }
    }

    if (changedKeys.size === 0) return [];

    const dirtyGroupIds = new Set<number>();
    let hasUnmappedChange = false;

    // Build key-to-targetGroupIds lookup map from sliders, selects and textParams
    const keyToGroupsMap = new Map<string, number[]>();
    if (module.sliders) {
      for (const s of module.sliders) {
        if (s.key && s.targetGroupIds && s.targetGroupIds.length > 0) {
          keyToGroupsMap.set(s.key, s.targetGroupIds);
        }
      }
    }
    if (module.selects) {
      for (const sel of module.selects) {
        if (sel.key && sel.targetGroupIds && sel.targetGroupIds.length > 0) {
          keyToGroupsMap.set(sel.key, sel.targetGroupIds);
        }
      }
    }
    if (module.textParams) {
      for (const text of module.textParams) {
        if (text.key && text.targetGroupIds && text.targetGroupIds.length > 0) {
          keyToGroupsMap.set(text.key, text.targetGroupIds);
        }
      }
    }

    for (const key of changedKeys) {
      const gids = keyToGroupsMap.get(key);
      if (gids && gids.length > 0) {
        for (const g of gids) dirtyGroupIds.add(g);
      } else {
        hasUnmappedChange = true;
      }
    }

    if (hasUnmappedChange || dirtyGroupIds.size === 0) return 'all';
    return Array.from(dirtyGroupIds);
  }

  let isFirstPreview = true;
  // Only the newest preview request may commit geometry. A slower response
  // from a previous reload/parameter change must never overwrite the current
  // red-logo mesh and make its stroke appear to grow/shrink intermittently.
  async function updatePreview(qualityOverride?: AppQuality): Promise<void> {
    const previewQuality = qualityOverride ?? getAppQuality();
    const generation = previewController.begin();
    statusLog.setStatus('Generating...', 'info');
    progressBar.start('Computing 3D model (C++ CSG)...');
    progressBar.stage(8, 'Preparing preview groups...');
    try {
      // Text geometry can change its full spatial transform (not only its
      // vertices inside one color group). Rebuild the complete assembly for
      // text-enabled modules so an old horizontal text mesh cannot remain in
      // the Three.js scene beside the new vertical one.
      const targetEval = qualityOverride || lastPreviewQuality !== previewQuality || (module.textParams && module.textParams.length > 0)
        ? 'all'
        : determineTargetGroupsToReevaluate();
      let fetchGroups: number[] = [];

      if (targetEval === 'all') {
        // Four full-density rattan cores exceed the JSON string limit when
        // requested as one assembly. Fetch their existing part groups one at
        // a time so the renderer receives the original core mesh unchanged.
        fetchGroups = module.id === 'rattan_pillow_table_lamp'
          ? [...new Set((module.objects ?? []).flatMap(object => object.parts.map(part => part.groupId)))]
          : [-1];
      } else if (Array.isArray(targetEval) && targetEval.length > 0) {
        fetchGroups = targetEval;
      }

      // Perform incremental updates per target groupId or full assembly
      const fetchedMeshResults: MeshData[] = [];
      if (fetchGroups.length > 0) {
        for (let index = 0; index < fetchGroups.length; index++) {
          const grpId = fetchGroups[index]!;
          const from = 12 + (index / fetchGroups.length) * 42;
          progressBar.stage(from, fetchGroups.length > 1
            ? `Generating geometry group ${index + 1}/${fetchGroups.length}...`
            : 'Generating geometry with C++ CSG...');
          const res = isSvgPreviewModule(module.id)
            ? await fetchSvgPreviewMesh(
              module.id,
              Number(currentParams.targetHeight ?? 80),
              Number(currentParams.extrusion ?? 4),
            )
            : await fetchPreview(module.id, currentParams, { group: grpId, quality: previewQuality });
          if (!previewController.isCurrent(generation)) return;
          if (res === null || res === undefined) {
            progressBar.cancel('Preview cancelled');
            statusLog.setStatus('Preview cancelled', 'info');
            return;
          }
          fetchedMeshResults.push(res);
          progressBar.stage(58 + ((index + 1) / fetchGroups.length) * 12,
            `Geometry received (${index + 1}/${fetchGroups.length})...`);
        }
      } else {
        progressBar.stage(60, 'Reusing cached geometry...');
      }

      // Do not merge or render a response that became stale while the worker
      // was generating it. This is especially important for the red logo,
      // whose revised inset must not be replaced by an older cached mesh.
      if (!previewController.isCurrent(generation)) return;

      progressBar.stage(72, 'Merging updated model parts...');

      previewController.merge(fetchedMeshResults);

      // Update last evaluated parameters
      lastEvaluatedParams = JSON.parse(JSON.stringify(currentParams));
      lastPreviewQuality = previewQuality;

      // Re-combine all cached part meshes into full meshData
      const meshData = previewController.combine();
      setAppQualityTriangleCount(previewQuality, meshData.triangleCount);

      if (!previewController.isCurrent(generation)) return;

      progressBar.stage(80, 'Building render meshes...');

      // Remove and dispose ONLY dirty/re-evaluated parts from the scene (In-place Mesh Update)
      // This guarantees unchanged parts (like the Shade) NEVER flicker, disappear or re-render!
      const gidsToUpdate = new Set<number>(fetchGroups.includes(-1) ? previewController.getGroupIds() : fetchGroups);
      
      previewRenderer.clearGroups(gidsToUpdate);
      if (supportOverlay) { scene.remove(supportOverlay); supportOverlay = null; }
      if (watertightEdges) {
        watertightEdges.traverse((child) => {
          if (!(child instanceof THREE.LineSegments)) return;
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        });
        watertightEdges.removeFromParent();
        watertightEdges = null;
      }

      const findPartSchema = (gid: number) => {
        const objs = module.objects || (module as any).schema?.objects;
        // Color configs are the authoritative source for generic/core modules.
        // Prefer them even when the object metadata exists, because many compact
        // module manifests only describe the object/part name and groupId.
        if (module.colorConfigs) {
          for (const cc of module.colorConfigs) {
            if (cc.targetGroupIds && cc.targetGroupIds.includes(gid)) {
              let partName = `Part_${gid}`;
              if (objs) {
                for (const obj of objs) {
                  for (const part of obj.parts) {
                    if (part.groupId === gid) {
                      partName = (part as any).name || (part as any).part_name || part.label || partName;
                    }
                  }
                }
              }
              return {
                colorKey: cc.key,
                defaultColor: cc.default || '#64748b',
                name: cc.label || partName
              };
            }
          }
        }
        if (objs) {
          for (const obj of objs) {
            for (const part of obj.parts) {
              if (part.groupId === gid) {
                return {
                  colorKey: part.colorKey || `group_${gid}`,
                  defaultColor: (part as any).color || part.defaultColor || (obj as any).color || '#64748b',
                  name: (part as any).name || (part as any).part_name || part.label || `Part_${gid}`
                };
              }
            }
          }
        }
        return { colorKey: `group_${gid}`, defaultColor: '#64748b', name: `Part_${gid}` };
      };

      let overhangCount = 0;

      // Render only the dirty/updated part groupIds in Three.js scene
      const uniqueGroupIds = Array.from(gidsToUpdate);
      
      for (let i = 0; i < uniqueGroupIds.length; i++) {
        if (!previewController.isCurrent(generation)) return;
        const gid = uniqueGroupIds[i]!;
        const grpMeshData = previewController.getGroup(gid) || meshData;
        const info = findPartSchema(gid);
        const pMesh = previewRenderer.createPartMesh(
          grpMeshData,
          gid,
          { colorKey: info.colorKey, color: partColors[info.colorKey] || info.defaultColor, name: info.name },
          isInternalLightOn,
        );
        if (pMesh) {
          const partGeo = pMesh.geometry;

          // Assign legacy references if present for backwards compatibility
          if (gid === 0) baseMesh = pMesh;
          else if (gid === 1) switchMesh = pMesh;
          else if (gid === 2) keycapMesh = pMesh;
          else if (gid === 3) letterMesh = pMesh;

          // Wireframe and support geometry are expensive on dense meshes. Build them
          // only when the corresponding inspection/view mode is actually enabled.
          if (viewModeNeedsWireframe(currentViewMode)) {
            addWireframeForMesh(pMesh);
          }
          if (isOverhangActive) {
            const sup = createPartSupportMesh(partGeo);
            if (sup.mesh) {
              (sup.mesh as any).userData = { groupId: gid, isSupport: true };
              assemblyGroup!.add(sup.mesh);
              overhangCount += sup.count;
            }
          }

          // Apply view modes & visibility immediately for this part
          applyVisibility();
          applyViewMode(currentViewMode);
          applyClippingPlanesToMaterials();

          // Yield to main thread for 1 frame if there are multiple parts for smooth progressive assembly animation
          if (uniqueGroupIds.length > 1 && i < uniqueGroupIds.length - 1) {
            statusLog.setStatus(`Building 3D Parts (${i + 1}/${uniqueGroupIds.length})...`, 'info');
            await new Promise(r => requestAnimationFrame(r));
            if (!previewController.isCurrent(generation)) return;
          }
        }
      }

      // The part geometry above is already the render geometry. Do not build a
      // second combined copy here: on dense rattan previews that duplicates
      // tens of megabytes and can trigger a browser RangeError.
      progressBar.stage(90, 'Uploading mesh to the 3D viewport...');

      // Update dimensions HUD for selection
      updateDimensionsHud();
      updateArchitecturalDimensions();

      latestMeshData = meshData;
      updatePrintEstimate();

      // Non-manifold edge geometry is also deferred until the user opens it.
      if (watertightEdgesRequested) buildWatertightEdges(meshData, true);

      // Internal light positioning
      syncInternalLightPosition();

      // LED updates
      // TASK 13 (2026-09-29): when the worker skipped the analysis (--no-analyze), the
      // isWatertight field is just the struct's default and does NOT describe the mesh.
      // Showing "watertight" then would be a false claim: lamp_023 really has 822
      // non-manifold edges but reports clean when the analysis is off. So an unavailable
      // analysis is reported as such instead of being silently treated as good news.
      const analysisSkipped = meshData.analysis?.skipped === true;
      const isWatertight = meshData.analysis?.isWatertight ?? true;
      const edgeCount = meshData.analysis?.nonManifoldEdgeCount ?? 0;
      if (analysisSkipped) {
        modelHealthLeds.setWatertightUnknown();
        statusLog.setStatus(`Ready • ${meshData.triangleCount}△ • analysis not run`, 'info');
        progressBar.finish(`✓ Ready • ${meshData.triangleCount}△ • watertight check skipped`);
      } else {
        modelHealthLeds.setWatertight(isWatertight, edgeCount);
        statusLog.setStatus(`Ready • ${meshData.triangleCount}△ • ${isWatertight ? '✓ Water' : `⚠ ${edgeCount}E`}`, 'ok');
        progressBar.finish(`✓ Ready • ${meshData.triangleCount}△ • ${isWatertight ? '✓ Watertight' : `⚠ ${edgeCount}E`}`);
      }
      if (isOverhangActive) modelHealthLeds.setSupport(overhangCount === 0, overhangCount);
      else modelHealthLeds.setSupportUnknown();

      // TASK 28: the model on screen now matches the parameters, so nothing is pending.
      clearDirty();

      applyThemeToScene();
      // No explode re-apply needed here: animate() runs applyUniversalCascadingExplode() every
      // frame, so freshly-built meshes pick up the current exploded/assembled Z on the next frame.
      updateSectionCutOutline();

      // Fit camera to complete 3D assembly on initial load
      if (isFirstPreview) {
        if (assemblyGroup) {
          assemblyGroup.updateMatrixWorld(true);
          fitCameraToObject(camera, controls, assemblyGroup, 1.75);
        }
        isFirstPreview = false;
      }
    } catch (err) {
      // TASK 18 (2026-09-30): say WHAT went wrong, not just that something did.
      // The server distinguishes these cases (busy / timed out / broken model / bad key) and
      // the old code collapsed them all into "API HTTP <status>", so a user seeing "429" had
      // no idea whether to wait or to change something.
      if (err instanceof ApiError) {
        const suffix = err.retryAfterSec !== null ? ` Try again in ~${err.retryAfterSec}s.` : '';
        const msg =
          err.kind === 'busy' ? `⚠ Server busy - too many requests right now.${suffix}` :
          err.kind === 'timeout' ? `⚠ This model is too heavy to render in time.${suffix}` :
          err.kind === 'auth' ? `⚠ API key rejected. Check VITE_API_KEY in apps/web/.env.local.` :
          err.kind === 'network' ? `⚠ Cannot reach the 3D server. Is the API running on port 38592?` :
          `⚠ Model generation failed: ${err.serverMessage}`;
        statusLog.setStatus(msg, err.isRetryable ? 'warning' : 'error');
        progressBar.error(msg);
      } else {
        statusLog.setStatus(`${err}`, 'error');
        progressBar.error(`${err}`);
      }
      console.error(err);
    }
  }

  let currentViewMode: ViewMode = 'shape';
  let latestMeshData: MeshData | null = null;

  function updatePrintEstimate(): void {
    if (!latestMeshData?.triangles?.length) {
      rightPanel.setPrintEstimate(null);
      return;
    }
    const selectedIds = sceneTree?.getSelectedGroupIds() ?? new Set<number>();
    const parts = (module.objects ?? module.assemblies ?? []).flatMap((object) => object.parts);
    const activeParts = selectedIds.size > 0
      ? parts.filter((part) => selectedIds.has(part.groupId))
      : parts;
    const colorCount = new Set(activeParts.map((part) => (
      (part.colorKey ? partColors[part.colorKey] : undefined) || part.defaultColor || '#64748b'
    ).toLowerCase())).size || 1;
    const estimate = estimatePrint(
      latestMeshData.triangles,
      selectedIds,
      { ...rightPanel.getPrintEstimateOptions(), colorCount },
    );
    if (!estimate) {
      rightPanel.setPrintEstimate(null);
      return;
    }
    rightPanel.setPrintEstimate(estimate);
  }
  let watertightEdgesRequested = false;

  const normalMaterial = new THREE.MeshNormalMaterial({ side: THREE.DoubleSide });

  const RENDER_MODE_NAMES: Record<ViewMode, string> = {
    shape: 'Solid (PBR Realistic Materials)',
    wireframe: 'Wireframe (Triangle Mesh)',
    overlay: 'Overlay (Solid + Edges)',
    xray: 'X-Ray (Internal Cavity View)',
    clay: 'Clay (Studio Curvature Inspection)',
    flat: 'Flat (Flat Shading Mesh Check)',
    normals: 'Normals (Surface Normal Vectors RGB)',
  };

  // Apply visibility state from SceneTreeViewer (eye icon) combined with View Mode
  function applyVisibility(): void {
    if (!assemblyGroup) return;
    assemblyGroup.traverse((child) => {
      // The red watertight markers keep their own visibility state.
      if (child.userData?.isWatertightEdge) return;
      const gid = child.userData?.groupId;
      const isVisible = (gid !== undefined && sceneTree) ? sceneTree.isPartVisible(gid) : true;

      if (child.userData?.isSupport) {
        child.visible = isVisible && isOverhangActive;
        return;
      }

      if (child instanceof THREE.Mesh) {
        child.visible = isVisible && (currentViewMode !== 'wireframe');
      } else if (child instanceof THREE.LineSegments) {
        child.visible = isVisible && (currentViewMode === 'wireframe' || currentViewMode === 'overlay' || currentViewMode === 'xray');
      }
    });
  }

  function applyViewMode(mode: ViewMode): void {
    currentViewMode = mode;
    if (viewModeNeedsWireframe(mode)) ensureWireframes();
    if (!assemblyGroup) return;

    assemblyGroup.traverse((child) => {
      if (child.userData?.isSupport || child.userData?.isWatertightEdge) return;

      if (child instanceof THREE.Mesh) {
        const defaultMat = (child.userData?.defaultMaterial as THREE.MeshPhongMaterial) || child.material;

        switch (mode) {
          case 'wireframe':
            // Wireframe: child.visible is hidden inside applyVisibility()
            break;

          case 'shape':
            child.material = defaultMat;
            defaultMat.transparent = false;
            defaultMat.opacity = 1.0;
            defaultMat.depthWrite = true;
            defaultMat.flatShading = false;
            defaultMat.needsUpdate = true;
            break;

          case 'overlay':
            child.material = defaultMat;
            defaultMat.transparent = false;
            defaultMat.opacity = 1.0;
            defaultMat.depthWrite = true;
            defaultMat.flatShading = false;
            defaultMat.needsUpdate = true;
            break;

          case 'xray':
            child.material = defaultMat;
            defaultMat.transparent = true;
            defaultMat.opacity = 0.35;
            defaultMat.depthWrite = false;
            defaultMat.flatShading = false;
            defaultMat.needsUpdate = true;
            break;

          case 'clay':
            if (!child.userData.clayMaterial) {
              child.userData.clayMaterial = new THREE.MeshStandardMaterial({
                color: 0xf4f4f6,
                roughness: 0.82,
                metalness: 0.04,
                side: THREE.DoubleSide,
                flatShading: false,
              });
            }
            child.material = child.userData.clayMaterial;
            break;

          case 'flat':
            child.material = defaultMat;
            defaultMat.transparent = false;
            defaultMat.opacity = 1.0;
            defaultMat.depthWrite = true;
            defaultMat.flatShading = true;
            defaultMat.needsUpdate = true;
            break;

          case 'normals':
            child.material = normalMaterial;
            break;
        }
      } else if (child instanceof THREE.LineSegments) {
        const lineMat = child.material as THREE.LineBasicMaterial;
        if (mode === 'wireframe') {
          lineMat.opacity = 0.85;
          lineMat.transparent = false;
        } else if (mode === 'overlay') {
          lineMat.opacity = 0.2;
          lineMat.transparent = true;
        } else if (mode === 'xray') {
          lineMat.opacity = 0.35;
          lineMat.transparent = true;
        }
      }
    });

    applyVisibility();
    applySelectionHighlighting();
    statusLog.setStatus(`Render mode: ${RENDER_MODE_NAMES[mode] || mode}`, 'ok');
  }

  // Zoom to fit the selected object or part
  function zoomToSelected(): void {
    if (!assemblyGroup || !sceneTree) return;
    const target = sceneTree.getSelectedTarget();
    const targetMeshes: THREE.Mesh[] = [];

    if (target.type === 'object') {
      const obj = module.objects?.find(o => o.id === target.id);
      const gids = new Set(obj ? obj.parts.map(p => p.groupId) : []);
      assemblyGroup.traverse((c) => {
        if (c instanceof THREE.Mesh && c.userData?.groupId !== undefined && gids.has(c.userData.groupId)) {
          targetMeshes.push(c);
        }
      });
    } else if (target.type === 'part' && target.groupId !== undefined) {
      const gid = target.groupId;
      assemblyGroup.traverse((c) => {
        if (c instanceof THREE.Mesh && c.userData?.groupId === gid) {
          targetMeshes.push(c);
        }
      });
    }

    if (targetMeshes.length === 0) {
      zoomAllToMesh();
      return;
    }

    const box = new THREE.Box3();
    for (const m of targetMeshes) {
      if (m.geometry) {
        m.geometry.computeBoundingBox();
        if (m.geometry.boundingBox) {
          const meshBox = m.geometry.boundingBox.clone().applyMatrix4(m.matrixWorld);
          box.union(meshBox);
        }
      }
    }

    if (box.isEmpty()) return;

    const center = new THREE.Vector3();
    box.getCenter(center);
    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    // Frame by the perspective frustum regardless of which camera is active, so "zoom to fit"
    // means the same thing in both views. In ortho the distance does not change the framing,
    // so the frustum is what has to be resized.
    const fov = CAM_FOV * (Math.PI / 180);
    let cameraDistance = Math.abs(maxDim / 2 / Math.tan(fov / 2)) * 1.6;
    cameraDistance = Math.max(cameraDistance, 15);

    const direction = new THREE.Vector3().subVectors(camera.position, controls.target).normalize();
    controls.target.copy(center);
    camera.position.copy(center).addScaledVector(direction, cameraDistance);
    if (camera === orthoCamera) setOrthoFrustum(orthoHalfHeightForDistance(cameraDistance));
    camera.lookAt(center);
    controls.update();

    statusLog.setStatus(`⛶ Zoomed to fit selection (${maxDim.toFixed(1)}mm)`, 'ok');
  }

  // Core Generic Part Color Applier: set color directly on each Mesh by colorKey
  function applyPartColors(): void {
    if (!assemblyGroup) return;
    const toColorNum = (hex: string) => parseInt(hex.replace('#', '0x'), 16);
    assemblyGroup.traverse((child) => {
      if (child instanceof THREE.Mesh && child.userData?.colorKey) {
        const hex = partColors[child.userData.colorKey];
        if (hex) {
          const col = toColorNum(hex);
          if (child.userData.defaultMaterial && 'color' in child.userData.defaultMaterial) {
            (child.userData.defaultMaterial as THREE.MeshPhongMaterial).color.setHex(col);
          }
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          for (const mat of mats) {
            if (mat && 'color' in mat && mat !== child.userData.clayMaterial) {
              (mat as THREE.MeshPhongMaterial).color.setHex(col);
              mat.needsUpdate = true;
            }
          }
        }
      }
    });
  }

  // Visual highlight for selected cluster
  function applySelectionHighlighting(): void {
    // 1. Always preserve user's original part colors
    applyPartColors();

    const selectedGids = sceneTree ? sceneTree.getSelectedGroupIds() : new Set<number>();
    const showWires = (currentViewMode === 'wireframe' || currentViewMode === 'overlay' || currentViewMode === 'xray');
    const defaultWireOpacity = currentViewMode === 'wireframe' ? 0.85 : (currentViewMode === 'xray' ? 0.35 : 0.15);

    if (!assemblyGroup) return;

    assemblyGroup.traverse((child) => {
      if (child.userData?.isSupport) return;
      const gid = child.userData?.groupId;
      const isSelected = gid !== undefined && selectedGids.has(gid);
      const isVisible = (gid !== undefined && sceneTree) ? sceneTree.isPartVisible(gid) : true;

      if (child instanceof THREE.Mesh) {
        const mat = child.material;
        if (mat && 'emissive' in mat && (mat as any).emissive) {
          (mat as any).emissive.setHex(isSelected ? 0x1d3557 : 0x000000);
        }
      } else if (child instanceof THREE.LineSegments && !child.userData?.isWatertightEdge) {
        const lineMat = child.material as THREE.LineBasicMaterial;
        lineMat.color.setHex(isSelected ? 0x2563eb : 0x1e293b);
        lineMat.opacity = isSelected ? 0.75 : defaultWireOpacity;
        child.visible = isVisible && showWires;
      }
    });

    // Update dimensions HUD to display bounding box of the selected item
    updateDimensionsHud();
  }

  // Right Panel Handlers
  rightPanel.onViewModeChange((m) => applyViewMode(m));
  rightPanel.onGridToggle((on) => {
    groundStyle = on ? 'grid' : 'none';
    gridHelper.visible = on;
    snapmakerBed.visible = false;
    solidGround.visible = false;
  });
  rightPanel.onGroundStyleChange((style) => {
    groundStyle = style;
    gridHelper.visible = !lightSimulationOn && (style === 'grid' || style === 'snapmaker');
    snapmakerBed.visible = !lightSimulationOn && style === 'snapmaker';
    solidGround.visible = !lightSimulationOn && style === 'solid';
    statusLog.setStatus(
      style === 'grid' ? '▦ Grid background' : style === 'snapmaker' ? '▣ Snapmaker build plate' : style === 'solid' ? '▰ Solid background' : '○ Viewport background hidden',
      'info',
    );
  });
  rightPanel.onZoomAll(() => {
    zoomAllToMesh();
    statusLog.setStatus('Zoomed to fit', 'ok');
  });

  rightPanel.onAutoRotateToggle((on) => {
    controls.autoRotate = on;
    statusLog.setStatus(on ? '↻ 360° Auto-Rotate: ON' : '360° Auto-Rotate: OFF', 'ok');
  });

  // Overhang >45 deg Highlight handler
  rightPanel.onOverhangToggle((on) => {
    isOverhangActive = on;
    if (on) {
      ensureSupportMeshes();
      const supportCount = countSupportFaces();
      modelHealthLeds.setSupport(supportCount === 0, supportCount);
      statusLog.setStatus(supportCount > 0
        ? `⚠️ Highlight Overhang >45°: ON (${supportCount} faces)`
        : '✓ No overhangs >45° detected (FDM safe)', 'ok');
    } else {
      removeSupportMeshes();
      // No support analysis is performed while the feature is disabled.
      modelHealthLeds.setSupportUnknown();
      statusLog.setStatus('Highlight Overhang >45°: OFF', 'ok');
    }
    applyVisibility();
  });

  // ============================================================================
  // 3D SECTION VIEW / CLIPPING PLANE SYSTEM
  // ============================================================================
  let isSectionCutActive = false;
  // Tracks the previous toggle state so the panel callback can tell a fresh switch-on from a
  // re-assert of an already-active cut (the axis buttons re-fire it with active === true).
  let wasSectionCutActive = false;
  let sectionCutAxis: 'x' | 'y' | 'z' = 'x';
  // Position ACROSS the model, 0-100%, not an absolute mm coordinate. See ui-panels.ts for why:
  // an absolute plane position could sit entirely outside a thin model (the old default 30mm
  // was past name_clicker's 29.7mm depth and 29.3mm height), which made Cut X/Y/Z all remove
  // nothing and look identical.
  let sectionCutPercent = 50;

  // Unit vectors for the three cut axes. The plane's normal is rebuilt from these on every
  // update instead of being stored, because its SIGN now depends on where the camera is: the cut
  // always removes the half nearest the viewer (see updateSectionSide / cameraSideOnAxis).
  const AXIS_UNIT: readonly THREE.Vector3[] = [
    new THREE.Vector3(1, 0, 0),
    new THREE.Vector3(0, 1, 0),
    new THREE.Vector3(0, 0, 1),
  ];

  // Which half to KEEP along the active axis. false = keep the low side (remove the high half),
  // true = keep the high side. Recomputed from the camera whenever the cut is switched on or its
  // axis changes, so the freshly cut face is the one looking back at the viewer.
  let sectionKeepHigh = false;

  // THREE.Plane stores the Vector3 BY REFERENCE - it does not copy it (Plane.js: `this.normal = normal`).
  // An earlier version built the plane from a shared per-axis normal table, so `clippingPlane.normal`
  // and that table entry were the same object and the `.copy()` in updateClippingPlane() wrote each
  // newly chosen axis normal straight back into the table, corrupting it:
  //   pick Cut Y -> table.x becomes (0,-1,0)
  //   pick Cut Z -> table.x becomes (0,0,-1)
  //   pick Cut X -> reads the corrupted entry and cuts along Z instead of X
  // That is exactly "switch X, Y, Z, then X no longer cuts on X". Measured 2026-10-03.
  // Fixed by giving the plane its own normal, now rebuilt from AXIS_UNIT each update.
  const clippingPlane = new THREE.Plane(new THREE.Vector3(-1, 0, 0), 0);
  let planeHelper: THREE.PlaneHelper | null = null;

  // ---- Section cut handle: drag it in the 3D view to move the cut ----
  //
  // Added straight to `scene`, NOT to assemblyGroup: applyClippingPlanesToMaterials() walks
  // assemblyGroup and gives every mesh there a clippingPlanes list, so a handle parented inside
  // it would clip itself the moment the plane reached it.
  //
  // depthTest:false + a high renderOrder keep it drawn on top of the model. A gizmo that sinks
  // into the solid is unusable - you cannot grab what you cannot see.
  const sectionHandle = new THREE.Group();
  sectionHandle.visible = false;
  scene.add(sectionHandle);

  const makeSectionHandleSprite = (): THREE.Sprite => {
    // The arrow is drawn inside a square canvas and shown as a sprite. Every mark must stay
    // within [0, size]: anything past the edge is clipped, not wrapped. An earlier version put
    // the arrowhead apexes at x = -6 and x = 102 on a 96px canvas, so each head was sliced in
    // half and the handle read as two loose fragments at the ends of the travel (measured
    // 2026-10-03).
    //
    // Style: TWO SEPARATE arrows pointing outward, matching the reference image the project owner
    // supplied. Three earlier passes were wrong in different ways - an 18px shaft with a dark
    // halo (a cartoon sticker), a 7px hairline with hard corners, then a single connected
    // double-arrow with radius-5 fillets ("the rounding looks hand-drawn"). The reference settles
    // it: the two heads are NOT joined by a continuous shaft, and every corner is a SHARP
    // geometric corner, not a fillet. Sharp corners cannot look hand-drawn, which is exactly the
    // complaint the fillets were meant to fix and failed to.
    //
    // Proportions were measured off that reference (240x87 px) rather than eyeballed: each arrow
    // is 95px long, the gap between them 30px, the head 42px deep and 53px tall, the shaft 24px.
    // Scaled so the pair spans 122 of the 128 canvas units, that is the constants below.
    //
    // Drawn at 4x and sampled down, so the long diagonal edges of the heads are anti-aliased
    // instead of staircased.
    const SS = 4;                  // supersampling factor
    const size = 128;
    const canvas = document.createElement('canvas');
    canvas.width = size * SS;
    canvas.height = size * SS;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(SS, SS);

    const cy = size / 2;
    const cx = size / 2;

    const arrowW = 52.7;   // length of one arrow, tip to inner end of its shaft
    const gapHalf = 8.3;   // half the gap between the two arrows
    const headLen = 23.3;  // how far the head extends back from its tip
    const headHalf = 14.7; // half-height of the head at its base
    const shaftHalf = 6.7; // half the shaft thickness

    const leftTip = cx - gapHalf - arrowW;    // 3
    const rightTip = cx + gapHalf + arrowW;   // 125
    const leftInner = cx - gapHalf;
    const rightInner = cx + gapHalf;

    // Two independent paths - deliberately not one silhouette, because the reference leaves a
    // clear break between the arrows.
    const left = new Path2D();
    left.moveTo(leftTip, cy);                             // tip
    left.lineTo(leftTip + headLen, cy - headHalf);        // upper barb
    left.lineTo(leftTip + headLen, cy - shaftHalf);       // in to the shaft
    left.lineTo(leftInner, cy - shaftHalf);               // shaft top
    left.lineTo(leftInner, cy + shaftHalf);               // inner end
    left.lineTo(leftTip + headLen, cy + shaftHalf);       // shaft bottom
    left.lineTo(leftTip + headLen, cy + headHalf);        // lower barb
    left.closePath();

    const right = new Path2D();
    right.moveTo(rightTip, cy);
    right.lineTo(rightTip - headLen, cy - headHalf);
    right.lineTo(rightTip - headLen, cy - shaftHalf);
    right.lineTo(rightInner, cy - shaftHalf);
    right.lineTo(rightInner, cy + shaftHalf);
    right.lineTo(rightTip - headLen, cy + shaftHalf);
    right.lineTo(rightTip - headLen, cy + headHalf);
    right.closePath();

    // Fill only, no outline - an outline is what made an earlier version read as a sticker.
    // Colour sampled from the reference's dominant pixel (#d91515).
    ctx.fillStyle = '#d91515';
    ctx.fill(left);
    ctx.fill(right);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    // Linear filtering + no mipmaps: the sprite is usually drawn smaller than its 512px source,
    // so without this the downscale aliases and the diagonals pick up a jagged edge.
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.anisotropy = 4;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
      map: tex,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    }));
    sprite.renderOrder = 999;

    // Alpha mask for hit-testing. The sprite's quad is SQUARE (128x128) but the arrows only
    // occupy a band across its middle, so a plain raycast against the sprite makes the large
    // empty areas above and below the arrows draggable too - reported as "the cut moves when the
    // pointer is nowhere near the arrow". Raycasting returns a uv, so the paint can be tested
    // exactly: only pixels that were actually filled count as a hit.
    //
    // Read once here rather than per pointer event: getImageData on every mousemove would stall
    // the frame. The full-resolution alpha is kept (512x512 = 256KB) so the test is pixel-exact
    // even when the sprite is drawn large on screen.
    const alpha = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    const mask = new Uint8Array(canvas.width * canvas.height);
    for (let i = 0, p = 3; i < mask.length; i++, p += 4) mask[i] = alpha[p] ?? 0;
    sprite.userData.hitMask = mask;
    sprite.userData.hitMaskW = canvas.width;
    sprite.userData.hitMaskH = canvas.height;
    return sprite;
  };

  const sectionHandleSprite = makeSectionHandleSprite();
  sectionHandle.add(sectionHandleSprite);

  const hitSectionHandlePaint = (): boolean => hitAlphaMaskedSprite(raycaster, sectionHandleSprite);

  /** Place the handle on the cut plane, at the model's centre on the other two axes. */
  function syncSectionHandle(): void {
    if (!isSectionCutActive || !assemblyGroup) {
      sectionHandle.visible = false;
      return;
    }
    const box = new THREE.Box3().setFromObject(assemblyGroup);
    if (box.isEmpty()) {
      sectionHandle.visible = false;
      return;
    }
    const center = new THREE.Vector3();
    box.getCenter(center);
    const axisIndex = sectionCutAxis === 'x' ? 0 : sectionCutAxis === 'y' ? 1 : 2;
    center.setComponent(axisIndex, sectionPlaneCoord());
    sectionHandle.position.copy(center);

    // Scale with the model so the same handle works on a 20mm keycap and a 300mm lampshade.
    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z, 1);
    const s = Math.max(maxDim * 0.11, 3);
    sectionHandleSprite.scale.set(s, s, s);

    // Point the arrow along the direction the user will actually drag. Cut Z is a horizontal
    // plane, so it moves vertically on screen; Cut X and Cut Y are vertical planes that read as
    // horizontal in the default views. (For X/Y the on-screen direction really depends on the
    // camera, so this is the common case, not an exact match.)
    sectionHandleSprite.material.rotation = sectionCutAxis === 'z' ? Math.PI / 2 : 0;

    sectionHandle.visible = true;
  }

  // Section cut bold border (thickened cut outline using solid ribbon mesh + multi-stroke lines)
  let sectionCutOutline: THREE.Group | null = null;
  let sectionCutPositions: Float32Array | null = null;
  const sectionCutOutlineMat = new THREE.LineBasicMaterial({
    color: 0x000000, // Deep solid black
    linewidth: 1,
    depthTest: true,
    depthWrite: false, // Prevent depth contention with coplanar triangles and self-occlusion
  });
  const sectionCutRibbonMat = new THREE.MeshBasicMaterial({
    color: 0x000000,
    side: THREE.DoubleSide,
    depthTest: true,
    depthWrite: false,
  });

  function updateSectionCutOutline(): void {
    if (sectionCutOutline) {
      scene.remove(sectionCutOutline);
      sectionCutOutline.traverse((c) => {
        if (c instanceof THREE.Mesh || c instanceof THREE.LineSegments) {
          c.geometry.dispose();
        }
      });
      sectionCutOutline = null;
    }
    sectionCutPositions = null;

    if (!isSectionCutActive || !assemblyGroup) return;

    const lineSegmentsPositions = computeSectionIntersectionSegments(
      assemblyGroup,
      clippingPlane,
      (groupId) => !sceneTree || sceneTree.isPartVisible(groupId),
    );

    sectionCutPositions = lineSegmentsPositions;
    const rebuiltOutline = buildSectionCutOutline(
      lineSegmentsPositions,
      clippingPlane.normal,
      sectionCutOutlineMat,
      sectionCutRibbonMat,
    );
    if (rebuiltOutline) {
      sectionCutOutline = rebuiltOutline;
      scene.add(sectionCutOutline);
    }
  }

  function applyClippingPlanesToMaterials(): void {
    if (!assemblyGroup) return;
    const activePlanes = isSectionCutActive ? [clippingPlane] : [];

    assemblyGroup.traverse((child) => {
      if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
        const mats = Array.isArray(child.material) ? child.material : [child.material];
        for (const mat of mats) {
          if (mat) {
            mat.clippingPlanes = activePlanes;
            mat.clipShadows = true;
            mat.needsUpdate = true;
          }
        }
      }
    });
  }

  /** World coordinate of the cut plane along the active axis, from the model's own bounds.
   *  The model is not guaranteed to sit at the origin (name_clicker spans negative X), so a
   *  percentage of its bounding box is the only mapping that always lands inside the model.
   *
   *  The travel runs from just OUTSIDE min to just OUTSIDE max. Both ends matter:
   *   - Exactly at min/max the plane is coplanar with the model's outer face, the GPU cannot
   *     order the two, and that face flickers as the camera moves.
   *   - Just INSIDE (the first attempt at this fix) is worse: clipping has no cap, so shaving a
   *     sliver off the face removes it and you see straight into the hollow interior - reported
   *     as "axes Y and Z lose a face at the ends". It was most visible on Y and Z because their
   *     spans (26mm, 23mm) are small, so the same absolute inset is a bigger share of the wall.
   *  Just outside gives a complete model at 0% and 100%, and no coplanar face. */
  function sectionPlaneCoord(): number {
    if (!assemblyGroup) return sectionCutPercent / 100 * 200 - 100;
    const box = new THREE.Box3().setFromObject(assemblyGroup);
    return sectionPlaneCoordinate(box, sectionCutAxis, sectionCutPercent);
  }

  /** Which side of the model the camera is on, along the active axis: +1 or -1.
   *  Returns 0 when the view is essentially square-on to the axis, i.e. the camera sits ON the
   *  cut plane. There is no meaningful "near half" then, so callers keep the previous choice
   *  rather than let a sub-degree camera wobble flip the side back and forth. */
  function cameraSideOnAxis(): number {
    if (!assemblyGroup) return 0;
    const box = new THREE.Box3().setFromObject(assemblyGroup);
    return cameraSideOnSectionAxis(camera, box, sectionCutAxis);
  }

  /** Aim the cut at the viewer: keep the far half, remove the near one, so the exposed face
   *  looks back at the camera and the cross-section is visible without orbiting.
   *
   *  "Near" is measured against the model CENTRE, not the cut plane. That matters once the cut
   *  has travelled past the centre: measuring against the plane would flip the side at 50% and
   *  the removed half would swap under the user's hand mid-drag. Against the centre the choice is
   *  stable for the whole drag, and the result is the intuitive one - whichever half you are
   *  looking at is the half that goes.
   *
   *  Called only when the cut is switched on or its axis changes, never while dragging, so the
   *  side cannot change under the pointer. */
  function updateSectionSide(): void {
    const side = cameraSideOnAxis();
    if (side === 0) return;                       // square-on: keep whatever was chosen last
    sectionKeepHigh = side < 0;                   // camera on the low side -> remove the low half
  }

  /** Unit vector of the axis at `index` (0 = x, 1 = y, 2 = z). Indexing AXIS_UNIT directly yields
   *  `Vector3 | undefined` under noUncheckedIndexedAccess; the index is always 0-2 here. */
  function axisUnit(index: number): THREE.Vector3 {
    return AXIS_UNIT[index] ?? AXIS_UNIT[0]!;
  }

  function updateClippingPlane(): void {
    const axisIndex = sectionAxisIndex(sectionCutAxis);
    // Three.Plane equation: dot(P, normal) + constant = 0
    // If normal = (0, 0, 1) [sectionKeepHigh=true, keeping Z > coord]:
    //   dot(P, (0,0,1)) + constant = Z + constant = 0 -> constant = -coord
    // If normal = (0, 0, -1) [sectionKeepHigh=false, keeping Z < coord]:
    //   dot(P, (0,0,-1)) + constant = -Z + constant = 0 -> constant = +coord
    // Therefore: constant = (sectionKeepHigh ? -1 : 1) * coord.
    const normSign = sectionKeepHigh ? 1 : -1;
    clippingPlane.normal.copy(axisUnit(axisIndex)).multiplyScalar(normSign);
    clippingPlane.constant = -normSign * sectionPlaneCoord();

    if (planeHelper) {
      scene.remove(planeHelper);
      planeHelper = null;
    }

    applyClippingPlanesToMaterials();
    updateSectionCutOutline();
    syncSectionHandle();
  }

  // Track activation order so Escape closes only the most recently activated viewport tool.
  let isMeasureActive = false;
  let lastActivatedViewportTool: 'measure' | 'section' | null = null;

  rightPanel.onSectionCutToggle((active, axis) => {
    if (active) lastActivatedViewportTool = 'section';
    else if (lastActivatedViewportTool === 'section') lastActivatedViewportTool = isMeasureActive ? 'measure' : null;
    const axisChanged = axis !== sectionCutAxis;
    isSectionCutActive = active;
    sectionCutAxis = axis;
    // Re-aim at the viewer on switch-on and on every axis change - but not on switch-off, so the
    // side survives a toggle off/on and the model does not jump behind the user's back.
    if (active && (axisChanged || !wasSectionCutActive)) updateSectionSide();
    wasSectionCutActive = active;
    updateClippingPlane();
    rightPanel.setSectionPercent(sectionCutPercent);
    if (active) {
      statusLog.setStatus(`✂️ 3D Section View ON (Cut Axis: ${axis.toUpperCase()}, Position: ${sectionCutPercent.toFixed(0)}%) — drag the handle`, 'ok');
    } else {
      statusLog.setStatus('3D Section View OFF', 'ok');
    }
  });

  rightPanel.onProfileInspectorOpen(() => {
    if (!isSectionCutActive || !sectionCutPositions || sectionCutPositions.length === 0) {
      statusLog.setStatus('Profile Inspector cần bật Section và có giao tuyến.', 'warn');
      return;
    }
    profileInspector.open({
      moduleId: module.id,
      part: selectedGroup,
      axis: sectionCutAxis,
      position: sectionPlaneCoord(),
      segments: sectionCutPositions,
    });
  });

  // ============================================================================
  // 3D POINT-TO-POINT MEASUREMENT TOOL SYSTEM
  // ============================================================================
  let measurePointA: THREE.Vector3 | null = null;
  let measurePointB: THREE.Vector3 | null = null;
  let currentMeasureAxis: 'free' | 'x' | 'y' | 'z' = 'free';

  /** The point on the SECTION CUT EDGE under the pointer, or null if the pointer is not near it.
   *
   *  Requested 2026-10-03: with the cut on, the measure tool could only land on the mesh surface,
   *  so measuring across the cut - the one thing a section view is FOR - was impossible; the
   *  intersection line is exactly the geometry you want to dimension.
  /** Pick a point on the SECTION CUT (cut outline edges, cut profile vertices, or cut plane surface).
   *  Uses an 18-pixel magnetic screen-space threshold so users can comfortably snap to section cuts
   *  without struggling to hit a 1-pixel hairline. Also respects vertex priorities and cut plane surface. */
  function pickSectionCutEdge(): THREE.Vector3 | null {
    if (!isSectionCutActive || !sectionCutPositions || sectionCutPositions.length === 0) return null;

    const ray = raycaster.ray;
    const count = sectionCutPositions.length / 3;

    // Screen-space magnetic snap threshold (18 pixels for comfortable CAD picking)
    const snapPixels = 18;
    const h = Math.max(container.clientHeight, 1);

    let bestPoint: THREE.Vector3 | null = null;
    let minScreenDist = snapPixels;

    const v0 = new THREE.Vector3();
    const v1 = new THREE.Vector3();
    const ptOnRay = new THREE.Vector3();
    const ptOnSeg = new THREE.Vector3();

    // 1. Test all line segments and vertices of the cut outline
    for (let i = 0; i < count; i += 2) {
      const idx0 = i * 3;
      const idx1 = (i + 1) * 3;
      v0.set(sectionCutPositions[idx0]!, sectionCutPositions[idx0 + 1]!, sectionCutPositions[idx0 + 2]!);
      v1.set(sectionCutPositions[idx1]!, sectionCutPositions[idx1 + 1]!, sectionCutPositions[idx1 + 2]!);

      const distSq = ray.distanceSqToSegment(v0, v1, ptOnRay, ptOnSeg);

      // Make sure segment is in front of camera
      const toPt = new THREE.Vector3().subVectors(ptOnRay, ray.origin);
      if (toPt.dot(ray.direction) <= 0) continue;

      // Pixel-to-world ratio at ptOnRay
      let worldRadiusForOnePixel = 1;
      if (camera instanceof THREE.PerspectiveCamera) {
        const camDist = camera.position.distanceTo(ptOnRay);
        worldRadiusForOnePixel = (2 * camDist * Math.tan((camera.fov * Math.PI / 180) / 2)) / h;
      } else if (camera instanceof THREE.OrthographicCamera) {
        worldRadiusForOnePixel = (camera.top - camera.bottom) / h;
      }

      const screenDist = Math.sqrt(distSq) / Math.max(worldRadiusForOnePixel, 1e-6);

      if (screenDist < minScreenDist) {
        minScreenDist = screenDist;

        // Vertex priority: if within 10 screen pixels of vertex v0 or v1, snap strictly to the vertex
        const d0 = ray.distanceToPoint(v0) / Math.max(worldRadiusForOnePixel, 1e-6);
        const d1 = ray.distanceToPoint(v1) / Math.max(worldRadiusForOnePixel, 1e-6);

        if (d0 < 10 && d0 <= d1) {
          bestPoint = v0.clone();
        } else if (d1 < 10) {
          bestPoint = v1.clone();
        } else {
          bestPoint = ptOnSeg.clone();
        }
      }
    }

    if (bestPoint) {
      return clippingPlane.projectPoint(bestPoint, new THREE.Vector3());
    }

    // 2. If not snapping directly to the cut edge, check if ray intersects the cut plane inside the cut boundary
    const cutPlanePt = new THREE.Vector3();
    if (ray.intersectPlane(clippingPlane, cutPlanePt)) {
      const toPlane = new THREE.Vector3().subVectors(cutPlanePt, ray.origin);
      if (toPlane.dot(ray.direction) > 0 && sectionCutPositions && sectionCutPositions.length > 0) {
        const tol = 1.0;
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        for (let p = 0; p < sectionCutPositions.length; p += 3) {
          const px = sectionCutPositions[p]!;
          const py = sectionCutPositions[p + 1]!;
          const pz = sectionCutPositions[p + 2]!;
          if (px < minX) minX = px; if (px > maxX) maxX = px;
          if (py < minY) minY = py; if (py > maxY) maxY = py;
          if (pz < minZ) minZ = pz; if (pz > maxZ) maxZ = pz;
        }
        if (
          cutPlanePt.x >= minX - tol && cutPlanePt.x <= maxX + tol &&
          cutPlanePt.y >= minY - tol && cutPlanePt.y <= maxY + tol &&
          cutPlanePt.z >= minZ - tol && cutPlanePt.z <= maxZ + tol
        ) {
          return cutPlanePt.clone();
        }
      }
    }

    return null;
  }

  /** Where a measure click should land: the cut edge if the pointer is on it, else the mesh
   *  surface snapped to its nearest vertex / edge midpoint. Null when nothing was hit. */
  function measureSnap(meshHits: THREE.Intersection[]): THREE.Vector3 | null {
    const edge = pickSectionCutEdge();
    if (edge) {
      // If there's a visible mesh hit in front of the cut (closer than the cut by at least 0.5mm):
      const nearestMeshHit = meshHits[0];
      if (nearestMeshHit && nearestMeshHit.distance < camera.position.distanceTo(edge) - 0.5) {
        return snapToClosestVertexOrEdge(nearestMeshHit);
      }
      return edge;
    }
    return meshHits[0] ? snapToClosestVertexOrEdge(meshHits[0]) : null;
  }

  const measurementOverlay = new MeasurementOverlay(scene, container);
  const { group: measureGroup, markerA, markerB, previewMarker, line: measureLine, label: measureLabel } = measurementOverlay;

  function resetMeasurement(): void {
    measurePointA = null;
    measurePointB = null;
    measurementOverlay.reset();
  }

  function updateMeasurementHUD(p1: THREE.Vector3, p2: THREE.Vector3): void {
    measurementOverlay.updateLabel(p1, p2);
  }

  function updateMeasurementLine(p1: THREE.Vector3, p2: THREE.Vector3): void {
    measurementOverlay.updateLine(p1, p2);
  }

  // Simple, Lightweight 500ml Water Bottle Scale Reference Object (Ø65mm x 210mm height)
  function createAquafinaBottle(): THREE.Group {
    const bottleGroup = new THREE.Group();
    bottleGroup.name = 'WaterBottleScaleReference';

    // Smooth profile points for 500ml water bottle
    const points: THREE.Vector2[] = [];
    points.push(new THREE.Vector2(0, 0));
    points.push(new THREE.Vector2(28, 0));
    points.push(new THREE.Vector2(32.5, 3));
    points.push(new THREE.Vector2(32.5, 145)); // smooth main body

    // Shoulder curve tapering to neck
    points.push(new THREE.Vector2(31.0, 160));
    points.push(new THREE.Vector2(25.0, 175));
    points.push(new THREE.Vector2(14.5, 192));
    
    // Neck & cap
    points.push(new THREE.Vector2(14.5, 198));
    points.push(new THREE.Vector2(15.5, 198));
    points.push(new THREE.Vector2(15.5, 210));
    points.push(new THREE.Vector2(0, 210));

    const bodyGeo = new THREE.LatheGeometry(points, 32);
    // Simple translucent neutral light material (no blue, no label graphics)
    const bodyMat = new THREE.MeshPhysicalMaterial({
      color: 0xe2e8f0,
      roughness: 0.2,
      transmission: 0.8,
      opacity: 0.35,
      transparent: true,
      ior: 1.3,
      side: THREE.DoubleSide,
    });
    const bodyMesh = new THREE.Mesh(bodyGeo, bodyMat);
    bottleGroup.add(bodyMesh);

    // Subtle edge wireframe outline for CAD visual clarity
    const wireGeo = new THREE.EdgesGeometry(bodyGeo, 25);
    const wireMat = new THREE.LineBasicMaterial({
      color: 0x94a3b8,
      transparent: true,
      opacity: 0.35,
    });
    const wire = new THREE.LineSegments(wireGeo, wireMat);
    bottleGroup.add(wire);

    // LatheGeometry default axis is Y. Rotate so Z is UP (matching CAD scene coordinate system)
    bottleGroup.rotation.x = Math.PI / 2;

    return bottleGroup;
  }

  const aquafinaBottle = createAquafinaBottle();
  aquafinaBottle.visible = false;
  scene.add(aquafinaBottle);

  // Position bottle further away from the model (120mm offset)
  function updateAquafinaBottlePosition(): void {
    if (!assemblyGroup) return;
    const box = new THREE.Box3().setFromObject(assemblyGroup);
    if (!box.isEmpty()) {
      const centerY = (box.max.y + box.min.y) / 2;
      aquafinaBottle.position.set(box.max.x + 120, centerY, box.min.z);
    } else {
      aquafinaBottle.position.set(150, 0, 0);
    }
  }

  rightPanel.onMeasureToggle((active) => {
    isMeasureActive = active;
    if (active) lastActivatedViewportTool = 'measure';
    else if (lastActivatedViewportTool === 'measure') lastActivatedViewportTool = isSectionCutActive ? 'section' : null;
    resetMeasurement();
    if (active) {
      updateAquafinaBottlePosition();
      aquafinaBottle.visible = true;
      statusLog.setStatus(`📏 Measure Mode: Click surface to measure • Drag bottle to move scale reference`, 'info');
    } else {
      aquafinaBottle.visible = false;
      // Same reasoning as the bulb: no bottle, no movable handle.
      if (moveTarget === 'bottle') hideMoveGizmo();
      statusLog.setStatus('Measure Mode OFF', 'ok');
    }
  });

  rightPanel.onAxisLockChange((axis) => {
    currentMeasureAxis = axis;
    const msg = axis === 'free' ? '3D Free Measurement' : `Locked to Point 1 ${axis.toUpperCase()} Axis`;
    statusLog.setStatus(`📏 Measure Tool: ${msg}`, 'info');
  });

  // TASK 22 (2026-09-30) - KEYBOARD SHORTCUTS + A HINT OVERLAY.
  //
  // The only shortcut was Escape (clear measurement). Everything else - fit, view presets,
  // grid, wireframe, measure - was reachable only by finding the right button, so new users
  // had no way to discover that those features existed at all.
  //
  // Guard: shortcuts must NOT fire while the user is typing. Without this, entering a value in
  // a sidebar number input would trigger view changes on every keystroke (e.g. "3" switching
  // to the Top view). This is the classic bug in hand-rolled shortcut handlers, so it is
  // checked first rather than per-key.
  function isTypingTarget(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    if (!el || !el.tagName) return false;
    const tag = el.tagName.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable === true;
  }

  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' && isTypingTarget(e.target)) return;
    // Leave modifier combinations to the browser (Ctrl+R, Cmd+L, ...).
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    switch (e.key) {
      case '?': case '/':
        if (e.shiftKey || e.key === '?') {
          toggleShortcutsModal();
          break;
        }
        break;
      case 'f': case 'F':
        zoomAllToMesh();
        statusLog.setStatus('🔍 Zoomed to fit  (F)', 'info');
        break;
      case 'g': case 'G':
        gridHelper.visible = !gridHelper.visible;
        statusLog.setStatus(gridHelper.visible ? '▦ Grid on  (G)' : '▦ Grid off  (G)', 'info');
        break;
      case 'w': case 'W':
        applyViewMode(currentViewMode === 'wireframe' ? 'shape' : 'wireframe');
        statusLog.setStatus(`View: ${currentViewMode === 'wireframe' ? 'Wireframe' : 'Solid'}  (W)`, 'info');
        break;
      case '1': case '3': case '7': case '9': {
        // Blender-style: 1 = front, 3 = right, 7 = top, 9 = bottom.
        const preset: ViewPreset = e.key === '1' ? 'front' : e.key === '3' ? 'right' : e.key === '7' ? 'top' : 'bottom';
        applyViewPreset(preset);
        break;
      }
      case 'z': case 'Z':
        setZoomPointMode(!isZoomPointActive);
        break;
      case 'Escape':
        if (shortcutsModalEl) {
          toggleShortcutsModal(false);
          break;
        }
        if (isZoomPointActive) {
          setZoomPointMode(false);
          break;
        }
        // Close one viewport tool per Escape, starting with the most recently activated one.
        // Keep the other tool active so section + measurement can be used together.
        if (lastActivatedViewportTool === 'measure' && isMeasureActive) {
          rightPanel.setMeasureMode(false);
        } else if (lastActivatedViewportTool === 'section' && isSectionCutActive) {
          rightPanel.setSectionCutMode(false);
        } else if (isMeasureActive) {
          rightPanel.setMeasureMode(false);
        } else if (isSectionCutActive) {
          rightPanel.setSectionCutMode(false);
        }
        clearHover();
        break;
      default:
        return;
    }
  });

  // Hint bar removed per user request.

  // View presets
  // TASK 22 (2026-09-30): extracted so the 1/3/7 shortcuts and the preset buttons share ONE
  // code path. Duplicating this logic for the keyboard would have let the two drift apart.
  function applyViewPreset(preset: ViewPreset): void {
    const target = controls.target.clone();
    const offset = new THREE.Vector3().subVectors(camera.position, target);
    const currentDist = offset.length();
    const dirs: Record<ViewPreset, THREE.Vector3> = {
      iso:    new THREE.Vector3( 1, -1,  0.85).normalize(),
      iso2:   new THREE.Vector3(-1,  1,  0.85).normalize(),
      front:  new THREE.Vector3( 0, -1,  0),
      back:   new THREE.Vector3( 0,  1,  0),
      left:   new THREE.Vector3(-1,  0,  0),
      right:  new THREE.Vector3( 1,  0,  0),
      top:    new THREE.Vector3( 0,  0,  1),
      bottom: new THREE.Vector3( 0,  0, -1),
    };
    const newDir = dirs[preset];
    // Front / Top / Bottom / Right are the true projection views: swap in the orthographic camera so
    // parallel edges stay parallel. `iso` returns to perspective. activateCamera copies the
    // current position across, so the swap itself does not move the viewpoint.
    const wantOrtho = preset !== 'iso' && preset !== 'iso2';
    activateCamera(wantOrtho ? orthoCamera : perspCamera);
    camera.position.copy(target).addScaledVector(newDir, currentDist);
    if (preset === 'top') {
      camera.up.set(0, 1, 0);
    } else if (preset === 'bottom') {
      camera.up.set(0, -1, 0);
    } else {
      camera.up.set(0, 0, 1);
    }
    camera.lookAt(target);
    if (wantOrtho) {
      // Keep the framing consistent with the perspective view at this distance.
      setOrthoFrustum(orthoHalfHeightForDistance(currentDist));
      is2DView = true;
      controls.enableRotate = false;
      controls.autoRotate = false;
      autoRotate = false;
    } else {
      is2DView = false;
      controls.enableRotate = true;
    }
    controls.update();
    const labelMap: Record<ViewPreset, string> = {
      iso: 'Isometric 1',
      iso2: 'Isometric 2',
      front: 'Front (2D ortho)',
      back: 'Back (2D ortho)',
      left: 'Left (2D ortho)',
      right: 'Right (2D ortho)',
      top: 'Top (2D ortho)',
      bottom: 'Bottom (2D ortho)'
    };
    statusLog.setStatus(`View: ${labelMap[preset]}${is2DView ? ' — locked 2D' : ''}`, 'ok');
  }

  rightPanel.onViewPreset((preset) => applyViewPreset(preset));

  // Auto-rotate
  rightPanel.onAutoRotateToggle((on) => {
    autoRotate = on;
    controls.autoRotate = on;
    if (on && is2DView) {
      const target = controls.target.clone();
      const offset = new THREE.Vector3().subVectors(camera.position, target);
      const currentDist = offset.length();
      const isoDir = new THREE.Vector3(1, 1, 0.85).normalize();
      activateCamera(perspCamera); // auto-rotate is a 3D motion - leave the ortho projection
      camera.up.set(0, 0, 1);
      camera.position.copy(target).addScaledVector(isoDir, currentDist);
      is2DView = false;
      controls.enableRotate = true;
      camera.lookAt(target);
      controls.update();
    }
    statusLog.setStatus(on ? 'Auto-rotate ON' : 'Auto-rotate OFF', 'ok');
  });

  // ============================================================================
  // 3-AXIS MOVE GIZMO — light-simulation bulb + scale-reference bottle
  // ============================================================================
  //
  // Click the bulb or the bottle and three arrows appear on it; drag an arrow to slide the object
  // along that axis and no other. This REPLACES the bottle's old drag, which slid it across a
  // horizontal plane: the bottle could never be lifted off the table, which made a scale
  // reference useless for judging a lampshade's height.
  //
  // Built from meshes rather than sprites so the arrows foreshorten honestly when an axis points
  // at the camera. depthTest is off and renderOrder is high - an axis hidden behind the object it
  // moves cannot be grabbed.
  //
  // Colours match the Cut X/Y/Z buttons in ui-panels.ts, so "red is X" is consistent everywhere.
  type MoveAxisId = 'x' | 'y' | 'z';
  const MOVE_AXIS_COLOR: Record<MoveAxisId, number> = { x: 0xef4444, y: 0x10b981, z: 0x3b82f6 };

  /** Unit arrow along +Y with a cone at each end; the caller re-orients it onto an axis. */
  function makeMoveArrow(axis: THREE.Vector3, color: number): { group: THREE.Group; mat: THREE.MeshBasicMaterial } {
    // Total normalized length = 1.0 (SHAFT 0.65 + 2 x HEAD 0.175 = 1.0 tip-to-tip)
    const SHAFT = 0.65, HEAD = 0.175, R = 0.016;
    const mat = new THREE.MeshBasicMaterial({
      color, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false,
    });
    const group = new THREE.Group();
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(R, R, SHAFT, 16), mat);
    shaft.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), axis);
    group.add(shaft);
    // A head at each end, so one arrow drags both ways - which is what "move along X" means.
    for (const sign of [1, -1]) {
      const cone = new THREE.Mesh(new THREE.ConeGeometry(R * 2.8, HEAD, 16), mat);
      cone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), axis.clone().multiplyScalar(sign));
      cone.position.copy(axis).multiplyScalar(sign * (SHAFT / 2 + HEAD / 2));
      group.add(cone);
    }
    group.traverse((o) => { o.renderOrder = 1001; });
    return { group, mat };
  }

  const moveGizmo = new THREE.Group();
  moveGizmo.visible = false;
  scene.add(moveGizmo);

  const moveAxes: Array<{ id: MoveAxisId; vec: THREE.Vector3; group: THREE.Group; mat: THREE.MeshBasicMaterial }> = [];
  (['x', 'y', 'z'] as MoveAxisId[]).forEach((id) => {
    const vec = new THREE.Vector3(id === 'x' ? 1 : 0, id === 'y' ? 1 : 0, id === 'z' ? 1 : 0);
    const { group, mat } = makeMoveArrow(vec, MOVE_AXIS_COLOR[id]);
    group.userData.axisId = id;
    moveGizmo.add(group);
    moveAxes.push({ id, vec, group, mat });
  });

  type MoveTarget = 'light' | 'bottle';
  let moveTarget: MoveTarget | null = null;

  /** The object the gizmo is currently attached to, or null if it is hidden / not applicable. */
  function moveTargetObject(): THREE.Object3D | null {
    if (moveTarget === 'light') return internalLightBulbMesh.visible ? internalLightBulbMesh : null;
    if (moveTarget === 'bottle') return aquafinaBottle.visible ? aquafinaBottle : null;
    return null;
  }

  /** Park the gizmo on its target and size it to that object. */
  function syncMoveGizmo(): void {
    const obj = moveTargetObject();
    if (!obj) { moveGizmo.visible = false; return; }
    const box = new THREE.Box3().setFromObject(obj);
    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    // Moderate, balanced scale: sleek gizmo that remains readable on small objects (bulb)
    // without inflating to giant dimensions on tall objects (bottle/vase).
    const len = Math.max(45, Math.min(maxDim * 0.45, 75));
    moveGizmo.position.copy(obj.position);
    moveGizmo.scale.setScalar(len);
    moveGizmo.visible = true;
  }

  // ============================================================================
  // 2D PLANAR SURFACE TRANSFORM GIZMO FOR SWING BADGE KEYCHAIN
  // ============================================================================
  interface Badge2DItemDef {
    id: string;
    label: string;
    scaleKey: string;
    offsetXKey: string;
    offsetZKey: string;
    rotateKey?: string;
  }

  const BADGE_ITEMS: Badge2DItemDef[] = [
    { id: 'number50', label: 'Số 50', scaleKey: 'number50Scale', offsetXKey: 'number50OffsetX', offsetZKey: 'number50OffsetZ' },
    { id: 'text1976', label: '1976-2026', scaleKey: 'text1976Scale', offsetXKey: 'text1976OffsetX', offsetZKey: 'text1976OffsetZ' },
    { id: 'banner', label: 'Dải Banner', scaleKey: 'bannerScale', offsetXKey: 'bannerOffsetX', offsetZKey: 'bannerOffsetZ' },
    { id: 'hoaLa1', label: 'Hoa Lá 1', scaleKey: 'hoaLa1Scale', offsetXKey: 'hoaLa1OffsetX', offsetZKey: 'hoaLa1OffsetZ', rotateKey: 'hoaLa1Rotate' },
    { id: 'hoaLa2', label: 'Hoa Lá 2', scaleKey: 'hoaLa2Scale', offsetXKey: 'hoaLa2OffsetX', offsetZKey: 'hoaLa2OffsetZ', rotateKey: 'hoaLa2Rotate' },
    { id: 'hoaLa3', label: 'Hoa Lá 3', scaleKey: 'hoaLa3Scale', offsetXKey: 'hoaLa3OffsetX', offsetZKey: 'hoaLa3OffsetZ', rotateKey: 'hoaLa3Rotate' },
  ];

  let selectedBadgeItemIndex = -1;
  let isDraggingBadge2D = false;
  let badge2DDragType: 'move' | 'scale' | 'rotate' | null = null;
  const badgePlane = new THREE.Plane(new THREE.Vector3(0, -1, 0), -0.6);
  const badgePlaneHit = new THREE.Vector3();
  const badgeStartHit = new THREE.Vector3();
  let badgeStartOffsetX = 0;
  let badgeStartOffsetZ = 0;
  let badgeStartScale = 1.0;

  const badge2DGizmoGroup = new THREE.Group();
  badge2DGizmoGroup.name = 'badge2DGizmoGroup';
  badge2DGizmoGroup.visible = (module.id === 'swing_badge_keychain');
  badge2DGizmoGroup.rotation.x = Math.PI / 2;
  scene.add(badge2DGizmoGroup);

  const bMoveGeo = new THREE.CircleGeometry(2.0, 32);
  const bMoveMat = new THREE.MeshBasicMaterial({ color: 0x0284c7, side: THREE.DoubleSide, depthTest: false, transparent: true, opacity: 0.85 });
  const bMoveMesh = new THREE.Mesh(bMoveGeo, bMoveMat);
  bMoveMesh.renderOrder = 999;
  bMoveMesh.userData = { isBadge2DHandle: true, type: 'move' };

  const bMoveLinesGeo = new THREE.BufferGeometry();
  const bMoveLinesPts = [
    -1.4, 0, 0.01,  1.4, 0, 0.01,
     0, -1.4, 0.01,  0, 1.4, 0.01,
     0.9, 0.4, 0.01, 1.4, 0, 0.01, 0.9, -0.4, 0.01, 1.4, 0, 0.01,
    -0.9, 0.4, 0.01, -1.4, 0, 0.01, -0.9, -0.4, 0.01, -1.4, 0, 0.01,
     0.4, 0.9, 0.01, 0, 1.4, 0.01, -0.4, 0.9, 0.01, 0, 1.4, 0.01,
     0.4, -0.9, 0.01, 0, -1.4, 0.01, -0.4, -0.9, 0.01, 0, -1.4, 0.01
  ];
  bMoveLinesGeo.setAttribute('position', new THREE.Float32BufferAttribute(bMoveLinesPts, 3));
  const bMoveLinesMat = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 2, depthTest: false });
  const bMoveLinesMesh = new THREE.LineSegments(bMoveLinesGeo, bMoveLinesMat);
  bMoveLinesMesh.renderOrder = 1000;
  bMoveMesh.add(bMoveLinesMesh);

  const bScaleGeo = new THREE.PlaneGeometry(1.6, 1.6);
  const bScaleMat = new THREE.MeshBasicMaterial({ color: 0xf59e0b, side: THREE.DoubleSide, depthTest: false, transparent: true, opacity: 0.9 });
  const bScaleMesh = new THREE.Mesh(bScaleGeo, bScaleMat);
  bScaleMesh.renderOrder = 999;
  bScaleMesh.userData = { isBadge2DHandle: true, type: 'scale' };

  const bRotateGeo = new THREE.RingGeometry(1.0, 1.6, 32);
  const bRotateMat = new THREE.MeshBasicMaterial({ color: 0x10b981, side: THREE.DoubleSide, depthTest: false, transparent: true, opacity: 0.9 });
  const bRotateMesh = new THREE.Mesh(bRotateGeo, bRotateMat);
  bRotateMesh.renderOrder = 999;
  bRotateMesh.userData = { isBadge2DHandle: true, type: 'rotate' };

  const bOutlineGeo = new THREE.RingGeometry(5.9, 6.1, 48);
  const bOutlineMat = new THREE.MeshBasicMaterial({ color: 0x38bdf8, side: THREE.DoubleSide, depthTest: false, transparent: true, opacity: 0.5 });
  const bOutlineMesh = new THREE.Mesh(bOutlineGeo, bOutlineMat);
  bOutlineMesh.renderOrder = 998;

  badge2DGizmoGroup.add(bMoveMesh, bScaleMesh, bRotateMesh, bOutlineMesh);

  function syncBadge2DGizmo(): void {
    if (module.id !== 'swing_badge_keychain') {
      badge2DGizmoGroup.visible = false;
      return;
    }
    const item = BADGE_ITEMS[selectedBadgeItemIndex];
    if (!item) {
      badge2DGizmoGroup.visible = false;
      return;
    }
    badge2DGizmoGroup.visible = true;

    const badgeDiameter = Number(currentParams['badgeDiameter']) || 40.0;
    const centerZ = badgeDiameter * 0.5;

    const ox = Number(currentParams[item.offsetXKey]) || 0;
    const oz = Number(currentParams[item.offsetZKey]) || 0;
    const scale = Number(currentParams[item.scaleKey]) || 1.0;
    const rot = item.rotateKey ? (Number(currentParams[item.rotateKey]) || 0) : 0;

    badge2DGizmoGroup.position.set(ox, -0.6, centerZ + oz);
    const radius = 6.0 * scale;
    bOutlineMesh.scale.set(scale, scale, 1);

    bScaleMesh.position.set(radius * 0.707, radius * 0.707, 0.02);

    if (item.rotateKey) {
      bRotateMesh.visible = true;
      const rotRad = (rot * Math.PI) / 180;
      const ry = (radius * 1.35) * Math.cos(rotRad);
      const rx = -(radius * 1.35) * Math.sin(rotRad);
      bRotateMesh.position.set(rx, ry, 0.02);
    } else {
      bRotateMesh.visible = false;
    }
  }

  function helperUpdateParam(key: string, val: number): void {
    const rounded = Math.round(val * 100) / 100;
    currentParams[key] = rounded;
    persistParams();
    const wrap = document.querySelector(`.control-group[data-slider-key="${key}"]`);
    if (wrap) {
      const r = wrap.querySelector<HTMLInputElement>('input[type="range"]');
      const n = wrap.querySelector<HTMLInputElement>('input[type="number"]');
      if (r) r.value = String(rounded);
      if (n) n.value = String(rounded);
    }
    updatePreviewDebounced();
  }

  function showMoveGizmo(target: MoveTarget): void {
    moveTarget = target;
    syncMoveGizmo();
  }

  function hideMoveGizmo(): void {
    moveTarget = null;
    moveGizmo.visible = false;
  }

  /** Highlight every arrow except `exceptId`; pass null to clear. */
  function highlightMoveAxis(exceptId: MoveAxisId | null): void {
    for (const a of moveAxes) a.mat.opacity = (exceptId === null || exceptId === a.id) ? 1 : 0.4;
  }

  // ---- axis drag state ----
  let isDraggingMove = false;
  let moveDragAxis: MoveAxisId | null = null;
  const moveDragController = new AxisDragController();
  const moveDragPt = new THREE.Vector3();

  function beginAxisDrag(id: MoveAxisId, hit: THREE.Vector3): boolean {
    const obj = moveTargetObject();
    if (!obj) return false;
    const axis = moveAxes.find((a) => a.id === id)!.vec;
    if (!moveDragController.begin(camera, obj, axis, hit)) return false;
    moveDragAxis = id;
    isDraggingMove = true;
    controls.enabled = false;
    return true;
  }

  function updateAxisDrag(hit: THREE.Vector3): void {
    const obj = moveTargetObject();
    if (!obj || !moveDragAxis) return;
    const drag = moveDragController.update(obj, hit);
    if (!drag) return;
    if (moveTarget === 'light') {
      // The glow mesh is only a stand-in; the light that actually lights the scene must follow it,
      // and the shadow frustum has to reach the new position or the pattern is cut off.
      internalPointLight.position.copy(obj.position);
      internalLightUserMoved = true;
      updateInternalLightShadowRange();
    }
    syncMoveGizmo();
    const mm = drag.axisValue.toFixed(1);
    statusLog.setStatus(
      `↔ ${moveTarget === 'light' ? 'Light bulb' : 'Bottle'} ${moveDragAxis.toUpperCase()} = ${mm}mm`, 'info');
  }

  /** Which arrow, if any, is under the pointer. Walks up to the axis group that owns the hit. */
  function pickMoveAxis(): MoveAxisId | null {
    const hit = raycaster.intersectObject(moveGizmo, true)[0];
    if (!hit) return null;
    let node: THREE.Object3D | null = hit.object;
    while (node) {
      if (node.userData?.axisId) return node.userData.axisId as MoveAxisId;
      node = node.parent;
    }
    return null;
  }

  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();

  // The bottle's old drag state is gone: movement now goes through the 3-axis gizmo above.

  // ---- Dragging the section-cut handle ----
  //
  // The drag plane contains the cut axis and faces the camera as squarely as possible, so the
  // pointer ray crosses it at a good angle. Projecting the hit onto the axis then gives the new
  // position. See sectionDragAxis() for the degenerate case (looking straight down the axis).
  let isDraggingSection = false;
  const sectionDragPlane = new THREE.Plane();
  const sectionDragPt = new THREE.Vector3();
  const sectionAxisVec = new THREE.Vector3();

  function sectionDragAxis(): THREE.Vector3 {
    return sectionAxisVec.set(
      sectionCutAxis === 'x' ? 1 : 0,
      sectionCutAxis === 'y' ? 1 : 0,
      sectionCutAxis === 'z' ? 1 : 0,
    );
  }

  viewCubeInteraction.bindClick(onViewCubeTargetClicked);

  let pointerDownPos = { x: 0, y: 0 };
  renderer.domElement.addEventListener('pointerdown', (evt) => {
    pointerDownPos = { x: evt.clientX, y: evt.clientY };

    if (isZoomPointActive) {
      return;
    }

    // Section handle first: it is drawn on top of everything, so if it is under the pointer the
    // user means to grab it, not to start an orbit.
    if (isSectionCutActive && sectionHandle.visible) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);

      if (hitSectionHandlePaint()) {
        const axis = sectionDragAxis();

        // Face the camera as much as possible while still containing the axis. When the view is
        // nearly parallel to the axis this collapses toward zero - dragging is then meaningless
        // (the plane barely moves on screen), so leave the position alone instead of letting the
        // projection blow up.
        if (beginSectionHandleDrag(camera, axis, sectionHandle.position, sectionDragPlane)) {
          isDraggingSection = true;
          controls.enabled = false;
          renderer.domElement.style.cursor = 'grabbing';
          statusLog.setStatus('✂️ Dragging cut plane...', 'info');
          return;
        }
      }
    }

    // Move gizmo before everything else: it is drawn on top of the scene, so a pointer over an
    // arrow means "move along this axis", not "orbit" and not "grab the cut handle".
    if (moveGizmo.visible) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);

      const id = pickMoveAxis();
      if (id) {
        const hit = raycaster.intersectObject(moveGizmo, true)[0];
        if (hit && beginAxisDrag(id, hit.point)) {
          renderer.domElement.style.cursor = 'grabbing';
          statusLog.setStatus(`↔ Dragging ${id.toUpperCase()} axis...`, 'info');
          return;
        }
      }
    }

    // 2D Planar Badge Surface Gizmo interaction
    if (badge2DGizmoGroup.visible) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);

      const badgeHits = raycaster.intersectObject(badge2DGizmoGroup, true);
      if (badgeHits[0]) {
        let node: THREE.Object3D | null = badgeHits[0].object;
        while (node && !node.userData?.isBadge2DHandle) {
          node = node.parent;
        }
        if (node && node.userData?.type) {
          const item = BADGE_ITEMS[selectedBadgeItemIndex];
          if (item && raycaster.ray.intersectPlane(badgePlane, badgeStartHit)) {
            isDraggingBadge2D = true;
            badge2DDragType = node.userData.type as 'move' | 'scale' | 'rotate';
            badgeStartOffsetX = Number(currentParams[item.offsetXKey]) || 0;
            badgeStartOffsetZ = Number(currentParams[item.offsetZKey]) || 0;
            badgeStartScale = Number(currentParams[item.scaleKey]) || 1.0;
            controls.enabled = false;
            renderer.domElement.style.cursor = 'grabbing';
            statusLog.setStatus(`🎯 Dragging 2D Planar Surface (${node.userData.type.toUpperCase()}) for ${item.label}...`, 'info');
            return;
          }
        }
      }
    }

    if (aquafinaBottle && aquafinaBottle.visible) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);

      const intersects = raycaster.intersectObject(aquafinaBottle, true);
      if (intersects[0]) {
        controls.enabled = false;
        renderer.domElement.style.cursor = 'grab';
      }
    }
  });

  renderer.domElement.addEventListener('pointerleave', () => {
    viewCubeInteraction.clearHover();
  });

  function clearHover(): void {
  }

  renderer.domElement.addEventListener('pointermove', (evt) => {
    if (isZoomPointActive) {
      renderer.domElement.style.cursor = 'crosshair';
      return;
    }

    // Check ViewCube hover first
    if (updateViewCubeHover(evt.clientX, evt.clientY)) {
      return;
    }

    // 2D Planar Badge Surface Gizmo drag handling
    if (isDraggingBadge2D && badge2DGizmoGroup.visible) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);

      const item = BADGE_ITEMS[selectedBadgeItemIndex];
      if (item && raycaster.ray.intersectPlane(badgePlane, badgePlaneHit)) {
        const badgeDiameter = Number(currentParams['badgeDiameter']) || 40.0;
        const centerZ = badgeDiameter * 0.5;

        if (badge2DDragType === 'move') {
          const deltaX = badgePlaneHit.x - badgeStartHit.x;
          const deltaZ = badgePlaneHit.z - badgeStartHit.z;
          helperUpdateParam(item.offsetXKey, badgeStartOffsetX + deltaX);
          helperUpdateParam(item.offsetZKey, badgeStartOffsetZ + deltaZ);
        } else if (badge2DDragType === 'scale') {
          const center3D = new THREE.Vector3(badgeStartOffsetX, -0.6, centerZ + badgeStartOffsetZ);
          const startDist = badgeStartHit.distanceTo(center3D);
          const currentDist = badgePlaneHit.distanceTo(center3D);
          if (startDist > 0.1) {
            const ratio = currentDist / startDist;
            const newScale = Math.max(0.2, Math.min(3.0, badgeStartScale * ratio));
            helperUpdateParam(item.scaleKey, newScale);
          }
        } else if (badge2DDragType === 'rotate' && item.rotateKey) {
          const center3D = new THREE.Vector3(badgeStartOffsetX, -0.6, centerZ + badgeStartOffsetZ);
          const dx = badgePlaneHit.x - center3D.x;
          const dz = badgePlaneHit.z - center3D.z;
          const angleDeg = Math.round((Math.atan2(-dx, dz) * 180 / Math.PI + 360) % 360);
          helperUpdateParam(item.rotateKey, angleDeg);
        }
        syncBadge2DGizmo();
      }
      return;
    }

    // Dragging the section-cut handle takes priority over hover and orbit.
    if (isDraggingSection && assemblyGroup) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);

      const percent = sectionHandleDragPercent(raycaster.ray, sectionDragPlane, assemblyGroup, sectionCutAxis, sectionDragPt);
      if (percent !== null) {
        sectionCutPercent = percent;
        updateClippingPlane();
        rightPanel.setSectionPercent(sectionCutPercent);
      }
      return;
    }

    if (isDraggingMove) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      if (moveDragController.intersect(raycaster.ray, moveDragPt)) updateAxisDrag(moveDragPt);
      return;
    }

    // Hover: light up the arrow under the pointer so it is discoverable as draggable.
    if (moveGizmo.visible) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const id = pickMoveAxis();
      highlightMoveAxis(id);
      if (id) {
        renderer.domElement.style.cursor = 'grab';
        return;
      }
      if (renderer.domElement.style.cursor === 'grab') renderer.domElement.style.cursor = 'default';
    }

    if (aquafinaBottle && aquafinaBottle.visible && !isDraggingMove) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObject(aquafinaBottle, true);
      if (intersects.length > 0) {
        renderer.domElement.style.cursor = 'pointer';
      } else if (renderer.domElement.style.cursor === 'pointer') {
        renderer.domElement.style.cursor = 'default';
      }
    }

    // Hover the lamp -> pointer, so "click the lamp to get the arrows" is discoverable. The bulb
    // itself is too small to aim at, so hover the model instead.
    if (isInternalLightOn && assemblyGroup) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const over = raycaster.intersectObject(assemblyGroup, true).length > 0;
      if (over) {
        renderer.domElement.style.cursor = 'pointer';
      } else if (renderer.domElement.style.cursor === 'pointer') {
        renderer.domElement.style.cursor = 'default';
      }
    }

    // Hover the section handle -> show the grab cursor so it is discoverable as draggable.
    if (isSectionCutActive && sectionHandle.visible) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const overHandle = hitSectionHandlePaint();
      if (overHandle) {
        renderer.domElement.style.cursor = 'grab';
      } else if (renderer.domElement.style.cursor === 'grab') {
        renderer.domElement.style.cursor = 'default';
      }
    }
  });

  renderer.domElement.addEventListener('pointerup', (evt) => {
    if (isDraggingBadge2D) {
      isDraggingBadge2D = false;
      badge2DDragType = null;
      controls.enabled = true;
      renderer.domElement.style.cursor = 'default';
      statusLog.setStatus('✓ Updated 2D Planar position on Badge face', 'ok');
      return;
    }

    if (isDraggingSection) {
      isDraggingSection = false;
      controls.enabled = true;
      renderer.domElement.style.cursor = 'default';
      statusLog.setStatus(`✂️ Cut position: ${sectionCutPercent.toFixed(0)}% across the model`, 'ok');
      return;
    }

    if (isDraggingMove) {
      isDraggingMove = false;
      moveDragController.cancel();
      moveDragAxis = null;
      controls.enabled = true;
      highlightMoveAxis(null);
      renderer.domElement.style.cursor = 'default';
      const obj = moveTargetObject();
      if (obj) {
        statusLog.setStatus(
          `✓ ${moveTarget === 'light' ? 'Light bulb' : 'Bottle'} moved to (${obj.position.x.toFixed(1)}, ${obj.position.y.toFixed(1)}, ${obj.position.z.toFixed(1)})mm`,
          'ok');
      }
      return;
    }

    // Ignore drags
    const dist = Math.hypot(evt.clientX - pointerDownPos.x, evt.clientY - pointerDownPos.y);
    if (dist > 5) return;

    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);

    // Clicking the bottle attaches the gizmo to it. Checked BEFORE the assembly part-selection:
    // in measure mode a click is also "pick a measurement point", so returning here stops the
    // gizmo click from dropping a point onto the bottle.
    if (aquafinaBottle.visible && raycaster.intersectObject(aquafinaBottle, true).length > 0) {
      showMoveGizmo('bottle');
      statusLog.setStatus('🍾 Bottle selected — drag an arrow to move it on X / Y / Z', 'info');
      return;
    }

    // Clicked the model (or empty space): drop a gizmo that is no longer reachable. Whether a new
    // one is attached is decided below, once the model hit-test exists.
    if (moveGizmo.visible) {
      hideMoveGizmo();
      highlightMoveAxis(null);
    }

    // Interactive Object Cluster Selection (Only test currently visible meshes)
    const intersects = pickSelectableIntersections(raycaster, assemblyGroup, {
      excluded: [watertightEdges, supportOverlay],
      includeNested: true,
      isGroupVisible: (gid) => !sceneTree || sceneTree.isPartVisible(gid),
    }, isSectionCutActive ? clippingPlane : null);

    if (isZoomPointActive) {
      if (intersects[0]) {
        zoomToPoint(intersects[0].point);
      }
      setZoomPointMode(false);
      return;
    }

    // Clicking the LAMP attaches the gizmo to the light bulb.
    //
    // Deliberately a hit on ANY model mesh rather than on the bulb sphere: the bulb is a 6mm ball
    // sitting INSIDE the shade, so clicking it directly is effectively impossible. "Click the lamp"
    // is both what the owner asked for and the only thing a person can actually aim at.
    //
    // This runs after the model hit-test but before measurement/selection consume it, and it does
    // NOT return - part selection and point picking still happen on the same click, so nothing the
    // click used to do is lost.
    if (isInternalLightOn && intersects.length > 0) {
      showMoveGizmo('light');
      statusLog.setStatus('💡 Light bulb selected — drag an arrow to move it on X / Y / Z', 'info');
    }

    // If measurement mode is active, handle point picking instead of selecting parts
    if (isMeasureActive) {
      if (!measurePointA) {
        // First pick -> Set Point A (snapped to the cut edge, else nearest vertex/edge)
        const pickPtA = measureSnap(intersects);
        if (pickPtA) {
          measurePointA = pickPtA;
          markerA.position.copy(measurePointA);
          markerA.visible = true;
          markerB.visible = false;
          measureLine.visible = false;
          previewMarker.visible = false;
          const axisMsg = currentMeasureAxis !== 'free' ? ` (Locked ${currentMeasureAxis.toUpperCase()} axis)` : '';
          statusLog.setStatus(`Point A set at (${measurePointA.x.toFixed(1)}, ${measurePointA.y.toFixed(1)}, ${measurePointA.z.toFixed(1)})${axisMsg}. Now click Point B.`, 'info');
        }
      } else {
        // Second pick -> Set Point B
        let targetPt: THREE.Vector3 | null = null;
        const snappedPt = measureSnap(intersects);
        if (snappedPt) {
          // Snap to the cut edge or mesh surface, then enforce strict axis constraint from Point A
          targetPt = constrainPointToAxis(measurePointA, snappedPt, currentMeasureAxis);
        } else if (currentMeasureAxis !== 'free') {
          // Fallback: Project camera ray onto 3D virtual axis line passing through P1 if clicking in empty space
          targetPt = projectRayToAxis(raycaster.ray, measurePointA, currentMeasureAxis);
        }

        if (targetPt) {
          measurePointB = targetPt;
          markerB.position.copy(measurePointB);
          markerB.visible = true;
          previewMarker.visible = false;
          updateMeasurementLine(measurePointA, measurePointB);
          updateMeasurementHUD(measurePointA, measurePointB);
          const dist = measurePointA.distanceTo(measurePointB);
          const axisLabel = currentMeasureAxis !== 'free' ? ` [${currentMeasureAxis.toUpperCase()} Lock]` : '';
          statusLog.setStatus(`✓ Measured${axisLabel}: ${dist.toFixed(2)} mm (Click again to start new measurement)`, 'ok');
          measurePointA = null;
        }
      }
      return;
    }

    const selectionResult = applyViewportSelection(intersects[0], evt.shiftKey || evt.ctrlKey || evt.metaKey, {
      tree: sceneTree,
      setSelectedGroup: (group) => { selectedGroup = group as typeof selectedGroup; },
      refreshHighlight: applySelectionHighlighting,
      updateSelectedGroupPanel: (group) => rightPanel.setSelectedGroup(group),
      setStatus: (message) => statusLog.setStatus(message, 'ok'),
    });
    if (selectionResult === 'preserved') return;
  });

  // Pointermove hover preview when Measurement Mode is active
  renderer.domElement.addEventListener('pointermove', (evt) => {
    if (!isMeasureActive) return;
    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);

    const intersects = pickSelectableIntersections(raycaster, assemblyGroup, {
      excluded: [watertightEdges, supportOverlay, measureGroup],
      includeNested: true,
    }, isSectionCutActive ? clippingPlane : null);
    // Same preference as the click: the cut edge wins over the mesh surface, so the preview
    // marker shows where the click will actually land.
    const hoverSnapPt = measureSnap(intersects);
    if (hoverSnapPt) {
      const snappedPt = hoverSnapPt;
      const effectivePt = measurePointA ? constrainPointToAxis(measurePointA, snappedPt, currentMeasureAxis) : snappedPt;
      previewMarker.position.copy(effectivePt);
      previewMarker.visible = true;

      if (measurePointA) {
        updateMeasurementLine(measurePointA, effectivePt);
        updateMeasurementHUD(measurePointA, effectivePt);
      }
      return;
    }

    if (measurePointA && currentMeasureAxis !== 'free') {
      // Fallback: 3D Axis-Ray Projection when hovering over empty space
      const effectivePt = projectRayToAxis(raycaster.ray, measurePointA, currentMeasureAxis);
      if (effectivePt) {
        previewMarker.position.copy(effectivePt);
        previewMarker.visible = true;
        updateMeasurementLine(measurePointA, effectivePt);
        updateMeasurementHUD(measurePointA, effectivePt);
        return;
      }
    }

    previewMarker.visible = false;
  });

  // Double-click anywhere on the model to directly zoom into that point
  renderer.domElement.addEventListener('dblclick', (evt) => {
    const hitCube = getViewCubeHit(evt.clientX, evt.clientY);
    if (hitCube) return;

    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);

    const intersects = pickSelectableIntersections(raycaster, assemblyGroup, {
      excluded: [watertightEdges, supportOverlay, measureGroup],
      includeNested: true,
      isGroupVisible: (gid) => !sceneTree || sceneTree.isPartVisible(gid),
    }, isSectionCutActive ? clippingPlane : null);

    if (intersects[0]) {
      zoomToPoint(intersects[0].point);
    }
  });

  modelHealthLeds.setOnWatertightClick(() => {
    if (modelHealthLeds.isWatertightOk()) return;
    watertightEdgesRequested = true;
    if (watertightEdges) {
      watertightEdges.visible = !watertightEdges.visible;
      return;
    }
    if (isSvgPreviewModule(module.id) && latestMeshData?.analysis && !latestMeshData.analysis.skipped) {
      const analysis = latestMeshData.analysis;
      modelHealthLeds.setWatertight(analysis.isWatertight, analysis.nonManifoldEdgeCount);
      if (!analysis.isWatertight) buildWatertightEdges(latestMeshData, true);
      statusLog.setStatus(analysis.isWatertight ? '✓ SVG mesh is watertight' : `⚠ ${analysis.nonManifoldEdgeCount} SVG open edges`, analysis.isWatertight ? 'ok' : 'warning');
      return;
    }
    statusLog.setStatus('Checking watertight edges...', 'info');
    fetchPreview(module.id, currentParams, { group: -1, analyze: true, quality: getAppQuality() }).then((result) => {
      latestMeshData = result;
      const analysis = result?.analysis;
      if (!analysis || analysis.skipped) {
        modelHealthLeds.setWatertightUnknown();
        statusLog.setStatus('Watertight analysis unavailable', 'warning');
        return;
      }
      modelHealthLeds.setWatertight(analysis.isWatertight, analysis.nonManifoldEdgeCount ?? 0);
      if (!analysis.isWatertight) buildWatertightEdges(result, true);
      statusLog.setStatus(analysis.isWatertight ? '✓ Watertight' : `⚠ ${analysis.nonManifoldEdgeCount ?? 0} open edges`, analysis.isWatertight ? 'ok' : 'warning');
    }).catch((err) => {
      statusLog.setStatus(`Watertight check failed: ${err?.message || err}`, 'error');
    });
  });
  modelHealthLeds.setOnSupportClick(() => {
    isOverhangActive = !isOverhangActive;
    rightPanel.setOverhangActive(isOverhangActive);
    if (isOverhangActive) ensureSupportMeshes();
    else removeSupportMeshes();
    applyVisibility();
    if (!isOverhangActive) modelHealthLeds.setSupportUnknown();
    statusLog.setStatus(isOverhangActive ? '⚠️ Highlight Overhang >45°: Enabled' : 'Highlight Overhang >45°: Disabled', 'ok');
  });

  function createPartSupportMesh(geo: THREE.BufferGeometry): { mesh: THREE.Mesh | null; count: number } {
    const posAttr = geo.getAttribute('position') as THREE.BufferAttribute;
    if (!posAttr) return { mesh: null, count: 0 };

    const indexAttr = geo.index;
    const supportPositions: number[] = [];
    let count = 0;

    const v0 = new THREE.Vector3();
    const v1 = new THREE.Vector3();
    const v2 = new THREE.Vector3();
    const e1 = new THREE.Vector3();
    const e2 = new THREE.Vector3();
    const faceNormal = new THREE.Vector3();

    const triCount = indexAttr ? indexAttr.count / 3 : posAttr.count / 3;

    for (let i = 0; i < triCount; i++) {
      if (indexAttr) {
        const i0 = indexAttr.getX(i * 3);
        const i1 = indexAttr.getX(i * 3 + 1);
        const i2 = indexAttr.getX(i * 3 + 2);
        v0.fromBufferAttribute(posAttr, i0);
        v1.fromBufferAttribute(posAttr, i1);
        v2.fromBufferAttribute(posAttr, i2);
      } else {
        v0.fromBufferAttribute(posAttr, i * 3);
        v1.fromBufferAttribute(posAttr, i * 3 + 1);
        v2.fromBufferAttribute(posAttr, i * 3 + 2);
      }

      e1.subVectors(v1, v0);
      e2.subVectors(v2, v0);
      faceNormal.crossVectors(e1, e2).normalize();

      // Z-up standard: overhang face pointing downwards has faceNormal.z < -0.707 (angle > 45° from horizontal)
      if (faceNormal.z < -0.707) {
        count++;
        // Push slightly along face normal to eliminate Z-fighting flickering
        const offset = faceNormal.clone().multiplyScalar(0.08);
        supportPositions.push(
          v0.x + offset.x, v0.y + offset.y, v0.z + offset.z,
          v1.x + offset.x, v1.y + offset.y, v1.z + offset.z,
          v2.x + offset.x, v2.y + offset.y, v2.z + offset.z
        );
      }
    }
    if (supportPositions.length === 0) return { mesh: null, count: 0 };

    const overlayGeo = new THREE.BufferGeometry();
    overlayGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(supportPositions), 3));
    overlayGeo.computeVertexNormals();

    const mesh = new THREE.Mesh(
      overlayGeo,
      new THREE.MeshBasicMaterial({
        color: 0xff8c00,
        opacity: 0.8,
        transparent: true,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: -1.0,
        polygonOffsetUnits: -1.0,
      })
    );
    mesh.renderOrder = 999;
    mesh.visible = isOverhangActive;
    return { mesh, count };
  }

  function viewModeNeedsWireframe(mode: ViewMode): boolean {
    return mode === 'wireframe' || mode === 'overlay' || mode === 'xray';
  }

  function addWireframeForMesh(mesh: THREE.Mesh): void {
    if (!assemblyGroup || mesh.userData?.isSupport) return;
    const exists = assemblyGroup.children.some(c => c.userData?.isWireframe && c.userData?.groupId === mesh.userData?.groupId);
    if (exists) return;
    const wire = new THREE.LineSegments(
      new THREE.WireframeGeometry(mesh.geometry),
      new THREE.LineBasicMaterial({ color: 0x1e293b, opacity: 0.15, transparent: true })
    );
    wire.userData = { groupId: mesh.userData?.groupId, isWireframe: true };
    assemblyGroup.add(wire);
  }

  function ensureWireframes(): void {
    if (!assemblyGroup) return;
    assemblyGroup.traverse((child) => {
      if (child instanceof THREE.Mesh && !child.userData?.isSupport && !child.userData?.isWireframe) {
        addWireframeForMesh(child);
      }
    });
    applyVisibility();
  }

  function removeSupportMeshes(): void {
    if (!assemblyGroup) return;
    const toRemove: THREE.Object3D[] = [];
    assemblyGroup.traverse((child) => { if (child.userData?.isSupport) toRemove.push(child); });
    for (const child of toRemove) {
      child.traverse((nested) => {
        if (nested instanceof THREE.Mesh || nested instanceof THREE.LineSegments) {
          nested.geometry.dispose();
          const material = nested.material as THREE.Material | THREE.Material[];
          if (Array.isArray(material)) material.forEach(m => m.dispose());
          else material.dispose();
        }
      });
      child.removeFromParent();
    }
  }

  function ensureSupportMeshes(): void {
    if (!assemblyGroup) return;
    removeSupportMeshes();
    const sourceMeshes: THREE.Mesh[] = [];
    assemblyGroup.traverse((child) => {
      if (child instanceof THREE.Mesh && !child.userData?.isSupport && !child.userData?.isWireframe) sourceMeshes.push(child);
    });
    for (const source of sourceMeshes) {
      const sup = createPartSupportMesh(source.geometry);
      if (sup.mesh) {
        sup.mesh.userData = { groupId: source.userData?.groupId, isSupport: true };
        assemblyGroup.add(sup.mesh);
      }
    }
  }

  function countSupportFaces(): number {
    let count = 0;
    assemblyGroup?.traverse((child) => {
      if (child.userData?.isSupport && child instanceof THREE.Mesh) {
        count += (child.geometry.getAttribute('position')?.count || 0) / 3;
      }
    });
    return count;
  }

  function buildWatertightEdges(meshData: MeshData, visible = false): void {
    if (!meshData.analysis || meshData.analysis.isWatertight || meshData.analysis.nonManifoldEdges.length === 0) {
      watertightEdges = null;
      return;
    }
    if (watertightEdges) {
      watertightEdges.traverse((child) => {
        if (!(child instanceof THREE.LineSegments)) return;
        child.geometry.dispose();
        (child.material as THREE.Material).dispose();
      });
      watertightEdges.removeFromParent();
    }
    const positionsByGroup = new Map<number, number[]>();
    for (const e of meshData.analysis.nonManifoldEdges) {
      const groupId = e.groupId ?? 0;
      const positions = positionsByGroup.get(groupId) ?? [];
      positions.push(e.v0.x, e.v0.y, e.v0.z, e.v1.x, e.v1.y, e.v1.z);
      positionsByGroup.set(groupId, positions);
    }
    const overlay = new THREE.Group();
    overlay.name = 'Watertight edge markers';
    for (const [groupId, positions] of positionsByGroup) {
      const edgeGeo = new THREE.BufferGeometry();
      edgeGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
      const edges = new THREE.LineSegments(
        edgeGeo,
        new THREE.LineBasicMaterial({ color: 0xff0000 })
      );
      edges.userData = { groupId, isWatertightEdge: true };
      edges.visible = visible;
      overlay.add(edges);
    }
    watertightEdges = overlay;
    assemblyGroup?.add(overlay);
  }

  function zoomAllToMesh(): void {
    if (assemblyGroup) {
      fitCameraToObject(camera, controls, assemblyGroup, 1.5);
    }
  }

  function updateDimensionsHud(): void {
    const dimsVal = document.getElementById('dims-val');
    if (!dimsVal || !assemblyGroup) return;

    const selectedGids = sceneTree ? sceneTree.getSelectedGroupIds() : new Set<number>();
    const targetMeshes: THREE.Mesh[] = [];

    if (selectedGids.size === 0) {
      // "All Assembly" is selected -> calculate total bounding box of all visible assembly parts
      assemblyGroup.traverse((child) => {
        if (child instanceof THREE.Mesh && !child.userData?.isSupport && child.geometry) {
          targetMeshes.push(child);
        }
      });
    } else {
      // Object or Part selected -> calculate bounding box of matching part group(s)
      assemblyGroup.traverse((child) => {
        if (child instanceof THREE.Mesh && !child.userData?.isSupport && child.geometry) {
          const gid = child.userData?.groupId;
          if (gid !== undefined && selectedGids.has(gid)) {
            targetMeshes.push(child);
          }
        }
      });
    }

    if (targetMeshes.length === 0) {
      dimsVal.innerHTML = '—';
      return;
    }

    const box = new THREE.Box3();
    for (const m of targetMeshes) {
      if (m.geometry) {
        m.geometry.computeBoundingBox();
        if (m.geometry.boundingBox) {
          const meshBox = m.geometry.boundingBox.clone().applyMatrix4(m.matrixWorld);
          box.union(meshBox);
        }
      }
    }

    if (!box.isEmpty()) {
      const size = new THREE.Vector3();
      box.getSize(size);
      dimsVal.innerHTML = `${size.x.toFixed(1)} × ${size.y.toFixed(1)} × ${size.z.toFixed(1)} mm`;
    } else {
      dimsVal.innerHTML = '—';
    }
  }

  function updateArchitecturalDimensions(): void {
    if (!architecturalDimensionsOn || !assemblyGroup) return;
    const selectedGids = sceneTree?.getSelectedGroupIds() ?? new Set<number>();
    const bounds = new THREE.Box3();
    assemblyGroup.updateMatrixWorld(true);
    assemblyGroup.traverse((child) => {
      if (!(child instanceof THREE.Mesh) || child.userData?.isSupport || child.userData?.isWireframe) return;
      const gid = child.userData?.groupId;
      if (selectedGids.size > 0 && (gid === undefined || !selectedGids.has(gid))) return;
      child.geometry.computeBoundingBox();
      if (child.geometry.boundingBox) bounds.union(child.geometry.boundingBox.clone().applyMatrix4(child.matrixWorld));
    });
    dimensionsOverlay.update(bounds, camera, container.clientWidth, container.clientHeight);
  }

  function applyThemeToScene(): void {
    applySceneTheme(scene, null, baseWire);
  }

  function renderAxesGizmo(): void {
    const w = container.clientWidth;
    const h = container.clientHeight;
    const gizmoSize = 100;
    // Centred under the Undo/Redo pill rather than tucked in the bottom-right corner.
    // The pill is `position:absolute; top:12px; height:30px`, so its lower edge sits
    // at 42px; the gizmo starts 8px below that.
    //
    // setViewport measures y from the BOTTOM of the drawing buffer, so a distance from the top has
    // to be converted: y = h - topOffset - gizmoSize.
    const undoBarTop = 12, undoBarHeight = 30, gapBelowBar = 8;
    const topOffset = undoBarTop + undoBarHeight + gapBelowBar;
    const gx = Math.round((w - gizmoSize) / 2);
    const gy = Math.round(h - topOffset - gizmoSize);
    gizmoCamera.position.copy(camera.position).sub(controls.target).normalize().multiplyScalar(3);
    gizmoCamera.up.copy(camera.up);
    gizmoCamera.lookAt(0, 0, 0);
    renderer.setViewport(gx, gy, gizmoSize, gizmoSize);
    renderer.setScissor(gx, gy, gizmoSize, gizmoSize);
    renderer.setScissorTest(true);
    renderer.clearDepth();
    renderer.render(gizmoScene, gizmoCamera);
    renderer.setScissorTest(false);
    renderer.setViewport(0, 0, w, h);
  }

  // Smooth Animated Relative Z-Explode Algorithm
  // (targetExplodeFactor / currentExplodeFactor are declared next to the explode button handler,
  //  which is the only thing that sets the target.)
  function applyUniversalCascadingExplode(explodeFactor: number): void {
    if (!assemblyGroup) return;

    // Get offsets map from module.objects or schema
    const objects = module.objects || (module as any).schema?.objects || [];
    const schemaOffsets = (module as any).schema?.exploded_view?.offsets || (module as any).exploded_view?.offsets || [];
    const gidToOffset: Record<number, { assembledZ: number; explodedZ: number }> = {};

    if (objects.length > 0) {
      for (const obj of objects) {
        const objExpZ = obj.exploded_z_offset ?? 0;
        const objAsmZ = obj.assembled_z_offset ?? 0;
        for (const p of obj.parts) {
          const expZ = p.exploded_z_offset ?? objExpZ;
          const asmZ = p.assembled_z_offset ?? objAsmZ;
          gidToOffset[p.groupId] = { assembledZ: asmZ, explodedZ: expZ };
        }
      }
    }

    if (schemaOffsets.length > 0) {
      for (const off of schemaOffsets) {
        const expZ = off.exploded_z_offset ?? 0;
        const asmZ = off.assembled_z_offset ?? 0;
        const groupIds = off.group_ids || [];
        for (const gid of groupIds) {
          if (!gidToOffset[gid] || gidToOffset[gid].explodedZ === 0) {
            gidToOffset[gid] = { assembledZ: asmZ, explodedZ: expZ };
          }
        }
      }
    }

    // Strict schema offset lookup: no hardcoded fallbacks
    assemblyGroup.traverse((child) => {
      if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
        const gid = child.userData?.groupId;
        if (gid !== undefined) {
          if (gidToOffset[gid]) {
            const { assembledZ, explodedZ } = gidToOffset[gid];
            // When explodeFactor = 0.0 (Exploded view), move up by (explodedZ - assembledZ)
            // When explodeFactor = 1.0 (Assembled view), deltaZ = 0
            const deltaZ = (explodedZ - assembledZ) * (1.0 - explodeFactor);
            child.position.z = deltaZ;
          } else if (gid !== 0) {
            console.warn(`[Assembly Schema Warning] Missing exploded_z_offset for groupId: ${gid} in module "${module.id}". Please define it in assembly-schema.json.`);
          }
        }
      }
    });
  }

  // TASK 25 (2026-09-30): stop the loop when this run is superseded, so an old animation
  // loop cannot keep rendering (and holding GPU work) after a new module has started.
  let loopRunning = true;
  function animate(): void {
    if (!loopRunning) return;
    requestAnimationFrame(animate);
    controls.update();

    // Target factor is set by the Explode/Attach button: 0.0 = exploded, 1.0 = assembled.
    // (It used to read currentParams['showAssembled'], a slider no module defines any more —
    //  see the note on the explode button handler.)

    // Smooth lerp easing towards target (0.12 per frame for smooth 60fps movement)
    const prevExplodeFactor = currentExplodeFactor;
    currentExplodeFactor += (targetExplodeFactor - currentExplodeFactor) * 0.12;

    applyUniversalCascadingExplode(currentExplodeFactor);

    // Keep the move gizmo parked on its target. syncMoveGizmo() also re-checks that the target is
    // still visible, so if the bulb or bottle is hidden the gizmo takes itself down rather than
    // hanging over nothing.
    if (moveGizmo.visible) syncMoveGizmo();

    if (module.id === 'swing_badge_keychain') {
      syncBadge2DGizmo();
    }

    // Update section cut outline dynamically if parts are animating/exploding
    if (isSectionCutActive && Math.abs(currentExplodeFactor - prevExplodeFactor) > 0.0002) {
      updateSectionCutOutline();
      // The model bounds grow while exploding, so the handle (and the plane position it maps
      // to) must follow or it drifts off the cut.
      syncSectionHandle();
    }

    // Keep measurement markers at a constant, elegant pixel size regardless of camera zoom distance (Professional CAD style)
    if (isMeasureActive) {
      const camDist = camera.position.distanceTo(controls.target);
      const scaleFactor = Math.max(0.3, camDist / 120.0);
      markerA.scale.setScalar(scaleFactor);
      markerB.scale.setScalar(scaleFactor);
      previewMarker.scale.setScalar(scaleFactor);

      // Project 3D midpoint of dimension line to 2D viewport screen coordinates for floating label badge
      if (measurementOverlay.midpoint && measureLabel.style.display !== 'none') {
        const v = measurementOverlay.midpoint.clone().project(camera);
        // Check if point is in front of camera (z < 1)
        if (v.z < 1) {
          const w = container.clientWidth;
          const h = container.clientHeight;
          const x = (v.x * 0.5 + 0.5) * w;
          const y = (-v.y * 0.5 + 0.5) * h;
          measureLabel.style.left = `${x}px`;
          measureLabel.style.top = `${y}px`;
          measureLabel.style.visibility = 'visible';
        } else {
          measureLabel.style.visibility = 'hidden';
        }
      }
    }

    cameraController.update(camera, controls, setOrthoFrustum, orthoHalfHeightForDistance);

    if (architecturalDimensionsOn) updateArchitecturalDimensions();
    renderer.clear();
    renderer.render(scene, camera);
    renderAxesGizmo();
  }
  window.addEventListener('resize', () => {
    const w = container.clientWidth, h = container.clientHeight;
    // Both cameras need resizing: the perspective one by aspect, the orthographic one by
    // widening its frustum. Only the active one renders, but the inactive one must not hold
    // a stale frustum for the next switch.
    perspCamera.aspect = w / h;
    perspCamera.updateProjectionMatrix();
    setOrthoFrustum(orthoCamera.top);
    renderer.setSize(w, h);
    updateArchitecturalDimensions();
  });

  // A full page reload must start a fresh preview revision. Otherwise the API
  // cache can serve a mesh from before the latest C++ geometry rebuild (for
  // example, the old strap without its eyelet hole or without the white PEI
  // badge), making the viewport appear to alternate between old and new.
  try {
    sessionStorage.setItem('boxed3d_preview_revision', String(Date.now()));
  } catch {
    // Preview remains functional when session storage is unavailable.
  }
  updatePreview();

  // TASK 25: register the teardown for whoever starts the next app (or navigates home).
  appLifecycle.registerTeardown(() => {
    loopRunning = false;
    // The panel toggles live on document.body, outside #app, so clearing #app when
    // navigating Home does not remove them. Remove the editor-only controls explicitly.
    columnToggles.forEach((state) => state.button.remove());
    columnToggles.length = 0;
    // TASK 07: release GPU memory held by this scene. WebGLRenderer keeps every geometry,
    // material and texture alive until it is disposed explicitly, even after the meshes are
    // removed from the scene - so repeated previews grew VRAM without bound.
    disposeSceneResources(scene);
    // dispose() frees resources; forceContextLoss() is what actually gives the WebGL context
    // slot back to the browser. Browsers allow only ~8-16 live contexts, so without this,
    // opening a handful of modules made rendering stop working.
    try {
      renderer.dispose();
      renderer.forceContextLoss();
    } catch {
      // Some drivers throw here if the context is already gone - nothing useful to do.
    }
  });

  animate();
}

/**
 * TASK 07 (2026-09-30): dispose every geometry/material in the given scene graph.
 *
 * `scene.clear()` / `assemblyGroup.clear()` only unparent objects - they do not free GPU
 * memory. Three.js keeps the underlying buffers alive until geometry.dispose() and
 * material.dispose() are called, so each preview leaked the previous model's VRAM. With a
 * 1.6M-triangle module and a slider that triggers a preview per pause, that adds up fast.
 *
 * Traversal is deliberate rather than tracking a list: the scene contains meshes, wireframe
 * LineSegments, support overlays and the clipping-plane helpers, and a list would silently
 * miss any of them the moment someone adds another overlay.
 */
// ---- Router: parse hash, dispatch ----
function route(): void {
  const hash = window.location.hash;
  if (hash === '#/drawing') {
    appLifecycle.invalidate();
    const root = document.getElementById('app')!;
    root.style.cssText = 'display:block;width:100vw;height:100vh;overflow:hidden;';
    renderDrawing2D(root);
    return;
  }
  if (hash === '#/flexi') {
    appLifecycle.invalidate();
    const root = document.getElementById('app')!;
    root.style.cssText = 'display:block;width:100vw;min-height:100vh;overflow-y:auto;';
    renderFlexiBuilder(root);
    return;
  }
  const match = hash.match(/^#\/m\/([\w_]+)$/);
  if (match) {
    const modId = match[1];
    if (!modId) return showHome();
    if (modId === 'core_bambu_led_kit') {
      window.location.hash = '#/m/core_bambu_led_socket';
      return;
    }
    startApp(modId);
  } else if (hash === '#/core' || hash === '#/core/') {
    showHome('core');
  } else {
    showHome();
  }
}
window.addEventListener('hashchange', route);

// R15/R17 cleanup: remove stale localStorage theme key (Phase 0 had 2 themes, now only 1 light).
// Idempotent — runs each boot, harmless.
try { localStorage.removeItem('app-theme'); } catch { /* private mode */ }

// Initial load
console.log('[App] Modules:', MODULES.map(m => m.id));
route();
